import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import { computeRealYield } from '@/lib/realYield';
import { useAuth } from '@/contexts/AuthContext';
import StockModal from '@/components/StockModal';
import LeveragedCefIcon from '@/components/LeveragedCefIcon';
import { CoverageCellTip, CoverageHeaderTip } from '@/components/CoverageTip';
import CompareModal from '@/components/CompareModal';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';
import useDebouncedSetter from '@/hooks/useDebouncedSetter';
import LoginModal from '@/components/LoginModal';
import QualityBadge from '@/components/QualityBadge';
import DividendFilterSegmentedControl from '@/components/DividendFilterSegmentedControl';
import CapitalScoreBadge from '@/components/CapitalScoreBadge';
import RevenueSparkline from '@/components/RevenueSparkline';
import { computeQuality } from '@/lib/qualityScore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Search, ArrowLeft, Filter, Star, X, Zap, AlertTriangle, ChevronLeft, ChevronRight, Crown, Clock, Percent, PieChart, Building2, Briefcase, BarChart3, Activity, Scale, TrendingUp, Banknote, Layers, Globe, Package, Home, Target, ArrowUpDown, CalendarClock, CalendarCheck, Key, Link2, Flag, RefreshCw, Gem, ShieldAlert } from 'lucide-react';
import { smoothScrollToEl } from '@/lib/smoothScroll';

// Pagination scroll-back: after Next/Prev, climb smoothly to the
// search bar (medium-fast, not instant) so the user reads the new
// page top-down instead of being stranded at the bottom.
function scrollToTableTop(selector) {
  smoothScrollToEl(document.querySelector(selector));
}
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AbbrTip from '@/components/AbbrTip';
import MinerviniBadge from '@/components/MinerviniBadge';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import DividendChangeBadge from '@/components/DividendChangeBadge';
import PreferredCallRiskChip from '@/components/PreferredCallRiskChip';
import BadgeKey from '@/components/BadgeKey';
import { fetchStockWithFallback } from '@/lib/stockFetch';
import LivePriceIndicator from '@/components/LivePriceIndicator';
import usePriceStream from '@/hooks/usePriceStream';
import useCpiYoy from '@/hooks/useCpiYoy';
import PreferredCells, { PreferredYtcCell } from '@/components/PreferredCells';

const ANOMALY_LOCK_TIP = 'Greyed out while the Accounting Anomalies filter is on — SPACs, warrants, money market and mutual funds have no financial statements to score, so none of them can appear in that list. Clear the Anomalies filter to use this.';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_OPTIONS = [
  { value: 'all', label: 'All Tiers',
    tip: 'Dividend Tiers — based on consecutive years of dividend increases. Kings (50+ yrs) are the most elite, Aristocrats (25+) are blue-chip reliable, Achievers (10+) are on track to join them.' },
  { value: 'king', label: '\u{1F451} Kings (50+ yrs)',
    tip: 'Dividend Kings — companies that have raised their dividend for 50+ consecutive years through every recession, war, and market crash. The gold standard for income reliability.' },
  { value: 'aristocrat', label: '\u{1F3C6} Aristocrats (25+ yrs)',
    tip: 'Dividend Aristocrats — S&P 500 companies with 25+ years of consecutive dividend increases. Battle-tested income stocks.' },
  { value: 'achiever', label: '\u{1F396}\uFE0F Achievers (10+ yrs)',
    tip: 'Dividend Achievers — 10+ years of consecutive dividend increases. Solid track record, on the way to Aristocrat status.' },
];

const FREQUENCY_OPTIONS = [
  { value: 'all', label: 'All Frequencies',
    tip: 'Dividend Frequency — how often a stock pays its dividend. Most common is Quarterly (4×/yr). Monthly payers suit income-focused portfolios. Weekly and Semi/Annually are rarer.' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Semi-Annually', label: 'Semi-Annually' },
  { value: 'Annually', label: 'Annually' },
];

const YIELD_OPTIONS = [
  { value: 'all', label: 'Any Yield', dot: null,
    tip: 'Dividend Yield = annual dividend ÷ current price, shown as a %. Higher yield = more immediate income, but often more risk. Healthy dividend growers are usually 2–5%; anything above 10% may be a yield trap.' },
  { value: '0-3', label: '0% - 3% (Safe)', dot: 'bg-green-500', tip: 'Safe to Growth-Oriented. Typical for healthy, growing companies and "Dividend Aristocrats". Low risk, very high sustainability, but less immediate income -- investors usually hope for capital appreciation. Examples: Big Tech, stable consumer goods.' },
  { value: '4-6', label: '4% - 6% (Sweet Spot)', dot: 'bg-yellow-500', tip: 'The Sweet Spot. Ideal range for income-focused investors like retirees. Moderate risk, generally stable -- check Payout Ratio. Often mature industries (Utilities, Real Estate, Telecom). Examples: Verizon/AT&T, big oil.' },
  { value: '7-10', label: '7% - 10% (Warning Zone)', dot: 'bg-orange-500', tip: 'The Warning Zone. High-risk territory. Yield often got this high because the stock price crashed, not because the company is doing well. Watch for "Yield Traps" -- if earnings drop, the dividend may be cut. Examples: Struggling retailers, cyclical downturns.' },
  { value: '11+', label: '11%+ (Danger Zone)', dot: 'bg-red-500', tip: 'The Danger Zone. Rarely sustainable for traditional stocks -- often signals an imminent dividend cut. Exceptions: REITs and BDCs must pay out 90% of profits by law and can naturally yield 8-12%. For standard corporations, 11%+ is almost always a distress signal.' },
];

const SORT_OPTIONS = []; // Removed - sorting is now via table headers

const PAYOUT_RATIO_OPTIONS = [
  { value: 'all', label: 'Payout Ratio', dot: null,
    tip: 'Payout Ratio = the % of earnings a company pays out as dividends. Under 60% is comfortable; over 100% means they\'re paying more than they earn — dividend cut risk. REITs and BDCs are exempt from this rule by law.' },
  { value: '0-60', label: '< 60% (Safe)', dot: 'bg-green-500', tip: 'Payout Ratio under 60%. The company pays less than 60% of earnings as dividends -- plenty of room to maintain or grow the dividend.' },
  { value: '61-80', label: '61-80% (Moderate)', dot: 'bg-yellow-500', tip: 'Payout Ratio 61-80%. Most earnings go to dividends. Sustainable for stable companies but less room for increases.' },
  { value: '81-100', label: '81-100% (High)', dot: 'bg-orange-500', tip: 'Payout Ratio 81-100%. Nearly all earnings paid as dividends. High income now but little margin of safety if earnings dip.' },
  { value: '100+', label: '> 100% (Unsustainable)', dot: 'bg-red-500', tip: 'Payout Ratio over 100%. The company is paying out more than it earns -- using reserves or debt. Dividend cut risk is elevated.' },
];

const SECTOR_OPTIONS = [
  { value: 'all', label: 'All Sectors',
    tip: 'Sector — the industry group a company belongs to (Financial Services, Utilities, Technology, etc.). Diversifying across sectors reduces risk from any single industry downturn.' },
  { value: 'Financial Services', label: 'Financial Services',
    tip: 'Financial Services — banks, insurance, asset managers, exchanges, credit services. Typically steady dividends; sensitive to interest rates.' },
  { value: 'Industrials', label: 'Industrials',
    tip: 'Industrials — machinery, aerospace, transportation, construction, defense. Tied to capex cycles; includes many long-time dividend payers.' },
  { value: 'Consumer Cyclical', label: 'Consumer Cyclical',
    tip: 'Consumer Cyclical — retailers, restaurants, travel, apparel, autos. Cyclical — booms in expansions, suffers in recessions.' },
  { value: 'Real Estate', label: 'Real Estate',
    tip: 'Real Estate — REITs (Real Estate Investment Trusts) that own apartments, malls, warehouses, cell towers. Required to distribute 90%+ of income, so yields are usually high; sensitive to interest rates.' },
  { value: 'Energy', label: 'Energy',
    tip: 'Energy — oil & gas producers, pipelines, refiners. Commodity-price-sensitive; often high yields with variable dividend safety.' },
  { value: 'Technology', label: 'Technology',
    tip: 'Technology — hardware, software, semiconductors, cloud, internet services. Historically higher growth, lower yields than average.' },
  { value: 'Basic Materials', label: 'Basic Materials',
    tip: 'Basic Materials — miners, chemicals, metals, paper, construction materials. Commodity-cycle sensitive; yields vary widely with input prices.' },
  { value: 'Consumer Defensive', label: 'Consumer Defensive',
    tip: 'Consumer Defensive — food, beverages, household goods, tobacco. Things people buy in any economy. Lower growth, stable dividends. Many Dividend Kings live here (P&G, Coca-Cola, Colgate).' },
  { value: 'Utilities', label: 'Utilities',
    tip: 'Utilities — electric, gas, and water providers. Regulated monopolies with slow growth + stable high yields. Classic "sleep well at night" sector.' },
  { value: 'Healthcare', label: 'Healthcare',
    tip: 'Healthcare — pharmaceuticals, biotech, medical devices, health insurers. Mix of growth (biotech) and defensive (large pharma) names.' },
  { value: 'Communication Services', label: 'Communication Services',
    tip: 'Communication Services — telecom, media, internet platforms. Mix of mature dividend payers (AT&T, Verizon) and growth names (Google, Netflix, Meta).' },
  { value: 'other', label: 'Other / ETFs',
    tip: 'Other / ETFs — funds and stocks without a clean sector classification. Includes broad-market index funds, sector-specific ETFs, covered-call funds, and bond ETFs.' },
];

const MARKET_CAP_OPTIONS = [
  { value: 'all', label: 'Any Market Cap',
    tip: 'Market Cap = total value of a company\'s shares (shares × price). Bigger = more stable but slower growth. Smaller = more upside but more volatile.' },
  { value: 'mega', label: 'Mega ($200B+)', tip: 'Mega Cap: Companies worth $200 billion or more. The largest, most established corporations like Apple, Microsoft.' },
  { value: 'large', label: 'Large ($10B-$200B)', tip: 'Large Cap: Companies worth $10B-$200B. Well-known, stable companies with proven track records.' },
  { value: 'mid', label: 'Mid ($2B-$10B)', tip: 'Mid Cap: Companies worth $2B-$10B. Growing companies balancing stability with upside potential.' },
  { value: 'small', label: 'Small ($300M-$2B)', tip: 'Small Cap: Companies worth $300M-$2B. Higher growth potential but more volatile.' },
  { value: 'micro', label: 'Micro (<$300M)', tip: 'Micro Cap: Companies worth under $300M. Smallest publicly traded companies, highest risk/reward.' },
];

const PE_RATIO_OPTIONS = [
  { value: 'all', label: 'Any P/E',
    tip: 'P/E (Price-to-Earnings) ratio = price ÷ earnings per share. Lower = cheaper relative to earnings. 10–20 is usually "fair", 30+ means investors expect big growth.' },
  { value: '0-10', label: 'P/E < 10 (Value)', tip: 'P/E (Price-to-Earnings) under 10. These stocks are priced low relative to their earnings -- potential bargains or companies facing challenges.' },
  { value: '10-20', label: 'P/E 10-20 (Fair)', tip: 'P/E 10-20 is considered fairly valued. The stock price reasonably reflects the company\'s earnings power.' },
  { value: '20-30', label: 'P/E 20-30 (Growth)', tip: 'P/E 20-30 indicates growth expectations. Investors are paying a premium because they expect earnings to increase.' },
  { value: '30+', label: 'P/E > 30 (Premium)', tip: 'P/E above 30 means investors are paying a high premium. Typically fast-growing tech or market leaders.' },
];

const BETA_OPTIONS = [
  { value: 'all', label: 'Any Beta',
    tip: 'Beta = how much a stock moves vs the overall market. 1.0 = moves with the market. <1.0 = more stable. >1.0 = more volatile / bigger swings.' },
  { value: 'low', label: 'Low (<0.8)', tip: 'Beta measures volatility vs. the market. Beta under 0.8 means the stock moves less than the market -- more stable, less risky.' },
  { value: 'moderate', label: 'Moderate (0.8-1.2)', tip: 'Beta 0.8-1.2 means the stock moves roughly in line with the overall market.' },
  { value: 'high', label: 'High (>1.2)', tip: 'Beta above 1.2 means the stock is more volatile than the market. Bigger swings up and down.' },
];

const DEBT_EQUITY_OPTIONS = [
  { value: 'all', label: 'Any D/E',
    tip: 'Debt-to-Equity ratio = how much debt a company uses vs shareholder equity. Lower = financially conservative. Higher = more leverage, more risk (but also potentially more reward).' },
  { value: '0-50', label: '< 50% (Low) ✅', tip: 'D/E (Debt-to-Equity) under 50%. The company uses relatively little debt compared to shareholder equity -- financially conservative.' },
  { value: '50-100', label: '50% – 100% (Moderate) ⚖️', tip: 'D/E 50% – 100% is moderate leverage. The company uses a balanced mix of debt and equity financing.' },
  { value: '100+', label: '> 100% (High) ❌', tip: 'D/E above 100% means the company has more debt than equity. Higher financial risk but common in capital-intensive industries.' },
];

const ROIC_OPTIONS = [
  { value: 'all', label: 'Any ROIC',
    tip: 'ROIC (Return on Invested Capital) = how efficiently a company turns invested money into profit. Above 10% is healthy; above 20% is elite — signals a durable competitive moat.' },
  { value: 'negative', label: 'Negative ROIC', tip: 'ROIC (Return on Invested Capital) is negative. The company is destroying value -- earning less than it costs to fund operations.' },
  { value: '0-10', label: 'ROIC 0-10%', tip: 'ROIC 0-10%. The company earns a modest return on the money invested in it. May or may not cover its cost of capital.' },
  { value: '10-20', label: 'ROIC 10-20%', tip: 'ROIC 10-20%. A solid return indicating the company efficiently turns invested capital into profit. Generally beats the cost of capital.' },
  { value: '20+', label: 'ROIC 20%+ (Elite)', tip: 'ROIC above 20% is exceptional. The company has a strong competitive moat -- it generates outstanding returns on every dollar invested.' },
];

const FCF_MARGIN_OPTIONS = [
  { value: 'all', label: 'Any FCF Margin',
    tip: 'Free Cash Flow Margin = what % of revenue turns into actual cash after all expenses. High FCF companies comfortably fund dividends, buybacks, and growth without taking on debt.' },
  { value: 'negative', label: 'Negative FCF', tip: 'FCF (Free Cash Flow) Margin is negative. The company spends more cash than it generates -- may need financing to sustain operations.' },
  { value: '0-10', label: 'FCF 0-10%', tip: 'FCF Margin 0-10%. The company converts a small portion of revenue into free cash. Typical for capital-intensive or growth-stage businesses.' },
  { value: '10-20', label: 'FCF 10-20%', tip: 'FCF Margin 10-20%. A healthy level of cash generation. The company retains meaningful cash after all expenses and reinvestment.' },
  { value: '20+', label: 'FCF 20%+ (Cash Machine)', tip: 'FCF Margin above 20% is outstanding. The company is a cash-generating machine -- strong ability to fund dividends, buybacks, and growth.' },
];

// Category → icon + tone lookup, used by the mobile filter-summary
// pills AND the "Icon Key" popover legend. Module-level so both can
// share a single source of truth — keep new filter categories in
// sync here when they're added to activeFilters.
const FILTER_CAT_ICONS = {
  'Preset':            { Icon: Zap,           tone: 'fuchsia' },
  'Tier':              { Icon: Crown,         tone: 'amber'   },
  'Frequency':         { Icon: Clock,         tone: 'sky'     },
  'Yield':             { Icon: Percent,       tone: 'green'   },
  'Payout Ratio':      { Icon: PieChart,      tone: 'emerald' },
  'Sector':            { Icon: Building2,     tone: 'indigo'  },
  'Market Cap':        { Icon: Briefcase,     tone: 'violet'  },
  'P/E':               { Icon: BarChart3,     tone: 'blue'    },
  'Beta':              { Icon: Activity,      tone: 'rose'    },
  'D/E':               { Icon: Scale,         tone: 'orange'  },
  'ROIC':              { Icon: TrendingUp,    tone: 'teal'    },
  'FCF Margin':        { Icon: Banknote,      tone: 'lime'    },
  'Leveraged':         { Icon: AlertTriangle, tone: 'rose'    },
  'Futures':           { Icon: Layers,        tone: 'violet'  },
  'ADR':               { Icon: Globe,         tone: 'sky'     },
  'BDC':               { Icon: Building2,     tone: 'cyan'    },
  'CEF':               { Icon: Package,       tone: 'indigo'  },
  'REIT':              { Icon: Home,          tone: 'orange'  },
  'CEF Strategy':      { Icon: Target,        tone: 'fuchsia' },
  'Div Change':        { Icon: ArrowUpDown,   tone: 'emerald' },
  'Upcoming Ex-Div':   { Icon: CalendarClock, tone: 'sky'     },
  'Pays Specials':     { Icon: Gem, tone: 'amber' },
  'Anomalies':         { Icon: ShieldAlert, tone: 'fuchsia' },
  'Recently Paid Div': { Icon: CalendarCheck, tone: 'teal'    },
  // Round 98f (Feb 2026) — Growth + Forensics & Ownership categories.
  'Rule of 40 FCF':    { Icon: TrendingUp,    tone: 'emerald' },
  'Rule of 40 Op':     { Icon: TrendingUp,    tone: 'emerald' },
  'Rule of 40 SBC':    { Icon: TrendingUp,    tone: 'emerald' },
  'Rev CAGR 3y':       { Icon: TrendingUp,    tone: 'green'   },
  'Altman Zone':       { Icon: Scale,         tone: 'amber'   },
  'Forensic Model':    { Icon: AlertTriangle, tone: 'emerald' },
  'Founder-Led':       { Icon: Flag,          tone: 'sky'     },
  '1Y Return':         { Icon: TrendingUp,    tone: 'green'   },
  'Founder Min %':     { Icon: Flag,          tone: 'sky'     },
};
const FILTER_TONE_CLS = {
  sky:     'bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300 ring-1 ring-sky-400/40',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 ring-1 ring-emerald-400/40',
  green:   'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300 ring-1 ring-green-400/40',
  amber:   'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 ring-1 ring-amber-400/40',
  orange:  'bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300 ring-1 ring-orange-400/40',
  rose:    'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 ring-1 ring-rose-400/40',
  indigo:  'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 ring-1 ring-indigo-400/40',
  violet:  'bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300 ring-1 ring-violet-400/40',
  blue:    'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 ring-1 ring-blue-400/40',
  cyan:    'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/60 dark:text-cyan-300 ring-1 ring-cyan-400/40',
  teal:    'bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300 ring-1 ring-teal-400/40',
  lime:    'bg-lime-100 text-lime-700 dark:bg-lime-950/60 dark:text-lime-300 ring-1 ring-lime-400/40',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950/60 dark:text-fuchsia-300 ring-1 ring-fuchsia-400/40',
  slate:   'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300 ring-1 ring-slate-400/40',
};

const SCREENER_PRESETS = [
  { value: 'none', label: 'Strategies', group: 'default',
    tip: 'Strategies — one-click combinations of filters built around common dividend strategies (Blue Chip Income, Safe & Steady, Value Picks, etc). Skip the manual tuning and start from a proven preset.' },
  // Fund Type presets (single-attribute type toggles — extracted from the
  // old "Strategy" bucket into their own dropdown for cleaner navigation).
  { value: 'adr_stocks', label: 'ADRs (Foreign Companies)', group: 'Fund Type',
    tip: 'American Depositary Receipts (ADRs) — U.S.-listed certificates issued by a U.S. bank that represent shares of a foreign company. They trade on NYSE/NASDAQ in U.S. dollars and pay dividends in USD, so you can hold international names without a foreign brokerage account or currency conversion.',
    filters: { is_adr: 'only' } },
  { value: 'bdc_stocks', label: 'BDCs (Business Development Cos.)', group: 'Fund Type',
    tip: 'Business Development Companies — closed-end funds that loan to small and mid-sized private businesses. Required to distribute 90%+ of taxable income, so yields of 8-12% are normal (not necessarily yield traps). Examples: MAIN, ARCC, OBDC, HTGC.',
    filters: { is_bdc: 'only' } },
  { value: 'clo_etfs', label: 'CLO ETFs (Collateralized Loan Obligations)', group: 'Fund Type',
    tip: 'Collateralized Loan Obligation ETFs — pools of floating-rate senior-secured corporate loans sliced into rated tranches. AAA funds (JAAA, PAAA, CLOA) yield ~4.5-5%; BBB-B mezzanine funds (JBBB, CLOZ, BCLO) ~6-7%. Most pay monthly; the Reckoner Annual/Reinvesting wrappers (RAAY, RAAR, RCLY, RCLR) pay 0.00% by design and only appear in All / Non-Dividend mode. Sort by Yield to compare tranche risk.',
    filters: { is_clo: 'only' } },
  { value: 'cef_stocks', label: 'CEFs (Closed-End Funds)', group: 'Fund Type',
    tip: 'Closed-End Funds — exchange-traded funds with a fixed share count that can trade at a premium or discount to NAV. Often use leverage and distribute monthly with high yields (6-12%). Examples: PDI, UTG, GAB, NEA, BST.',
    filters: { is_cef: 'only' } },
  { value: 'leveraged_cefs', label: 'CEFs — Structurally Leveraged ⚡', group: 'Fund Type',
    tip: 'Closed-End Funds that use borrowed capital or preferred shares (typically 25-45% effective leverage) to boost distributions. Amplifies both yield and downside risk. Curated set of ~96 well-known names (PDI, PDO, UTG, DSL, PTY, ETV, GAB, etc.) across PIMCO, Nuveen, BlackRock, Eaton Vance, abrdn, and other CEF sponsors. Perfect for building a "high-yield leveraged monthly-payer" shortlist.',
    filters: { leveraged_cef: 'only' } },
  { value: 'leveraged_cefs_monthly', label: 'Leveraged CEFs ⚡ — Monthly Payers', group: 'Fund Type',
    tip: 'The "high-yield monthly leveraged" shortlist — structurally-leveraged Closed-End Funds (PDI, UTG, GAB, PTY, etc.) that ALSO pay monthly. These are the classic 8-12% yielding names that dividend investors reach for when they want monthly cash flow. Remember: leverage amplifies both the yield AND the drawdown.',
    filters: { leveraged_cef: 'only', frequency: 'Monthly' } },
  { value: 'reit_stocks', label: 'REITs (Real Estate Investment Trusts)', group: 'Fund Type',
    tip: 'Real Estate Investment Trusts — companies that own, operate, or finance income-producing real estate (apartments, offices, warehouses, data centers, cell towers, healthcare, mortgages). Required by law to distribute 90%+ of taxable income, so yields of 4-10% are normal. Examples: O, PLD, AMT, VICI, AGNC, STAG.',
    filters: { is_reit: 'only' } },
  { value: 'spac_stocks', label: 'SPACs (Blank-Check Companies)', group: 'Fund Type',
    tip: 'Special Purpose Acquisition Companies — cash shells listed to merge with a private business and take it public. Pay NO dividends (they hold IPO proceeds in trust until they find a target). Ticker often ends in U/UN (a "unit" = share + warrant). Only surfaces on the Non-Dividend screener. Examples: CEPS, CMIIU, CXIIU.',
    filters: { is_spac: 'only' } },
  { value: 'futures_etfs', label: 'Futures-Based ETFs', group: 'Fund Type',
    tip: 'ETFs using futures contracts for exposure (Bitwise Trendwise, Managed Futures, Return Stacked, Commodity Strategy, Invesco DB, Teucrium, etc.). These carry hidden structural leverage, margin risk, and roll costs. Not a traditional 2X/3X fund, but still higher risk than a standard equity ETF.',
    filters: { uses_futures: 'only' } },
  { value: 'cheap_broad_etfs', label: 'Cheap & Broad ETFs (≤0.10%)', group: 'Fund Type',
    tip: 'ETFs with an annual expense ratio of 0.10% or less. The cost-efficient core: Vanguard, Schwab, iShares index funds (VTI, SCHD, VOO, VYM, VIG, etc.) where almost none of your return is lost to fees. Use this as a counterpoint when browsing fee-heavy income strategies.',
    filters: { low_expense_ratio: 'only' } },
  { value: 'high_expense_etfs', label: 'High Expense Ratio ETFs (>0.50%)', group: 'Fund Type',
    tip: 'ETFs with an annual expense ratio above 0.50%. Surfaces fee-heavy covered-call, derivative-income, leveraged, and actively-managed funds whose costs quietly compound against your returns. Useful for fee-sensitive investors who want to spot-check the price of their income strategy.',
    filters: { high_expense_ratio: 'only' } },
  { value: 'leveraged_etfs', label: 'Leveraged & Inverse ETFs', group: 'Fund Type',
    tip: 'Amplified-exposure ETFs (2X / 3X / Ultra / UltraPro / Direxion Daily Bull & Bear). HIGH RISK -- these are short-term trading vehicles that lose value over time due to daily compounding (volatility decay). Not suitable for buy-and-hold dividend investing.',
    filters: { leveraged: 'only' } },
  // Round P140·4 (Feb 2026) — Warrants live in their own dedicated
  // screener at `/warrants` (schema doesn't overlap: exercise price +
  // underlying + redemption deadline instead of yield/coverage). This
  // entry is a *nav shortcut* — applyFundType() detects the `warrants`
  // value and redirects rather than filtering the current table.
  { value: 'warrants', label: 'Warrants (SPAC & IPO)', group: 'Fund Type',
    tip: 'Stock warrants — long-dated call-option-like contracts (usually 5-year expiry) attached to SPAC IPOs and post-merger companies. Trade on their own ticker (`.WS` / `.WT`). Opens the dedicated Warrant Screener with exercise price, distance-to-breakeven, and SEC 8-K redemption alerts.',
    filters: {} },
  // P158·14 (Feb 2026) — Money Market Fund preset. 5-letter tickers
  // ending in XX (SPAXX / VMFXX / FGXXX / SWVXX / …). Only surfaces
  // in Dividend or All modes — MMFs pay ordinary-income dividends
  // monthly, so they belong on the dividend side even though they
  // never appear in traditional yield/frequency fields.
  // Oct 2026 — Preferred Shares preset. ~40 exchange-listed preferreds /
  // depositary shares (5th-letter P/O/M/N tickers + treasury-vehicle
  // preferreds). Swaps the stock columns for Par · Coupon · vs Par ·
  // Call Date · Type, mirroring the MMF column swap.
  { value: 'preferred_shares', label: 'Preferred Shares (Par & Call Terms)', group: 'Fund Type',
    tip: 'Exchange-listed preferred stock and depositary shares — fixed-income equity paying a stated coupon on a $25 / $100 par value. The table swaps in Par, Coupon, Price-vs-Par, Call Date and Cumulative/Term type so you can spot above-par call risk at a glance. Read the /learn/preferred-stock-tickers guide for the full picture.',
    filters: { is_preferred: 'only' } },
  { value: 'money_market_funds', label: 'Money Market Funds (XX Tickers)', group: 'Fund Type',
    tip: 'US money market funds — 5-letter tickers ending in double-XX (SPAXX / VMFXX / FGXXX / SWVXX / etc.). Target a constant $1.00 NAV, pay yield monthly from short-dated Treasury / repo / govt paper. Used as brokerage cash-sweep vehicles and as short-term cash-equivalents. Read the /learn/money-market-funds guide for the full picture.',
    filters: { is_money_market_fund: 'only' } },
  // Strategy presets (multi-filter combos — alphabetical by label)
  { value: 'blue_chip', label: 'Blue Chip Income', group: 'Strategy',
    tip: 'The largest companies in the world (market cap $200B+) paying safe dividends (0-3% yield). Maximum stability with reliable income.',
    filters: { market_cap: 'mega', yield_range: '0-3' } },
  { value: 'buying_calendar', label: 'Buying Calendar (A-Grade, ≤15 days)', group: 'Strategy',
    tip: 'Buying Calendar — A-grade Dividend Quality (70+) names whose next ex-dividend date is within the next 15 days. Designed as a one-click answer to "what high-quality dividend stocks pay soon?" so you can act fast on imminent payments.',
    filters: { min_quality: 70, next_ex_within_days: 15 } },
  { value: 'coverage_watch', label: 'Coverage Watch (Cut-Risk Radar)', group: 'Strategy',
    tip: 'Coverage Watch — dividend payers whose SEC-reported distribution coverage (FCF for corporates, FFO for REITs, NII for BDCs/CEFs) is BELOW 1.0× on a trailing-twelve-month basis AND deteriorating quarter-over-quarter. They\'re paying out more than they generate and the gap is widening — the classic setup BEFORE a cut announcement. Sourced straight from SEC XBRL filings (the same data behind the Quality Score\'s forward signals). Click any row\'s Quality pill for the full coverage breakdown.',
    filters: { max_coverage_ratio: 1.0, max_coverage_trend: 0 } },
  { value: 'coverage_improving', label: 'Coverage Improving (Recovery Radar)', group: 'Strategy',
    tip: 'Coverage Improving — dividend payers whose SEC-reported distribution coverage (FCF for corporates, FFO for REITs, NII for BDCs/CEFs) is ABOVE 1.0× on a trailing-twelve-month basis AND rising more than +10% over the last ~4 quarters. Cash generation is strengthening relative to the payout — the classic setup BEFORE a dividend raise is announced. The recovery-play mirror of Coverage Watch, sourced from the same SEC XBRL filings. Tap any row\'s coverage value for the per-quarter breakdown.',
    filters: { min_coverage_ratio: 1.0, min_coverage_trend: 10 } },
  { value: 'recently_paid_div', label: 'Recently Paid Div (last 7 days)', group: 'Strategy',
    tip: 'Recently Paid Div — stocks whose ex-dividend date fell in the past 7 days. Great for spotting names you almost owned in time, sanity-checking whether you missed buying a stock you meant to, or just seeing what just paid out across the dividend market. Sorted newest-first.',
    filters: { recently_paid_within_days: 7 } },
  { value: 'growth_income', label: 'Growth & Income', group: 'Strategy',
    tip: 'Fairly valued companies (P/E 10-20) with safe dividends (0-3%) and average market volatility. Balances capital appreciation with income.',
    filters: { pe_ratio: '10-20', yield_range: '0-3', beta: 'moderate' } },
  { value: 'low_vol', label: 'Low Volatility Income', group: 'Strategy',
    tip: 'Stocks that move less than the market (beta under 0.8) with safe dividends (0-3%). Smoother ride for income-focused investors.',
    filters: { beta: 'low', yield_range: '0-3' } },
  { value: 'mega_fortress', label: 'Mega Cap Fortress', group: 'Strategy',
    tip: 'The biggest companies ($200B+) with low debt (D/E under 50%). Financially bulletproof -- strong balance sheets built to weather any storm.',
    filters: { market_cap: 'mega', debt_equity: '0-50' } },
  { value: 'safe_steady', label: 'Safe & Steady', group: 'Strategy',
    tip: 'Conservative picks: low payout ratio (under 60%), low volatility (beta under 0.8), and safe yield (0-3%). Prioritizes dividend safety over high income.',
    filters: { payout_ratio: '0-60', beta: 'low', yield_range: '0-3' } },
  { value: 'small_cap_div', label: 'Small Cap Dividend', group: 'Strategy',
    tip: 'Smaller companies ($300M-$2B) paying above-average dividends (4-6%). Higher growth potential but more volatility than large caps.',
    filters: { market_cap: 'small', yield_range: '4-6' } },
  { value: 'undervalued', label: 'Undervalued Income', group: 'Strategy',
    tip: 'Cheap stocks (P/E under 10) paying good dividends (4-6%). The market may be underpricing these income generators.',
    filters: { pe_ratio: '0-10', yield_range: '4-6' } },
  { value: 'value_picks', label: 'Value Picks', group: 'Strategy',
    tip: 'Stocks priced low relative to earnings (P/E under 10) with conservative debt levels (D/E under 50). Classic value investing approach.',
    filters: { pe_ratio: '0-10', debt_equity: '0-50' } },
  // Round 98c (Jul 2026) — Non-Div Screener presets. Show up in the
  // Strategies dropdown when the 3-way switch is on Non-Dividend or
  // All. These use Rule of 40 (FCF + Op) thresholds that live on
  // equity_quality.rule_of_40_*_raw, stored as PERCENT (50 = 50%).
  { value: 'efficient_scalers', label: 'Efficient Scalers', group: 'Non-Div Strategy',
    tip: 'Efficient Scalers — non-dividend companies clearing BOTH sides of the Rule of 40: FCF variant ≥ 50% AND Operating variant ≥ 40%. Growth is real (revenue expanding) AND profitable at the same time. The SaaS/growth-investor benchmark for "worth paying the multiple". ~200 stocks match today.',
    filters: { min_rule_of_40_fcf: 50, min_rule_of_40_op: 40 } },
  // Round 98d (Feb 2026) — Non-Div preset #2. Micro/small-cap
  // survivors: mkt cap $100M–$2B, Beneish M < −2.22 (safe / not a
  // manipulator per historical accounting-fraud threshold), Altman
  // Z > 2.99 (safe / low-bankruptcy-risk zone), and 3y revenue CAGR
  // ≥ 15% (real growth). Lite = we skip the heavier momentum +
  // founder-led lenses used in the full preset.
  { value: 'micro_cap_giants_lite', label: 'Micro-Cap Giants Lite', group: 'Non-Div Strategy',
    tip: 'Micro-Cap Giants Lite — small/mid names ($100M–$2B) that pass ALL of: Beneish M < −2.22 (accounting-safe), Altman Z > 2.99 (bankruptcy-safe), 3y revenue CAGR ≥ 15% (real growth). Screens for the compounders BEFORE Wall Street notices. "Lite" = full preset (with momentum + founder-led) coming after DB backfill.',
    filters: { market_cap_min: 100_000_000, market_cap_max: 2_000_000_000, max_beneish_m: -2.22, min_altman_z: 2.99, min_rev_cagr_3y: 0.15 } },
  // Sep 2026 — Non-Div preset: Mark Minervini's SEPA Trend Template.
  // Nightly Massive scan (services/minervini.py) writes stock_cache.minervini;
  // preset = all 8 Trend Template rules + latest-quarter YoY EPS ≥ 20%.
  // "Lite" = VCP is a tightness proxy badge, not a requirement.
  { value: 'minervini_sepa_lite', label: 'Minervini SEPA Lite', group: 'Non-Div Strategy',
    tip: 'Minervini SEPA Lite — Mark Minervini\'s Stage-2 Trend Template, all 8 rules: price above the 50/150/200-day averages, 50 > 150 > 200, 200-day rising for a month, ≥30% above the 52-week low, within 25% of the 52-week high, relative strength in the top 30% of non-dividend stocks — plus latest-quarter EPS up ≥20% year over year. Rows with a "Tight base" badge also show VCP-style contraction (10-day range and volume both drying up vs the 50-day). Recalculated nightly after the close.',
    filters: { minervini_sepa: true } },
  // Round 98d (Feb 2026) — Non-Div preset #3. Companies with a
  // clean quality model (forensic_penalty_applied=false — no red
  // flags in accounting, dilution, share float, working capital)
  // that ALSO clear the Rule of 40 FCF cutoff at 40%. Broader net —
  // graduates to the FULL version below by layering on a ≥10% Form 3
  // founder-stake filter.
  { value: 'forensic_green_compounders_lite', label: 'Forensic-Green Compounders Lite', group: 'Non-Div Strategy',
    tip: 'Forensic-Green Compounders Lite — non-dividend names that (a) pass our forensic quality model with ZERO penalties (clean accounting, no dilution trap, no working-capital red flags) AND (b) clear the Rule of 40 FCF variant at ≥ 40%. The intersection of "trustworthy books" + "actually growing profitably". Broader net than the Full version — includes Wall-Street-owned quality growth without insisting on founder-scale ownership.',
    filters: { forensic_green: true, min_rule_of_40_fcf: 40 } },
  // Round 98i (Jul 2026) — Full-flavor version of Forensic-Green
  // Compounders. Adds a ≥10% Form 3 top-holder floor on top of the
  // Lite filters so we only surface founder- or insider-controlled
  // clean-books compounders. ARM/NOW/SPOT/VIK/FSLR/MEDP/DT class.
  // Match count against production DB at ship time: 29 tickers.
  { value: 'forensic_green_compounders_full', label: 'Forensic-Green Compounders (Insider-Controlled)', group: 'Non-Div Strategy',
    tip: 'Forensic-Green Compounders (Insider-Controlled) — the Lite version tightened with a MATERIAL insider floor. Ticker must (a) pass our forensic quality model with ZERO penalties, (b) clear the Rule of 40 FCF variant at ≥ 40%, AND (c) show a Form 3 top-holder position of ≥ 10% of the float (founder-scale ownership). Screens for the "clean books × profitably growing × management still has skin" three-way intersection — historically the compounders like ARM, ServiceNow, Spotify, Vik Cruises, First Solar.',
    filters: { forensic_green: true, min_rule_of_40_fcf: 40, founder_led: true, min_founder_top_pct: 0.10 } },
  // Round 98g (Feb 2026) — Non-Div preset #4. "Founders Who
  // Compound": companies where the Form 3 top-holder is parked at
  // founder-scale AND the underlying business is real (Rule of 40
  // FCF ≥ 30 = growing profitably) AND survivable (Altman Z NOT
  // Distress). Skips-the-story-stocks by requiring the profitable-
  // growth floor. When Form 3 insider % + trailing return backfill
  // ships, this preset expands into a full Founder-Led + Momentum
  // stack; today it's the "lite" version.
  { value: 'founder_led_compounders', label: 'Founder-Led Compounders', group: 'Non-Div Strategy',
    tip: 'Founder-Led Compounders — non-dividend companies where a Form 3 filing shows a founder-scale top holder AND the business is profitably growing (Rule of 40 FCF ≥ 30%) AND balance sheet is NOT in the Altman Distress zone. Filters out story-stock founders and mattress-money biotechs while keeping the compounders like AAPL-in-1985 or MELI-in-2007.',
    filters: { founder_led: true, min_rule_of_40_fcf: 30, altman_zone_filter: 'safe' } },
  // Round 98h (Feb 2026) — Full (non-Lite) versions of the two
  // "waiting on backfill" presets. Both cron requirements are now
  // met (top_pct populated by form3_warmer, perf_1y populated by
  // daily-weekly-tickers cron) so we can ship the tight versions.
  { value: 'founder_led_full', label: 'Founder-Led (Skin in the Game)', group: 'Non-Div Strategy',
    tip: 'Founder-Led (Skin in the Game) — tightens Founder-Led Compounders with a MATERIAL insider-% floor. Founder must own ≥ 10% of the float (single-holder), business must be profitably growing (Rule of 40 FCF ≥ 30%), and balance sheet must be Safe (Altman Z > 2.99). Screens for the "founder is meaningfully rich or broke on this company" pattern that historically outperforms.',
    filters: { founder_led: true, min_founder_top_pct: 0.10, min_rule_of_40_fcf: 30, altman_zone_filter: 'safe' } },
  { value: 'quality_momentum', label: 'Quality Momentum', group: 'Non-Div Strategy',
    tip: 'Quality Momentum — non-dividend Growth-tier B or better (score ≥ 55) that has ALSO already outperformed the market on a trailing-12-month basis (perf_1y ≥ 20%). Classic Novy-Marx quality+momentum combo — buys stuff that\'s working AND survivable, not just fast movers.',
    filters: { min_quality: 55, min_perf_1y: 20 } },
];

const PAYOUT_COLORS = {
  Weekly: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  Monthly: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  Quarterly: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'Semi-Annually': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Annually: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

function TierBadge({ tier }) {
  if (!tier) return null;
  const config = {
    king: { icon: '\u{1F451}', label: 'King', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' },
    aristocrat: { icon: '\u{1F3C6}', label: 'Aristocrat', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
    achiever: { icon: '\u{1F396}\uFE0F', label: 'Achiever', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  };
  const c = config[tier];
  if (!c) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.cls}`} title={`Dividend ${c.label}`}>
      {c.icon}
    </span>
  );
}

function PerfCell({ value }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">--</span>;
  const isPositive = value >= 0;
  return (
    <span className={`font-mono text-sm font-medium ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
      {isPositive ? '+' : ''}{value}%
    </span>
  );
}

// Stale-price visuals removed (May 2026): see StockTable.js — Massive WS
// keeps prices live, so the per-row "Updated …" hint and AGE_COLORS class
// no longer apply. Prices render in the standard text color.

// Tooltip descriptions lookup for filter dropdown items
const FILTER_TIPS = {};
[...MARKET_CAP_OPTIONS, ...PE_RATIO_OPTIONS, ...BETA_OPTIONS, ...DEBT_EQUITY_OPTIONS, ...PAYOUT_RATIO_OPTIONS, ...ROIC_OPTIONS, ...FCF_MARGIN_OPTIONS].forEach(o => {
  if (o.tip) FILTER_TIPS[o.value] = o.tip;
});
SCREENER_PRESETS.forEach(p => {
  if (p.tip) FILTER_TIPS[`preset_${p.value}`] = p.tip;
});

/**
 * ScreenerTooltip — element-anchored popover used by the filter dropdowns.
 *
 * Previous version tracked mouse x/y, which on mobile put the bubble
 * directly under the finger (so you couldn't read it) and would vanish as
 * soon as the finger moved. This version:
 *   - Anchors to the trigger's bounding rect (stable position).
 *   - Prefers above-the-element, flips below when too close to top.
 *   - Horizontally clamps to stay fully inside the viewport.
 *   - Works for mouse hover AND touch (shown via explicit tap handler).
 */
function ScreenerTooltip({ tip, anchorRect }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  // Re-compute position after the bubble actually mounts so clamping uses
  // the real rendered width (short text renders < MAX_W). Runs once per
  // (tip, anchorRect) change — microtask cost is trivial.
  useEffect(() => {
    if (!tip || !anchorRect) {
      setPos(null);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const MARGIN = 8;
    const bubbleW = node.offsetWidth || 320;
    const bubbleH = node.offsetHeight || 100;
    // Prefer the document element's client width since it excludes the
    // vertical scrollbar — `window.innerWidth` sometimes includes it and
    // caused right-edge filters to overhang by ~15px.
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;

    // Center above the trigger by default; flip below when there's not
    // enough room above for the full bubble + margin.
    let top;
    if (anchorRect.top - bubbleH - MARGIN >= 0) {
      top = anchorRect.top - bubbleH - MARGIN;
    } else {
      top = Math.min(anchorRect.bottom + MARGIN, vh - bubbleH - MARGIN);
    }

    let left = anchorRect.left + anchorRect.width / 2 - bubbleW / 2;
    // Horizontally clamp to keep the whole bubble inside the viewport.
    left = Math.max(MARGIN, Math.min(vw - bubbleW - MARGIN, left));
    setPos({ left, top });
  }, [tip, anchorRect]);

  if (!tip || !anchorRect) return null;

  return (
    <div
      ref={ref}
      className="fixed z-[9999] px-3 py-2.5 rounded-lg bg-card border border-border shadow-xl text-xs text-foreground leading-relaxed pointer-events-none"
      style={{
        left: pos ? `${pos.left}px` : '-9999px',
        top: pos ? `${pos.top}px` : '-9999px',
        maxWidth: '320px',
        opacity: pos ? 1 : 0,
        transition: 'opacity 120ms ease',
      }}
      data-testid="screener-tooltip"
    >
      {tip}
    </div>
  );
}

// ───── Copy Filter Link helpers (Round 68 Feb 2026) ─────
// Builds a URL-encoded querystring representing every active filter
// so the user can paste a sharable link that pre-applies the same
// Screener state on the recipient's screen. Mirror image of the
// hydration useEffect that reads the same keys back on first mount.
function buildSharableFilterParams(s) {
  const out = {};
  if (s.tierFilter && s.tierFilter !== 'all') out.tier = s.tierFilter;
  if (s.frequencyFilter && s.frequencyFilter !== 'all') out.frequency = s.frequencyFilter;
  if (s.yieldFilter && s.yieldFilter !== 'all') out.yield_range = s.yieldFilter;
  if (s.payoutRatioFilter && s.payoutRatioFilter !== 'all') out.payout_ratio = s.payoutRatioFilter;
  if (s.sectorFilter && s.sectorFilter !== 'all') out.sector = s.sectorFilter;
  if (s.marketCapFilter && s.marketCapFilter !== 'all') out.market_cap = s.marketCapFilter;
  if (s.peRatioFilter && s.peRatioFilter !== 'all') out.pe_ratio = s.peRatioFilter;
  if (s.betaFilter && s.betaFilter !== 'all') out.beta = s.betaFilter;
  if (s.debtEquityFilter && s.debtEquityFilter !== 'all') out.debt_equity = s.debtEquityFilter;
  if (s.roicFilter && s.roicFilter !== 'all') out.roic = s.roicFilter;
  if (s.fcfMarginFilter && s.fcfMarginFilter !== 'all') out.fcf_margin = s.fcfMarginFilter;
  if (s.leveragedFilter && s.leveragedFilter !== 'all') out.leveraged = s.leveragedFilter;
  if (s.highExpenseRatioFilter && s.highExpenseRatioFilter !== 'all') out.high_expense_ratio = s.highExpenseRatioFilter;
  if (s.lowExpenseRatioFilter && s.lowExpenseRatioFilter !== 'all') out.low_expense_ratio = s.lowExpenseRatioFilter;
  if (s.usesFuturesFilter && s.usesFuturesFilter !== 'all') out.uses_futures = s.usesFuturesFilter;
  if (s.isAdrFilter && s.isAdrFilter !== 'all') out.is_adr = s.isAdrFilter;
  if (s.isBdcFilter && s.isBdcFilter !== 'all') out.is_bdc = s.isBdcFilter;
  if (s.isCloFilter && s.isCloFilter !== 'all') out.is_clo = s.isCloFilter;
  if (s.isMoneyMarketFundFilter && s.isMoneyMarketFundFilter !== 'all') out.is_money_market_fund = s.isMoneyMarketFundFilter;
  if (s.isPreferredFilter && s.isPreferredFilter !== 'all') out.is_preferred = s.isPreferredFilter;
  if (s.isCefFilter && s.isCefFilter !== 'all') out.is_cef = s.isCefFilter;
  if (s.leveragedCefFilter && s.leveragedCefFilter !== 'all') out.leveraged_cef = s.leveragedCefFilter;
  if (s.isReitFilter && s.isReitFilter !== 'all') out.is_reit = s.isReitFilter;
  if (s.isSpacFilter && s.isSpacFilter !== 'all') out.is_spac = s.isSpacFilter;
  if (s.dividendChangeFilter && s.dividendChangeFilter !== 'all') out.dividend_change = s.dividendChangeFilter;
  if (s.minStreakYears && Number(s.minStreakYears) > 0) out.min_streak_years = String(s.minStreakYears);
  if (s.minRecentRaisePct && Number(s.minRecentRaisePct) > 0) out.min_recent_raise_pct = String(s.minRecentRaisePct);
  if (s.cefStrategyFilter && s.cefStrategyFilter !== 'all') out.cef_strategy = s.cefStrategyFilter;
  if (s.nextExWithinDays > 0) out.next_ex_within_days = String(s.nextExWithinDays);
  if (s.paysSpecialsFilter && s.paysSpecialsFilter !== 'all') out.pays_specials = s.paysSpecialsFilter;
  if (s.anomalyFilter && s.anomalyFilter !== 'all') out.anomalies = s.anomalyFilter;
  if (s.recentlyPaidWithinDays > 0) out.recently_paid_within_days = String(s.recentlyPaidWithinDays);
  if (s.minQuality > 0) out.min_quality = String(s.minQuality);
  if (s.sortKey && s.sortKey !== 'name') out.sort_by = s.sortKey;
  if (s.sortDir && s.sortDir !== 'asc') out.sort_dir = s.sortDir;
  return out;
}

function CopyFilterLinkButton({ buildParams }) {
  const [busy, setBusy] = useState(false);
  const handleCopy = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const params = buildParams();
      const keys = Object.keys(params);
      const qs = new URLSearchParams(params).toString();
      const base = `${window.location.origin}/screener`;
      const url = qs ? `${base}?${qs}` : base;
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        // Clipboard API can fail in iframes / non-secure contexts;
        // fall back to a temporary textarea + execCommand('copy').
        try {
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          copied = document.execCommand('copy');
          document.body.removeChild(ta);
        } catch { /* ignore */ }
      }
      if (copied) {
        toast.success(
          keys.length === 0
            ? 'Filter link copied (no filters active)'
            : `Filter link copied — ${keys.length} filter${keys.length === 1 ? '' : 's'} encoded`,
          {
            description: 'Paste anywhere — the recipient lands on this exact Screener view.',
            duration: 6000,
          }
        );
      } else {
        toast.error('Could not copy link', {
          description: 'Long-press the address bar to copy the URL manually.',
        });
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-md border border-cyan-300/50 dark:border-cyan-700/50 bg-cyan-50/60 dark:bg-cyan-950/30 text-[11px] font-semibold text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-950/60 transition-colors disabled:opacity-60 shrink-0"
      title="Copy a sharable URL that pre-applies your current Screener filters. Paste it anywhere — the recipient lands on this exact view."
      data-testid="screener-copy-filter-link-btn"
    >
      <Link2 className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">Copy filter link</span>
      <span className="sm:hidden">Copy link</span>
    </button>
  );
}

export default function Screener() {
  const { isDark } = useMarketTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Round P140·3 (Feb 2026) — Silent redirect for `/screener?asset_type=warrant`.
  // Warrants have their own dedicated screener at `/warrants` (columns
  // don't overlap with regular equity screens — no yield, no coverage,
  // but they DO have exercise price + underlying + redemption
  // deadline). Bounces before the rest of the Screener mounts so
  // there's no flash of the wrong UI.
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      if ((params.get('asset_type') || '').toLowerCase() === 'warrant') {
        navigate('/warrants', { replace: true });
      }
    } catch { /* URLSearchParams unavailable — noop */ }
  }, [location.search, navigate]);
  // Round 97a (Jul 4 2026) — Non-Dividend Expansion. Admin bypass so
  // the site owner can preview All / Non-Dividend modes ahead of the
  // Stripe paywall going live.
  const isAdmin = ((user || {}).role || '') === 'admin';
  // Seed searchQuery from `?q=` on first mount so peer-compare links
  // from the Stock Modal land here with the issuer name pre-filled.
  const initialQ = (() => {
    try { return new URLSearchParams(location.search).get('q') || ''; } catch { return ''; }
  })();
  // Round 97a — 3-way "Dividend / All / Non-Dividend" segmented control.
  // Seeds from `?filter=` (dividend|all|non_dividend). We trust the URL
  // param here (no client-side admin gate at seed time — the auth
  // context is still resolving on first render, so a client-side gate
  // would downgrade every deep-link into Dividend mode before the user
  // record loads). The backend enforces the real access control by
  // silently downgrading non-admin callers to `dividend` server-side.
  const initialDividendFilter = (() => {
    try {
      const raw = (new URLSearchParams(location.search).get('filter') || 'dividend').toLowerCase();
      if (!['dividend', 'all', 'non_dividend'].includes(raw)) return 'dividend';
      return raw;
    } catch { return 'dividend'; }
  })();
  const [dividendFilter, setDividendFilter] = useState(initialDividendFilter);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Round 96n (Jul 3 2026) — Real vs Nominal Yield display toggle for
  // the Screener's results table. Filters + sorts continue to run on
  // NOMINAL yield at the backend (unchanged); this toggle only
  // overlays a "yield − CPI YoY" value on the client. Shares the
  // same localStorage key as StockTable so the user's preference
  // sticks across the whole app.
  const cpiYoy = useCpiYoy();
  const [yieldMode, setYieldMode] = useState(() => {
    try { return localStorage.getItem('dividrip.yieldMode') || 'nominal'; } catch { return 'nominal'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dividrip.yieldMode', yieldMode); } catch { /* ignore */ }
  }, [yieldMode]);
  const effectiveYieldMode = (yieldMode === 'real' && typeof cpiYoy !== 'number') ? 'nominal' : yieldMode;

  // Round P144 (Feb 2026) — "Allow SPAC & Warrant" checkbox. Default
  // OFF so the screener hides blank-check shells and warrant tickers
  // (a "price asc" sort was surfacing a wall of $0.00 SPAC units
  // ahead of any real stock). Session-persisted so navigating between
  // screener presets and back preserves the choice for the current
  // visit. Auto-checks whenever a SPAC-only or Warrants fund-type
  // filter is active (see the effect below) and auto-unchecks when
  // that filter is cleared. Users who tick the checkbox manually
  // still get session-persistence just like the auto path.
  const [allowSpacsWarrants, setAllowSpacsWarrants] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return sessionStorage.getItem('dividrip.allowSpacsWarrants') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem('dividrip.allowSpacsWarrants', allowSpacsWarrants ? '1' : '0');
    } catch { /* ignore */ }
  }, [allowSpacsWarrants]);
  // Sep 2026 — "Allow: Money Market Funds" checkbox + MMF Type dropdown
  // (Dividend / All modes). Same session-scoped pattern as SPAC & Warrant.
  const [allowMmf, setAllowMmf] = useState(() => {
    try { return sessionStorage.getItem('dividrip.allowMmf') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('dividrip.allowMmf', allowMmf ? '1' : '0'); } catch { /* ignore */ }
  }, [allowMmf]);
  const [mmfTypeFilter, setMmfTypeFilter] = useState('all');

  // Round 84k Phase C scaffolding (Feb 15, 2026) — subscribe to live
  // prices for the 25 stocks visible on the current screener page.
  // `stocks` is server-paginated (per_page: 25) so it IS the visible
  // page — every filter / sort / search / page change triggers a
  // refetch which rebuilds the array, which retriggers the SSE
  // subscription. The chip flips from POLLING to pulsing LIVE once
  // the paid Massive WebSocket plan is active.
  const screenerSubTickers = useMemo(() => {
    return stocks.map((s) => String(s?.ticker || '').toUpperCase()).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stocks.map((s) => s?.ticker).join(',')]);
  const { status: screenerStreamStatus } = usePriceStream(screenerSubTickers);
  // Frequency breakdown chip data — populated only when a date-range
  // filter (Upcoming Ex-Div / Recently Paid Div) is on. Each item is
  // {frequency: "Quarterly", count: 52}.
  const [freqBreakdown, setFreqBreakdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialQ);
  // Round 92o++++++++N++++++++++ (May 2026) — local-input mirror so
  // typed chars render instantly while the actual `searchQuery`
  // state (which drives the screener's filter pipeline) is
  // debounced 1.5s after the last keystroke. See
  // hooks/useDebouncedSetter.js for the contract. Search logic
  // itself is untouched — only the trigger timing changes.
  const [searchInput, setSearchInput] = useState(initialQ);
  const debouncedSetSearchQuery = useDebouncedSetter(setSearchQuery, 1000);
  // Mirror any external `searchQuery` change (e.g. URL `?q=` hydration,
  // CopyFilterLink loaders) back into the visible input.
  useEffect(() => {
    setSearchInput((prev) => (prev === searchQuery ? prev : searchQuery));
  }, [searchQuery]);

  // Round 92o++++++++N++++++++++ (Feb 2026) — re-hydrate `searchQuery`
  // whenever the URL's `?q=` changes WHILE the Screener page stays
  // mounted. React Router doesn't unmount when navigating between
  // /screener URLs (e.g. clicking the Stock-Modal "Compare peer funds
  // from same issuer" link from a stock that was itself opened from
  // /screener), so the initial-mount `useState(initialQ)` above never
  // re-fires. This effect closes that gap so peer-compare navigation
  // always lands with the new issuer name in the search box.
  useEffect(() => {
    try {
      const p = new URLSearchParams(location.search);
      const newQ = p.get('q') || '';
      setSearchQuery((prev) => (prev === newQ ? prev : newQ));
    } catch { /* no-op */ }
  }, [location.search]);

  // Filters
  const [tierFilter, setTierFilter] = useState('all');
  const [frequencyFilter, setFrequencyFilter] = useState('all');
  const [yieldFilter, setYieldFilter] = useState('all');
  const [payoutRatioFilter, setPayoutRatioFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  // Round 98a (Jul 2026) — Industry filter with searchable typeahead.
  // 300+ industries makes a flat Select unusable, so we ship it as a
  // Popover + Command combo. The list is fetched per-mode from
  // /api/screener/industries (Dividend / All / Non-Dividend slice).
  const [industryFilter, setIndustryFilter] = useState('all');
  const [industryList, setIndustryList] = useState([]);
  const [industryTopExample, setIndustryTopExample] = useState(null);
  // Round 98c (Jul 2026) — Non-Div Screener preset filters. Values in
  // PERCENT to match backend storage on equity_quality.rule_of_40_*_raw.
  const [minRuleOf40Fcf, setMinRuleOf40Fcf] = useState(0);
  const [minRuleOf40Op, setMinRuleOf40Op] = useState(0);
  const [minRuleOf40Sbc, setMinRuleOf40Sbc] = useState(0);
  // Round 98d (Feb 2026) — Additional preset primitives powering
  // Micro-Cap Giants Lite + Forensic-Green Compounders Lite. Zero /
  // false = "off" (backend omits the constraint) so composing with
  // Efficient Scalers doesn't over-restrict the result set.
  const [marketCapMin, setMarketCapMin] = useState(0);
  const [marketCapMax, setMarketCapMax] = useState(0);
  const [maxBeneishM, setMaxBeneishM] = useState(0);
  const [minAltmanZ, setMinAltmanZ] = useState(0);
  const [minRevCagr3y, setMinRevCagr3y] = useState(0);
  const [forensicGreen, setForensicGreen] = useState(false);
  const [minerviniSepa, setMinerviniSepa] = useState(false);
  // Round 98f (Feb 2026) — Growth + Forensics & Ownership category
  // filters. Categorical version of the numeric preset primitives so
  // users can compose their own screens without invoking a preset.
  // Empty-string = 'all' (no filter). Zone filter goes on its own
  // slot; other two are booleans.
  const [altmanZoneFilter, setAltmanZoneFilter] = useState('all');
  const [founderLedFilter, setFounderLedFilter] = useState(false);
  // Round 98h (Feb 2026) — Full preset primitives. `minFounderTopPct`
  // stored as a FRACTION (0.10 = 10% of float). `minPerf1y` stored
  // as PERCENT (20 = 20%) matching how perf_1y ships from the daily
  // cron. -9999 sentinel = "no floor" so 0% is still a valid filter.
  const [minFounderTopPct, setMinFounderTopPct] = useState(0);
  const [minPerf1y, setMinPerf1y] = useState(-9999);
  // Jul 2026 — Coverage Watch preset primitives (SEC XBRL coverage).
  const [maxCoverageRatio, setMaxCoverageRatio] = useState(0);
  const [maxCoverageTrend, setMaxCoverageTrend] = useState(-9999);
  // Coverage Improving (recovery radar) mirror primitives.
  const [minCoverageRatio, setMinCoverageRatio] = useState(0);
  const [minCoverageTrend, setMinCoverageTrend] = useState(-9999);
  const coverageColumnActive = maxCoverageRatio > 0 || maxCoverageTrend > -9000 || minCoverageRatio > 0 || minCoverageTrend > -9000;
  const [marketCapFilter, setMarketCapFilter] = useState('all');
  const [peRatioFilter, setPeRatioFilter] = useState('all');
  const [betaFilter, setBetaFilter] = useState('all');
  const [debtEquityFilter, setDebtEquityFilter] = useState('all');
  const [roicFilter, setRoicFilter] = useState('all');
  const [fcfMarginFilter, setFcfMarginFilter] = useState('all');
  const [leveragedFilter, setLeveragedFilter] = useState('all');
  const [highExpenseRatioFilter, setHighExpenseRatioFilter] = useState('all');
  const [lowExpenseRatioFilter, setLowExpenseRatioFilter] = useState('all');
  const [usesFuturesFilter, setUsesFuturesFilter] = useState('all');
  const [isAdrFilter, setIsAdrFilter] = useState('all');
  const [isBdcFilter, setIsBdcFilter] = useState('all');
  const [isCloFilter, setIsCloFilter] = useState('all');
  // Sep 2026 — CLO peer leaderboard column. When the CLO ETFs fund-type
  // filter is active (isCloFilter === 'only'), the screener adds a
  // "3yr Sharpe" column just before Price so users can rank the ~30
  // CLO ETFs by risk-adjusted return at a glance. One bulk fetch
  // covers every CLO ticker; the column disappears when the filter
  // clears. `cloSharpeSort` piggybacks on the existing sort UI —
  // Mongo doesn't know clo_sharpe, so we sort the current page
  // client-side when this key is active.
  const [cloBulk, setCloBulk] = useState(null);
  const cloView = isCloFilter === 'only';
  useEffect(() => {
    if (!cloView) { setCloBulk(null); return; }
    let cancelled = false;
    axios.get(`${API}/clo-metrics/bulk`, { timeout: 20000 })
      .then((r) => { if (!cancelled) setCloBulk(r.data?.metrics || {}); })
      .catch(() => { if (!cancelled) setCloBulk({}); });
    return () => { cancelled = true; };
  }, [cloView]);
  // P158·14 (Feb 2026) — Money Market Fund filter state. Drives the
  // Fund Type "Money Market Funds (XX Tickers)" preset.
  const [isMoneyMarketFundFilter, setIsMoneyMarketFundFilter] = useState('all');
  // Oct 2026 — Preferred Shares fund type ('all' | 'only' | 'hide').
  const [isPreferredFilter, setIsPreferredFilter] = useState('all');
  // Sep 2026 — with the MMF fund type active the stock columns are all
  // blank by construction (a $1.00 NAV fund has no yield, payout,
  // Div/Yr or quality score), so the table swaps in the money-market
  // column set from /money-market-fund-yields and swaps back the moment
  // the filter clears.
  const [isCefFilter, setIsCefFilter] = useState('all');
  // Round P136 (Feb 2026) — Structurally-Leveraged CEF preset filter.
  // 'only' restricts to the curated set of ~96 25-45%-leverage CEFs.
  // See backend/data/leveraged_cefs.py for the full list.
  const [leveragedCefFilter, setLeveragedCefFilter] = useState('all');
  const [isReitFilter, setIsReitFilter] = useState('all');
  // Round P121 (Feb 2026) — SPAC / blank-check filter. Only meaningful
  // on the Non-Dividend screener since SPACs never pay dividends.
  const [isSpacFilter, setIsSpacFilter] = useState('all');
  // Round P157 — SPAC lifecycle-stage filter (only shown in All /
  // Non-Dividend modes; SPACs never appear in pure Dividend mode).
  const [spacStageFilter, setSpacStageFilter] = useState('all');
  const [dividendChangeFilter, setDividendChangeFilter] = useState('all');
  // Dividend Raise Streak filter — minimum consecutive-raise years.
  // String for select-binding; 0 = "Any streak". Reliable proxy for
  // "has this been consistently growing the dividend?"
  const [minStreakYears, setMinStreakYears] = useState('0');
  // Most Recent Raise % filter — minimum % on the latest hike.
  // Excludes 0% (no raise) and negative (cut). String for select-binding.
  const [minRecentRaisePct, setMinRecentRaisePct] = useState('0');
  // Upcoming ex-dividend date filter — surfaces stocks whose next
  // ex-div is within N days. Options: 0 (off), 7, 15, 30, 60, 95.
  // 95 covers a full quarterly cycle plus 5-day buffer so every
  // dividend-paying stock shows up at least once.
  const [nextExWithinDays, setNextExWithinDays] = useState(0);
  const [paysSpecialsFilter, setPaysSpecialsFilter] = useState('all');
  const [anomalyFilter, setAnomalyFilter] = useState('all');
  // Recently-paid filter — surfaces stocks whose LAST ex-div fell in the
  // past N days. Used by the "Recently Paid Div" Quick Screens preset
  // for spotting quality names you may have just missed buying. Mutually
  // exclusive with nextExWithinDays — backend only honours one at a time.
  const [recentlyPaidWithinDays, setRecentlyPaidWithinDays] = useState(0);
  // Read cef_strategy from URL on FIRST render so the very first
  // fetchScreener call already includes the filter — fixes a race
  // where the unfiltered initial fetch beat the deep-link useEffect.
  const [cefStrategyFilter, setCefStrategyFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all';
    const p = new URLSearchParams(window.location.search);
    return p.get('cef_strategy') || 'all';
  });
  const [cefStrategies, setCefStrategies] = useState([]);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  // Client-side post-process: minimum dividend quality score (0–100).
  // Applied AFTER the server returns the filtered/sorted page so it
  // works without any backend changes. Server-side support could come
  // later if performance demands it.
  const [minQuality, setMinQuality] = useState(0);
  // Highest quality_score on file — fetched once on mount so the slider's
  // upper bound matches reality and users never drag into the dead-zone
  // where no stocks return. Falls back to 100 until the fetch lands.
  const [qualityMax, setQualityMax] = useState(100);
  const [qualityScoredCount, setQualityScoredCount] = useState(null);
  // Quality bands ({threshold, count, label}) — used by the histogram
  // chip-row above the slider for one-click jump-to-threshold. Round
  // 98b (Jul 2026): now re-fetches whenever the 3-way switch changes
  // so the histogram + slider max match the CURRENT mode's universe
  // (dividend quality for Dividend, non-div quality for Non-Div, and
  // a badge-matching unified score for All).
  const [qualityBands, setQualityBands] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/screener/quality-stats`, {
          params: { dividend_filter: dividendFilter },
        });
        if (cancelled) return;
        const m = res.data?.max;
        if (typeof m === 'number' && m > 0) setQualityMax(m);
        if (typeof res.data?.count_scored === 'number') setQualityScoredCount(res.data.count_scored);
        if (Array.isArray(res.data?.bands)) setQualityBands(res.data.bands);
      } catch {
        // Non-fatal — slider stays capped at 100.
      }
    })();
    return () => { cancelled = true; };
  }, [dividendFilter]);

  // ───── Filter-state URL hydration (Copy Filter Link feature) ─────
  // Reads every known filter from the URL on first mount and applies
  // it via the matching setter. Only runs once. Pairs with the
  // "Copy filter link" button below the search bar — a user shares
  // their current Screener URL, the recipient lands here, and every
  // active filter pre-applies before the first user-facing fetch.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const setIfPresent = (key, setter) => {
      const v = p.get(key);
      if (v != null && v !== '') setter(v);
    };
    setIfPresent('tier', setTierFilter);
    setIfPresent('frequency', setFrequencyFilter);
    setIfPresent('yield_range', setYieldFilter);
    setIfPresent('payout_ratio', setPayoutRatioFilter);
    setIfPresent('sector', setSectorFilter);
    setIfPresent('market_cap', setMarketCapFilter);
    setIfPresent('pe_ratio', setPeRatioFilter);
    setIfPresent('beta', setBetaFilter);
    setIfPresent('debt_equity', setDebtEquityFilter);
    setIfPresent('roic', setRoicFilter);
    setIfPresent('fcf_margin', setFcfMarginFilter);
    setIfPresent('leveraged', setLeveragedFilter);
    setIfPresent('high_expense_ratio', setHighExpenseRatioFilter);
    setIfPresent('low_expense_ratio', setLowExpenseRatioFilter);
    setIfPresent('uses_futures', setUsesFuturesFilter);
    setIfPresent('is_adr', setIsAdrFilter);
    setIfPresent('is_bdc', setIsBdcFilter);
    setIfPresent('is_clo', setIsCloFilter);
    setIfPresent('is_money_market_fund', setIsMoneyMarketFundFilter);
    setIfPresent('is_preferred', setIsPreferredFilter);
    if (p.get('is_preferred') === 'only') setActiveFundType('preferred_shares');
    setIfPresent('is_cef', setIsCefFilter);
    setIfPresent('is_reit', setIsReitFilter);
    setIfPresent('is_spac', setIsSpacFilter);
    setIfPresent('dividend_change', setDividendChangeFilter);
    setIfPresent('min_streak_years', setMinStreakYears);
    setIfPresent('min_recent_raise_pct', setMinRecentRaisePct);
    if (['yes', 'habitual'].includes(p.get('pays_specials'))) setPaysSpecialsFilter(p.get('pays_specials'));
    if (p.get('anomalies') === 'exclude3') setAnomalyFilter('exclude3');
    const nx = p.get('next_ex_within_days');
    if (nx) setNextExWithinDays(Math.max(0, Number(nx) || 0));
    const rp = p.get('recently_paid_within_days');
    if (rp) setRecentlyPaidWithinDays(Math.max(0, Number(rp) || 0));
    const mq = p.get('min_quality');
    if (mq) setMinQuality(Math.max(0, Math.min(100, Number(mq) || 0)));
    const sk = p.get('sort_by');
    if (sk) setSortKey(sk);
    const sd = p.get('sort_dir');
    if (sd === 'asc' || sd === 'desc') setSortDir(sd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [activePreset, setActivePreset] = useState('none');
  // Round 96ac (Jul 2026) — Fund Type category has its own independent
  // preset slot so a user can pair e.g. "REITs" (fund type) with
  // "Blue Chip Income" (strategy) simultaneously without one overriding
  // the other. Both slots key off the shared SCREENER_PRESETS list,
  // just filtered by group.
  const [activeFundType, setActiveFundType] = useState('none');
  const [tooltip, setTooltip] = useState({ tip: null, anchorRect: null });
  const tooltipTimer = useRef(null);
  // Mobile filter rail — track scroll position so we can fade in/out
  // the directional arrow affordances. Only meaningful below the lg
  // breakpoint where the rail becomes horizontal-scroll. The rail is
  // remeasured on resize so arrow visibility stays accurate when the
  // user rotates a tablet.
  const filterRailRef = useRef(null);
  const [railScroll, setRailScroll] = useState({ canLeft: false, canRight: false });
  useEffect(() => {
    const el = filterRailRef.current;
    if (!el) return;
    const update = () => {
      const canLeft = el.scrollLeft > 4;
      // 4px slop on the right too — accounts for sub-pixel rendering
      // so the chevron doesn't linger after a fully-scrolled rail.
      const canRight = (el.scrollLeft + el.clientWidth) < (el.scrollWidth - 4);
      setRailScroll((prev) =>
        prev.canLeft === canLeft && prev.canRight === canRight ? prev : { canLeft, canRight }
      );
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  const scrollRail = (dir) => {
    const el = filterRailRef.current;
    if (!el) return;
    // One "screen" worth = 80% of visible width — feels natural on phones.
    const delta = Math.round(el.clientWidth * 0.8) * (dir === 'left' ? -1 : 1);
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  // Anchor the tooltip to the triggering element's bounding rect (not the
  // mouse cursor). This keeps it stable on touch screens and lets the
  // ScreenerTooltip component handle viewport clamping cleanly. The
  // second arg may be a PointerEvent (hover/click) or a raw rect.
  const showTip = useCallback((tip, e, opts = {}) => {
    clearTimeout(tooltipTimer.current);
    const el = e?.currentTarget?.closest?.('[data-testid$="-trigger"], [role="combobox"], [data-tip-anchor]') || e?.currentTarget;
    const rect = el?.getBoundingClientRect?.();
    if (!rect) return;
    setTooltip({ tip, anchorRect: rect });
    // Auto-hide on touch so the bubble doesn't linger forever after a tap.
    // Desktop hover uses onMouseLeave to clear it — no timer needed there.
    if (opts.autoHide) {
      tooltipTimer.current = setTimeout(() => setTooltip({ tip: null, anchorRect: null }), 4500);
    }
  }, []);
  const hideTip = useCallback(() => {
    clearTimeout(tooltipTimer.current);
    setTooltip({ tip: null, anchorRect: null });
  }, []);

  // Hide tooltip when any filter changes (dropdown item clicked = dropdown closes = tooltip orphaned)
  useEffect(() => { hideTip(); }, [tierFilter, frequencyFilter, yieldFilter, payoutRatioFilter, sectorFilter, marketCapFilter, peRatioFilter, betaFilter, debtEquityFilter, roicFilter, fcfMarginFilter, activePreset, activeFundType, hideTip]);

  // Round P144 (Feb 2026) — Auto-sync the "Allow SPAC & Warrant"
  // checkbox with the active fund-type filter. Picking `spac_stocks`
  // or `warrants` auto-checks the box (the user obviously wants to
  // see that slice), and clearing that filter with the red X or
  // switching Fund Type back to `none` auto-unchecks it. Users who
  // manually tick the box get their choice preserved as long as
  // the SPAC/Warrant screener isn't in the picture. The reason we
  // watch `isSpacFilter` too is that other presets sometimes set
  // `is_spac: only` directly without switching `activeFundType`.
  useEffect(() => {
    const spacOrWarrantActive = (
      activeFundType === 'spac_stocks'
      || activeFundType === 'warrants'
      || isSpacFilter === 'only'
    );
    setAllowSpacsWarrants(spacOrWarrantActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFundType, isSpacFilter]);

  // Dismiss the tooltip on any tap outside a filter trigger — prevents
  // "orphaned" bubbles stuck on screen after the user scrolls past.
  useEffect(() => {
    const onDocPointer = (ev) => {
      const target = ev.target;
      if (target && target.closest && target.closest('[data-testid$="-trigger"], [data-tip-anchor], th, [role="columnheader"]')) return;
      hideTip();
    };
    document.addEventListener('touchstart', onDocPointer, { passive: true });
    return () => document.removeEventListener('touchstart', onDocPointer);
  }, [hideTip]);

  // Helper — returns the full set of handlers to show a tooltip for a
  // filter trigger. Handles hover (desktop) + tap (mobile) uniformly.
  // `options` = the OPTION array; `currentValue` = filter state. Picks
  // the specific option's tip or falls back to the 'all' general tip.
  const tipHandlers = useCallback((options, currentValue) => {
    const tipFor = (val) => {
      const picked = options.find((x) => x.value === val);
      if (picked?.tip) return picked.tip;
      // Fallback to the 'all' option's general tip (our new top-value tip).
      return options.find((x) => x.value === 'all' || x.value === 'none')?.tip || null;
    };
    return {
      onMouseEnter: (e) => { const t = tipFor(currentValue); if (t) showTip(t, e); },
      onMouseLeave: hideTip,
      onTouchStart: (e) => { const t = tipFor(currentValue); if (t) showTip(t, e, { autoHide: true }); },
    };
  }, [showTip, hideTip]);

  // Shared tip-handler for individual SelectItem rows so both the label
  // on hover/tap and a dropdown row hover-through work.
  const itemTipHandlers = useCallback((tip) => ({
    onMouseEnter: (e) => tip && showTip(tip, e),
    onMouseLeave: hideTip,
    // Selecting an item unmounts it before mouseleave can fire → the tip
    // would stick on screen. Hide on the selecting pointer-up as well.
    onPointerUp: hideTip,
    onTouchStart: (e) => tip && showTip(tip, e, { autoHide: true }),
  }), [showTip, hideTip]);

  // Safety net: any click or scroll anywhere dismisses a lingering tip.
  useEffect(() => {
    const off = () => hideTip();
    window.addEventListener('pointerdown', off, true);
    window.addEventListener('scroll', off, true);
    return () => {
      window.removeEventListener('pointerdown', off, true);
      window.removeEventListener('scroll', off, true);
    };
  }, [hideTip]);

  // Plain-English descriptions for the sortable column headers on the
  // Screener results table. Clicking still sorts; hover/tap just explains
  // what the column is.
  const HEADER_TIPS = {
    price: 'Current last-trade price. Refreshes when you open a stock, add it to your portfolio or watchlist, or sign in with existing holdings — not live-ticking while browsing.',
    yield: 'Dividend Yield = annual dividend ÷ current price. Higher = more immediate income (and often more risk). 2–5% is the typical "healthy grower" band.',
    divyr: 'Annual Dividend — the total dollars per share paid out over a year. (Monthly × 12, Quarterly × 4, etc.)',
    payout: 'Payout Ratio — what % of earnings are paid out as dividends. Under 60% is comfortable; over 100% signals a potential dividend cut (REITs/BDCs exempt).',
    perf_1m: 'Price change over the last 30 days. Capital appreciation only — dividends not included.',
    perf_1y: 'Price change over the last 12 months. Capital appreciation only — dividends not included.',
  };
  const headerTip = useCallback((text) => ({
    onMouseEnter: (e) => showTip(text, e),
    onMouseLeave: hideTip,
    onTouchStart: (e) => showTip(text, e, { autoHide: true }),
  }), [showTip, hideTip]);

  // Modal
  const [selectedStock, setSelectedStock] = useState(null);
  // Round 97l (Feb 2026) — Initial-tab override for callers that want
  // to deep-link directly into a specific Stock Modal tab (e.g. the
  // Founder Stake Detector badge jumps straight to insider-activity).
  // Resets to the default tab on every modal close.
  const [pendingInitialTab, setPendingInitialTab] = useState(null);
  const [pendingInitialChartPeriod, setPendingInitialChartPeriod] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  // Round 95bu (Jun 2026) — inline Compare on the Screener.
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [prefetchedNews, setPrefetchedNews] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [ownedTickers, setOwnedTickers] = useState([]);

  const activeFilters = [
    activePreset !== 'none' ? { cat: 'Strategy', label: SCREENER_PRESETS.find(p => p.value === activePreset)?.label || activePreset, onRemove: () => applyPreset('none'), restore: () => applyPreset(activePreset) } : null,
    activeFundType !== 'none' ? { cat: 'Fund Type', label: SCREENER_PRESETS.find(p => p.value === activeFundType)?.label || activeFundType, onRemove: () => applyFundType('none'), restore: () => applyFundType(activeFundType) } : null,
    tierFilter !== 'all' ? { cat: 'Tier', label: TIER_OPTIONS.find(o => o.value === tierFilter)?.label || tierFilter, onRemove: () => setTierFilter('all'), restore: () => setTierFilter(tierFilter) } : null,
    frequencyFilter !== 'all' ? { cat: 'Frequency', label: frequencyFilter, onRemove: () => setFrequencyFilter('all'), restore: () => setFrequencyFilter(frequencyFilter) } : null,
    yieldFilter !== 'all' ? { cat: 'Yield', label: YIELD_OPTIONS.find(o => o.value === yieldFilter)?.label || yieldFilter, onRemove: () => setYieldFilter('all'), restore: () => setYieldFilter(yieldFilter) } : null,
    payoutRatioFilter !== 'all' ? { cat: 'Payout Ratio', label: PAYOUT_RATIO_OPTIONS.find(o => o.value === payoutRatioFilter)?.label || payoutRatioFilter, onRemove: () => setPayoutRatioFilter('all'), restore: () => setPayoutRatioFilter(payoutRatioFilter) } : null,
    sectorFilter !== 'all' ? { cat: 'Sector', label: SECTOR_OPTIONS.find(o => o.value === sectorFilter)?.label || sectorFilter, onRemove: () => setSectorFilter('all'), restore: () => setSectorFilter(sectorFilter) } : null,
    industryFilter !== 'all' ? { cat: 'Industry', label: industryFilter, onRemove: () => setIndustryFilter('all'), restore: () => setIndustryFilter(industryFilter) } : null,
    marketCapFilter !== 'all' ? { cat: 'Market Cap', label: MARKET_CAP_OPTIONS.find(o => o.value === marketCapFilter)?.label || marketCapFilter, onRemove: () => setMarketCapFilter('all'), restore: () => setMarketCapFilter(marketCapFilter) } : null,
    peRatioFilter !== 'all' ? { cat: 'P/E', label: PE_RATIO_OPTIONS.find(o => o.value === peRatioFilter)?.label || peRatioFilter, onRemove: () => setPeRatioFilter('all'), restore: () => setPeRatioFilter(peRatioFilter) } : null,
    betaFilter !== 'all' ? { cat: 'Beta', label: BETA_OPTIONS.find(o => o.value === betaFilter)?.label || betaFilter, onRemove: () => setBetaFilter('all'), restore: () => setBetaFilter(betaFilter) } : null,
    debtEquityFilter !== 'all' ? { cat: 'D/E', label: DEBT_EQUITY_OPTIONS.find(o => o.value === debtEquityFilter)?.label || debtEquityFilter, onRemove: () => setDebtEquityFilter('all'), restore: () => setDebtEquityFilter(debtEquityFilter) } : null,
    roicFilter !== 'all' ? { cat: 'ROIC', label: ROIC_OPTIONS.find(o => o.value === roicFilter)?.label || roicFilter, onRemove: () => setRoicFilter('all'), restore: () => setRoicFilter(roicFilter) } : null,
    fcfMarginFilter !== 'all' ? { cat: 'FCF Margin', label: FCF_MARGIN_OPTIONS.find(o => o.value === fcfMarginFilter)?.label || fcfMarginFilter, onRemove: () => setFcfMarginFilter('all'), restore: () => setFcfMarginFilter(fcfMarginFilter) } : null,
    leveragedFilter !== 'all' ? { cat: 'Leveraged', label: leveragedFilter === 'only' ? 'Only Leveraged' : 'Hide Leveraged', onRemove: () => setLeveragedFilter('all'), restore: () => setLeveragedFilter(leveragedFilter) } : null,
    highExpenseRatioFilter !== 'all' ? { cat: 'Expense Ratio', label: highExpenseRatioFilter === 'only' ? 'Only High Expense (>0.50%)' : 'Hide High Expense', onRemove: () => setHighExpenseRatioFilter('all'), restore: () => setHighExpenseRatioFilter(highExpenseRatioFilter) } : null,
    lowExpenseRatioFilter !== 'all' ? { cat: 'Expense Ratio', label: lowExpenseRatioFilter === 'only' ? 'Only Low Expense (≤0.10%)' : 'Hide Low Expense', onRemove: () => setLowExpenseRatioFilter('all'), restore: () => setLowExpenseRatioFilter(lowExpenseRatioFilter) } : null,
    usesFuturesFilter !== 'all' ? { cat: 'Futures', label: usesFuturesFilter === 'only' ? 'Only Futures-Based' : 'Hide Futures-Based', onRemove: () => setUsesFuturesFilter('all'), restore: () => setUsesFuturesFilter(usesFuturesFilter) } : null,
    isAdrFilter !== 'all' ? (() => {
      const pairedFundType = activeFundType === 'adr_stocks' ? activeFundType : null;
      return {
        cat: 'ADR', label: isAdrFilter === 'only' ? 'Only ADRs' : 'Hide ADRs',
        onRemove: () => { setIsAdrFilter('all'); if (pairedFundType) setActiveFundType('none'); },
        restore: () => { setIsAdrFilter(isAdrFilter); if (pairedFundType) setActiveFundType(pairedFundType); },
      };
    })() : null,
    isCloFilter !== 'all' ? (() => {
      // Removing the CLO chip also clears the paired Fund Type preset
      // (and the undo toast restores both) so the two never disagree.
      const pairedFundType = activeFundType === 'clo_etfs' ? activeFundType : null;
      return {
        cat: 'CLO', label: isCloFilter === 'only' ? 'Only CLO ETFs' : 'Hide CLO ETFs',
        onRemove: () => { setIsCloFilter('all'); if (pairedFundType) setActiveFundType('none'); },
        restore: () => { setIsCloFilter(isCloFilter); if (pairedFundType) setActiveFundType(pairedFundType); },
      };
    })() : null,
    isBdcFilter !== 'all' ? (() => {
      // Sep 2026 — Same pairing pattern the CLO chip uses (above): if
      // the "BDCs" Fund Type preset is what set is_bdc=only, clicking
      // X on EITHER chip needs to clear BOTH so the two never
      // disagree. Restore path re-arms them together.
      const pairedFundType = activeFundType === 'bdc_stocks' ? activeFundType : null;
      return {
        cat: 'BDC', label: isBdcFilter === 'only' ? 'Only BDCs' : 'Hide BDCs',
        onRemove: () => { setIsBdcFilter('all'); if (pairedFundType) setActiveFundType('none'); },
        restore: () => { setIsBdcFilter(isBdcFilter); if (pairedFundType) setActiveFundType(pairedFundType); },
      };
    })() : null,
    isPreferredFilter !== 'all' ? (() => {
      const pairedFundType = activeFundType === 'preferred_shares' ? activeFundType : null;
      return {
        cat: 'Preferred', label: isPreferredFilter === 'only' ? 'Only Preferred Shares' : 'Hide Preferred Shares',
        onRemove: () => { setIsPreferredFilter('all'); if (pairedFundType) setActiveFundType('none'); },
        restore: () => { setIsPreferredFilter(isPreferredFilter); if (pairedFundType) setActiveFundType(pairedFundType); },
      };
    })() : null,
    isCefFilter !== 'all' ? (() => {
      const pairedFundType = activeFundType === 'cef_stocks' ? activeFundType : null;
      return {
        cat: 'CEF', label: isCefFilter === 'only' ? 'Only CEFs' : 'Hide CEFs',
        onRemove: () => { setIsCefFilter('all'); if (pairedFundType) setActiveFundType('none'); },
        restore: () => { setIsCefFilter(isCefFilter); if (pairedFundType) setActiveFundType(pairedFundType); },
      };
    })() : null,
    leveragedCefFilter !== 'all' ? { cat: 'CEF', label: 'Structurally Leveraged ⚡', onRemove: () => setLeveragedCefFilter('all'), restore: () => setLeveragedCefFilter(leveragedCefFilter) } : null,
    isReitFilter !== 'all' ? (() => {
      const pairedFundType = activeFundType === 'reit_stocks' ? activeFundType : null;
      return {
        cat: 'REIT', label: isReitFilter === 'only' ? 'Only REITs' : 'Hide REITs',
        onRemove: () => { setIsReitFilter('all'); if (pairedFundType) setActiveFundType('none'); },
        restore: () => { setIsReitFilter(isReitFilter); if (pairedFundType) setActiveFundType(pairedFundType); },
      };
    })() : null,
    cefStrategyFilter !== 'all' ? { cat: 'CEF Strategy', label: cefStrategyFilter.replace(/^Morningstar US CEF /, ''), onRemove: () => setCefStrategyFilter('all'), restore: () => setCefStrategyFilter(cefStrategyFilter) } : null,
    dividendChangeFilter !== 'all' ? {
      cat: 'Div Change',
      label: dividendChangeFilter === 'raised'
        ? 'Recently Raised (≥10%)'
        : dividendChangeFilter === 'cut'
        ? 'Recently Cut (≥10%)'
        : 'Recent Raise or Cut',
      onRemove: () => setDividendChangeFilter('all'),
      restore: () => setDividendChangeFilter(dividendChangeFilter),
    } : null,
    Number(minStreakYears) > 0 ? {
      cat: 'Raise Streak',
      label: `≥ ${minStreakYears} yr${Number(minStreakYears) === 1 ? '' : 's'} of raises`,
      onRemove: () => setMinStreakYears('0'),
      restore: () => setMinStreakYears(minStreakYears),
    } : null,
    Number(minRecentRaisePct) > 0 ? {
      cat: 'Recent Raise',
      label: `≥ ${minRecentRaisePct}% on last raise`,
      onRemove: () => setMinRecentRaisePct('0'),
      restore: () => setMinRecentRaisePct(minRecentRaisePct),
    } : null,
    anomalyFilter !== 'all' ? { cat: 'Anomalies', label: '0–2 accounting anomaly items (scored stocks only)', onRemove: () => setAnomalyFilter('all'), restore: () => setAnomalyFilter('exclude3') } : null,
    paysSpecialsFilter !== 'all' ? { cat: 'Pays Specials', label: paysSpecialsFilter === 'habitual' ? 'Habitual: 10+ special dividends' : '3+ special dividends', onRemove: () => setPaysSpecialsFilter('all'), restore: ((v) => () => setPaysSpecialsFilter(v))(paysSpecialsFilter) } : null,
    nextExWithinDays > 0 ? { cat: 'Upcoming Ex-Div', label: `Within ${nextExWithinDays} days`, onRemove: () => setNextExWithinDays(0), restore: () => setNextExWithinDays(nextExWithinDays) } : null,
    recentlyPaidWithinDays > 0 ? { cat: 'Recently Paid Div', label: `Last ${recentlyPaidWithinDays} days`, onRemove: () => setRecentlyPaidWithinDays(0), restore: () => setRecentlyPaidWithinDays(recentlyPaidWithinDays) } : null,
    // Round 98f — Growth category chips.
    minRuleOf40Fcf > 0 ? { cat: 'Rule of 40 FCF', label: `≥ ${minRuleOf40Fcf}%`, onRemove: () => setMinRuleOf40Fcf(0), restore: () => setMinRuleOf40Fcf(minRuleOf40Fcf) } : null,
    minRuleOf40Op > 0 ? { cat: 'Rule of 40 Op', label: `≥ ${minRuleOf40Op}%`, onRemove: () => setMinRuleOf40Op(0), restore: () => setMinRuleOf40Op(minRuleOf40Op) } : null,
    minRuleOf40Sbc > 0 ? { cat: 'Rule of 40 SBC', label: `≥ ${minRuleOf40Sbc}%`, onRemove: () => setMinRuleOf40Sbc(0), restore: () => setMinRuleOf40Sbc(minRuleOf40Sbc) } : null,
    minRevCagr3y > 0 ? { cat: 'Rev CAGR 3y', label: `≥ ${Math.round(minRevCagr3y * 100)}%`, onRemove: () => setMinRevCagr3y(0), restore: () => setMinRevCagr3y(minRevCagr3y) } : null,
    // Round 98h — Full preset chips.
    minPerf1y > -9000 ? { cat: '1Y Return', label: `≥ ${minPerf1y}%`, onRemove: () => setMinPerf1y(-9999), restore: () => setMinPerf1y(minPerf1y) } : null,
    minFounderTopPct > 0 ? { cat: 'Founder Min %', label: `≥ ${Math.round(minFounderTopPct * 100)}% of float`, onRemove: () => setMinFounderTopPct(0), restore: () => setMinFounderTopPct(minFounderTopPct) } : null,
    // Round 98f — Forensics & Ownership category chips.
    altmanZoneFilter !== 'all' ? { cat: 'Altman Zone', label: altmanZoneFilter === 'safe' ? 'SAFE' : altmanZoneFilter === 'gray' ? 'GRAY' : 'DISTRESS', onRemove: () => setAltmanZoneFilter('all'), restore: () => setAltmanZoneFilter(altmanZoneFilter) } : null,
    forensicGreen ? { cat: 'Forensic Model', label: 'Clean (no penalty)', onRemove: () => setForensicGreen(false), restore: () => setForensicGreen(true) } : null,
    founderLedFilter ? { cat: 'Founder-Led', label: 'Only Founder-Detected', onRemove: () => setFounderLedFilter(false), restore: () => setFounderLedFilter(true) } : null,
  ].filter(Boolean);

  // Sep 2026 — the SPAC Stage / MMF Type dropdowns are a slice picker,
  // not a filter. When any screener filter is active they'd AND into an
  // empty result (and the bar that hosts them is hidden by the empty
  // state, so the user can't see what's stuck). Reset them to Any the
  // moment a screener filter is set and grey them out while it stays.
  const screenerFilterActive = activeFilters.length > 0;
  // Sep 2026 — accounting anomalies are only scored for operating companies;
  // SPACs, warrants, money market and mutual funds have no statements to score,
  // so the "Allow" toggles are greyed while the anomaly filter is on.
  const anomalyLocksFunds = anomalyFilter !== 'all';
  const effectiveSpacStage = screenerFilterActive ? 'all' : spacStageFilter;
  const effectiveMmfType = screenerFilterActive ? 'all' : mmfTypeFilter;
  useEffect(() => {
    if (!screenerFilterActive) return;
    if (spacStageFilter !== 'all') setSpacStageFilter('all');
    if (mmfTypeFilter !== 'all') setMmfTypeFilter('all');
  }, [screenerFilterActive, spacStageFilter, mmfTypeFilter]);
  // Sep 2026 — the MMF header set (NAV · Family · Type · 7-Day Yield ·
  // Expense · Fund Size · Weekly Liquid) also kicks in when an MMF Type
  // is picked from the dropdown, not only via the Fund Type filter.
  const mmfView = isMoneyMarketFundFilter === 'only' || effectiveMmfType !== 'all';
  // Oct 2026 — Preferred Shares column set (Par · Coupon · vs Par ·
  // Call Date · Type). `altView` = any column-swapping fund type.
  const prefView = isPreferredFilter === 'only';
  const altView = mmfView || prefView;

  // Sep 2026 — BDC preset column set. Fires when the user picks
  // "Only BDCs" under Fund Types. Adds a NAV Growth column so
  // BDCs can be ranked by loan-book health (5-quarter change in
  // NAV per share, computed weekly by the bdc_nav_growth_backfill
  // cron). Mirrors the `mmfView` pattern.
  const bdcView = isBdcFilter === 'only';

  // Wrap a filter entry's onRemove with a sonner Undo toast. Used by
  // the Icon Key popover when a user taps a vibrant (active) icon to
  // clear it. The pill × buttons elsewhere stay synchronous (no toast)
  // because those buttons are visually "the X means delete" — adding
  // a toast there would create double-feedback.
  const clearFilterWithUndo = (entry) => {
    if (!entry?.onRemove || !entry?.restore) return;
    entry.onRemove();
    toast(`Cleared ${entry.cat}`, {
      description: entry.label,
      duration: 5000,
      action: {
        label: 'Undo',
        onClick: () => entry.restore(),
      },
    });
  };

  const screenerFetchSeq = useRef(0);
  const fetchScreener = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: 25,
        sort_by: sortKey,
        sort_dir: sortDir,
      };
      if (tierFilter !== 'all') params.tier = tierFilter;
      if (frequencyFilter !== 'all') params.frequency = frequencyFilter;
      if (yieldFilter !== 'all') params.yield_range = yieldFilter;
      if (payoutRatioFilter !== 'all') params.payout_ratio = payoutRatioFilter;
      if (sectorFilter !== 'all') params.sector = sectorFilter;
      // Round 98a — Industry filter param (exact match). Backend
      // ignores the value when it's the "all" sentinel.
      if (industryFilter !== 'all') params.industry = industryFilter;
      if (marketCapFilter !== 'all') params.market_cap = marketCapFilter;
      if (peRatioFilter !== 'all') params.pe_ratio = peRatioFilter;
      if (betaFilter !== 'all') params.beta = betaFilter;
      if (debtEquityFilter !== 'all') params.debt_equity = debtEquityFilter;
      if (roicFilter !== 'all') params.roic = roicFilter;
      if (fcfMarginFilter !== 'all') params.fcf_margin = fcfMarginFilter;
      if (leveragedFilter !== 'all') params.leveraged = leveragedFilter;
      if (highExpenseRatioFilter !== 'all') params.high_expense_ratio = highExpenseRatioFilter;
      if (lowExpenseRatioFilter !== 'all') params.low_expense_ratio = lowExpenseRatioFilter;
      if (usesFuturesFilter !== 'all') params.uses_futures = usesFuturesFilter;
      if (isAdrFilter !== 'all') params.is_adr = isAdrFilter;
      if (isBdcFilter !== 'all') params.is_bdc = isBdcFilter;
      if (isCloFilter !== 'all') params.is_clo = isCloFilter;
      if (isMoneyMarketFundFilter !== 'all') params.is_money_market_fund = isMoneyMarketFundFilter;
      if (isPreferredFilter !== 'all') params.is_preferred = isPreferredFilter;
      if (isCefFilter !== 'all') params.is_cef = isCefFilter;
      if (leveragedCefFilter !== 'all') params.leveraged_cef = leveragedCefFilter;
      if (isReitFilter !== 'all') params.is_reit = isReitFilter;
      if (isSpacFilter !== 'all') params.is_spac = isSpacFilter;
      if (effectiveSpacStage !== 'all') params.spac_stage = effectiveSpacStage;
      if (effectiveMmfType !== 'all') params.mmf_type = effectiveMmfType;
      // Sep 2026 — hide MMFs unless the checkbox is on or an MMF-only
      // filter is already asking for them.
      if (!allowMmf && isMoneyMarketFundFilter !== 'only' && effectiveMmfType === 'all') params.hide_mmf = true;
      if (cefStrategyFilter !== 'all') params.cef_strategy = cefStrategyFilter;
      if (dividendChangeFilter !== 'all') params.dividend_change = dividendChangeFilter;
      if (Number(minStreakYears) > 0) params.min_streak_years = minStreakYears;
      if (Number(minRecentRaisePct) > 0) params.min_recent_raise_pct = minRecentRaisePct;
      if (nextExWithinDays > 0) params.next_ex_within_days = nextExWithinDays;
      if (paysSpecialsFilter !== 'all') params.pays_specials = paysSpecialsFilter;
      if (anomalyFilter !== 'all') params.anomalies = anomalyFilter;
      if (recentlyPaidWithinDays > 0) params.recently_paid_within_days = recentlyPaidWithinDays;
      if (searchQuery.trim()) params.q = searchQuery.trim();
      // Server-side Min Quality filter — backed by the cached
      // `quality_score` field. Passing 0 omits the filter entirely
      // so users who never touch the slider see the full universe.
      if (minQuality > 0) params.min_quality = minQuality;
      // Round 98c — Rule of 40 preset filters (Non-Div Screener).
      if (minRuleOf40Fcf > 0) params.min_rule_of_40_fcf = minRuleOf40Fcf;
      if (minRuleOf40Op > 0) params.min_rule_of_40_op = minRuleOf40Op;
      if (minRuleOf40Sbc > 0) params.min_rule_of_40_sbc = minRuleOf40Sbc;
      // Round 98d — Micro-Cap Giants Lite + Forensic-Green
      // Compounders Lite preset primitives. Zero / false = omit.
      if (marketCapMin > 0) params.market_cap_min = marketCapMin;
      if (marketCapMax > 0) params.market_cap_max = marketCapMax;
      if (maxBeneishM !== 0) params.max_beneish_m = maxBeneishM;
      if (minAltmanZ > 0) params.min_altman_z = minAltmanZ;
      if (minRevCagr3y > 0) params.min_rev_cagr_3y = minRevCagr3y;
      if (forensicGreen) params.forensic_green = true;
      if (minerviniSepa) { params.minervini_sepa = true; if (sortKey === 'name') { params.sort_by = 'minervini_rs'; params.sort_dir = 'desc'; } }
      // Round 98f — categorical Growth + Forensics category filters.
      if (altmanZoneFilter && altmanZoneFilter !== 'all') params.altman_zone_filter = altmanZoneFilter;
      if (founderLedFilter) params.founder_led = true;
      // Round 98h — Full preset primitives.
      if (minFounderTopPct > 0) params.min_founder_top_pct = minFounderTopPct;
      if (minPerf1y > -9000) params.min_perf_1y = minPerf1y;
      // Jul 2026 — Coverage Watch preset primitives.
      if (maxCoverageRatio > 0) params.max_coverage_ratio = maxCoverageRatio;
      if (maxCoverageTrend > -9000) params.max_coverage_trend = maxCoverageTrend;
      if (minCoverageRatio > 0) params.min_coverage_ratio = minCoverageRatio;
      if (minCoverageTrend > -9000) params.min_coverage_trend = minCoverageTrend;
      // Round 97a — Non-Dividend Expansion 3-way switch. Non-admin
      // requests silently server-side downgrade to `dividend`, so
      // sending this param is safe regardless of user role.
      if (dividendFilter && dividendFilter !== 'dividend') {
        params.dividend_filter = dividendFilter;
      }
      // Round P144 (Feb 2026) — "Allow SPAC & Warrant" checkbox. Off
      // by default: pass `hide_spacs_warrants=true` unless the user
      // has explicitly ticked it OR the active fund-type filter is
      // SPACs-only / Warrants (the backend guards against double-
      // filtering when `is_spac`/`is_warrant` is already `only`).
      if (!allowSpacsWarrants) params.hide_spacs_warrants = true;

      const mySeq = ++screenerFetchSeq.current;
      const { data } = await axios.get(`${API}/stocks/screener`, { params, withCredentials: true });
      // Sep 2026 — drop stale responses: a slower earlier request (e.g. the
      // unfiltered boot fetch) must never overwrite a newer filtered result.
      if (mySeq !== screenerFetchSeq.current) return;
      setStocks(data.stocks || []);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total || 0);
      setFreqBreakdown(data.breakdown_by_frequency || null);
    } catch (err) {
      console.error('Screener fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, tierFilter, frequencyFilter, yieldFilter, payoutRatioFilter, sectorFilter, industryFilter, marketCapFilter, peRatioFilter, betaFilter, debtEquityFilter, roicFilter, fcfMarginFilter, leveragedFilter, highExpenseRatioFilter, lowExpenseRatioFilter, usesFuturesFilter, isAdrFilter, isBdcFilter, isCloFilter, isMoneyMarketFundFilter, isPreferredFilter, isCefFilter, leveragedCefFilter, isReitFilter, isSpacFilter, spacStageFilter, cefStrategyFilter, dividendChangeFilter, minStreakYears, minRecentRaisePct, nextExWithinDays, paysSpecialsFilter, anomalyFilter, recentlyPaidWithinDays, sortKey, sortDir, searchQuery, minQuality, dividendFilter, minRuleOf40Fcf, minRuleOf40Op, minRuleOf40Sbc, marketCapMin, marketCapMax, maxBeneishM, minAltmanZ, minRevCagr3y, forensicGreen, minerviniSepa, altmanZoneFilter, founderLedFilter, minFounderTopPct, minPerf1y, maxCoverageRatio, maxCoverageTrend, minCoverageRatio, minCoverageTrend, allowSpacsWarrants, allowMmf, mmfTypeFilter]);

  // Round 98i.1 (Jul 2026) — Skip-first-fetch guard for URL preset
  // deep-links. When the user lands from `/founder-led?preset=...` we
  // don't want the initial fetchScreener to fire with default state
  // (which would race with the preset-triggered refetch and sometimes
  // WIN, leaving the user staring at 4,464 stocks even though the
  // preset UI says otherwise). The ref is initialized synchronously
  // at mount, so by the time React runs any useEffect it already
  // knows whether to skip. useRef ensures the value survives re-renders.
  const skipFirstScreenerFetch = useRef((() => {
    try {
      const p = new URLSearchParams(window.location.search).get('preset');
      return !!(p && SCREENER_PRESETS.some(x => x.value === p && x.value !== 'none'));
    } catch { return false; }
  })());

  useEffect(() => {
    if (skipFirstScreenerFetch.current) {
      // First render only — consume the flag and skip. The
      // URL-preset useEffect (below) will run in the same tick,
      // batch-update all filter states, then this effect will
      // re-fire due to deps changing and hit the real fetch path.
      skipFirstScreenerFetch.current = false;
      return;
    }
    fetchScreener();
  }, [fetchScreener]);

  // Round 98a (Jul 2026) — Fetch the industry list for the current
  // 3-way mode so the Industry Popover typeahead has an up-to-date
  // set of options with per-industry counts. Re-runs on mode switch
  // so a user flipping from Non-Div → Dividend sees the correct
  // dividend industries (which are a somewhat different distribution).
  // Also resets the current selection if it no longer exists in the
  // new mode's set (e.g. "BLANK CHECKS" only exists in non-div).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/screener/industries`, {
          params: { dividend_filter: dividendFilter },
          withCredentials: true,
        });
        if (cancelled) return;
        const list = resp.data?.industries || [];
        setIndustryList(list);
        setIndustryTopExample(resp.data?.top || null);
        // Auto-reset if the current selection isn't in the new set.
        if (industryFilter !== 'all' && !list.some((x) => x.name === industryFilter)) {
          setIndustryFilter('all');
        }
      } catch {
        if (cancelled) return;
        setIndustryList([]);
        setIndustryTopExample(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dividendFilter]);

  // Round 97a (Jul 4 2026) — Keep the ?filter= query param in sync with
  // the segmented control state so a user can copy the URL and land
  // their friend on the same view. `dividend` is the default and gets
  // stripped from the URL to keep the canonical shareable URL clean.
  useEffect(() => {
    try {
      const p = new URLSearchParams(location.search);
      if (dividendFilter === 'dividend') p.delete('filter');
      else p.set('filter', dividendFilter);
      const next = p.toString();
      const target = next ? `${location.pathname}?${next}` : location.pathname;
      if (target !== location.pathname + location.search) {
        navigate(target, { replace: true });
      }
    } catch { /* no-op */ }
  }, [dividendFilter, location.pathname, location.search, navigate]);

  // Round 99 (Feb 2026, user-directed) — When flipping the 3-way
  // Dividend / All / Non-Dividend switch, DO NOT wipe any active
  // filters. Per user rule, filters (the chips with an X in front)
  // can only be cleared by clicking the individual chip's X or the
  // "Clear All Filters" button. Switching mode may legitimately
  // return zero matches — that's expected UX, not a reason to nuke
  // the user's carefully-built filter stack. Left intentionally
  // as a no-op so the mode toggle is purely a query-scope change.

  // Round 97a — Once auth resolves, if a non-admin landed on the page
  // with a paywalled `?filter=all` or `?filter=non_dividend` deep link,
  // silently downgrade the client state to `dividend` so the UI
  // matches what the backend is going to serve. Runs only after `user`
  // becomes non-null (auth confirmed loaded), otherwise a first-render
  // admin would get downgraded before their role is known.
  useEffect(() => {
    if (user === undefined || user === null) return;
    if ((user.role || '') === 'admin') return;
    if (dividendFilter === 'all' || dividendFilter === 'non_dividend') {
      setDividendFilter('dividend');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch watchlist
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/portfolio`, { withCredentials: true })
      .then(res => setWatchlist((res.data || []).map(p => p.ticker)))
      .catch(() => {});
    axios.get(`${API}/portfolio/owned-tickers`, { withCredentials: true })
      .then(res => setOwnedTickers(res.data || []))
      .catch(() => {});
  }, [user]);

  // Load CEF strategies once on mount — used to populate the
  // "CEF Strategy" filter dropdown.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/cef-strategies`)
      .then((res) => {
        if (cancelled) return;
        setCefStrategies(res.data?.strategies || []);
      })
      .catch(() => {
        if (cancelled) return;
        setCefStrategies([]);
      });
    return () => { cancelled = true; };
  }, []);

  // Deep-link support: cef_strategy is read from window.location.search
  // in the useState initializer above (see cefStrategyFilter declaration)
  // so the very first fetchScreener already includes the filter.

  const handleStockClick = async (ticker, initialTab = null, chartPeriod = null) => {
    if (initialTab) setPendingInitialTab(initialTab);
    if (chartPeriod) setPendingInitialChartPeriod(chartPeriod);
    setLoadingDetail(true);
    setModalOpen(true);
    setPrefetchedNews(null);
    axios.get(`${API}/stocks/${ticker}/news`, { withCredentials: true })
      .then(res => setPrefetchedNews(res.data)).catch(() => {});
    try {
      // 3.5 s timeout race with stale-cache fallback — see lib/stockFetch.js
      const data = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setSelectedStock(fresh),
      });
      setSelectedStock(data);
    } catch {
      setSelectedStock(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Round 79 — StockModal back-restore (see /app/frontend/src/lib/stockModalRestore.js)
  useReopenModalOnMount(handleStockClick);

  const toggleWatchlist = async (ticker) => {
    if (!user) { setLoginModalOpen(true); return; }
    try {
      if (watchlist.includes(ticker)) {
        await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
        setWatchlist(prev => prev.filter(t => t !== ticker));
      } else {
        await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
        setWatchlist(prev => [...prev, ticker]);
      }
      window.dispatchEvent(new Event('watchlist-changed'));
    } catch {}
  };

  const applyPresetGeneric = (presetValue, currentActive, setActive) => {
    const setters = {
      tier: setTierFilter,
      frequency: setFrequencyFilter,
      yield_range: setYieldFilter,
      payout_ratio: setPayoutRatioFilter,
      sector: setSectorFilter,
      market_cap: setMarketCapFilter,
      pe_ratio: setPeRatioFilter,
      beta: setBetaFilter,
      debt_equity: setDebtEquityFilter,
      roic: setRoicFilter,
      fcf_margin: setFcfMarginFilter,
      leveraged: setLeveragedFilter,
      high_expense_ratio: setHighExpenseRatioFilter,
      low_expense_ratio: setLowExpenseRatioFilter,
      uses_futures: setUsesFuturesFilter,
      is_adr: setIsAdrFilter,
      is_bdc: setIsBdcFilter,
      is_clo: setIsCloFilter,
      is_money_market_fund: setIsMoneyMarketFundFilter,
      is_preferred: setIsPreferredFilter,
      is_cef: setIsCefFilter,
      leveraged_cef: setLeveragedCefFilter,
      is_reit: setIsReitFilter,
      is_spac: setIsSpacFilter,
      dividend_change: setDividendChangeFilter,
      // Round 98g — Categorical Altman zone flows through the string
      // setters map ('all' default, presets set 'safe' / 'gray' /
      // 'distress'). Same shape as sector/tier etc.
      altman_zone_filter: setAltmanZoneFilter,
    };
    // Numeric-typed filters need their own zero-defaults instead of
    // the string 'all', so they live in a separate map keyed by the
    // same preset filter-name.
    const numericSetters = {
      min_quality: setMinQuality,
      next_ex_within_days: setNextExWithinDays,
      recently_paid_within_days: setRecentlyPaidWithinDays,
      // Round 98c — Non-Div preset numeric setters.
      min_rule_of_40_fcf: setMinRuleOf40Fcf,
      min_rule_of_40_op: setMinRuleOf40Op,
      min_rule_of_40_sbc: setMinRuleOf40Sbc,
      // Round 98d — Micro-Cap Giants Lite + Forensic-Green setters.
      market_cap_min: setMarketCapMin,
      market_cap_max: setMarketCapMax,
      max_beneish_m: setMaxBeneishM,
      min_altman_z: setMinAltmanZ,
      min_rev_cagr_3y: setMinRevCagr3y,
      // Round 98h — Full preset numeric setters.
      min_founder_top_pct: setMinFounderTopPct,
      min_perf_1y: setMinPerf1y,
      // Jul 2026 — Coverage Watch setters.
      max_coverage_ratio: setMaxCoverageRatio,
      max_coverage_trend: setMaxCoverageTrend,
      min_coverage_ratio: setMinCoverageRatio,
      min_coverage_trend: setMinCoverageTrend,
    };
    const numericResetValue = { min_quality: 0, next_ex_within_days: 0, recently_paid_within_days: 0, min_rule_of_40_fcf: 0, min_rule_of_40_op: 0, min_rule_of_40_sbc: 0, market_cap_min: 0, market_cap_max: 0, max_beneish_m: 0, min_altman_z: 0, min_rev_cagr_3y: 0, min_founder_top_pct: 0, min_perf_1y: -9999, max_coverage_ratio: 0, max_coverage_trend: -9999, min_coverage_ratio: 0, min_coverage_trend: -9999 };
    // Round 98d — Boolean preset flag. `forensic_green` gates the
    // Forensic-Green Compounders Lite preset; false = the constraint
    // is dropped entirely on the backend.
    const boolSetters = {
      forensic_green: setForensicGreen,
      minervini_sepa: setMinerviniSepa,
      // Round 98g — Founder-Led Compounders preset needs founder_led=true.
      founder_led: setFounderLedFilter,
    };

    // Clear ONLY the filters the currently-active preset had set, so user's
    // manually-set filters (e.g. sector=Technology) are preserved.
    const current = SCREENER_PRESETS.find(p => p.value === currentActive);
    if (current?.filters) {
      Object.keys(current.filters).forEach(k => {
        if (numericSetters[k]) numericSetters[k](numericResetValue[k] ?? 0);
        else if (boolSetters[k]) boolSetters[k](false);
        else setters[k]?.('all');
      });
    }

    setActive(presetValue);
    if (presetValue === 'none') { setPage(1); return; }

    const preset = SCREENER_PRESETS.find(p => p.value === presetValue);
    if (!preset?.filters) return;
    Object.entries(preset.filters).forEach(([k, v]) => {
      if (numericSetters[k]) numericSetters[k](Number(v));
      else if (boolSetters[k]) boolSetters[k](Boolean(v));
      else setters[k]?.(v);
    });
    setPage(1);
  };
  // Round 96ac (Jul 2026) — thin wrappers so the Strategies dropdown
  // and Fund Types dropdown each drive their own independent
  // active-preset slot without stepping on each other.
  const applyPreset = (presetValue) => {
    // Sep 2026 — "Mega Tier Undervalued" is a nav shortcut to /mega-tier
    // (same side of the 3-way switch), not a filter.
    if (presetValue === 'mega_tier') {
      navigate(`/mega-tier?filter=${dividendFilter}`);
      return;
    }
    // Sep 2026 — "Exclude 3+ anomaly items" is a toggle that composes with any
    // preset (dividend AND non-dividend stocks are scored), not a preset itself.
    if (presetValue === 'anomalies_exclude3') {
      setAnomalyFilter((v) => (v === 'exclude3' ? 'all' : 'exclude3'));
      return;
    }
    applyPresetGeneric(presetValue, activePreset, setActivePreset);
  };
  const anomaliesItem = (
    <div className="my-1 border-t border-fuchsia-400/70 dark:border-fuchsia-500/60 pt-1" data-testid="preset-anomalies-section">
      <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-fuchsia-700 dark:text-fuchsia-300" data-testid="preset-group-anomalies">Accounting Anomalies · all stocks</div>
      <SelectItem
        value="anomalies_exclude3"
        className={`font-semibold ${anomalyFilter === 'exclude3' ? 'text-fuchsia-700 dark:text-fuchsia-300' : ''}`}
        data-testid="preset-option-anomalies-exclude3"
        data-active={anomalyFilter === 'exclude3' ? 'true' : 'false'}
        {...itemTipHandlers('Hide every stock whose Financial Health → Accounting Anomalies card flags 3 or more of the ten Enron-checklist items (Beneish M, C-Score, DSRI, ROIC vs WACC, leverage, goodwill, Exhibit 21 complexity, auditor fees and tenure, 8-K departures). Only stocks that have been scored (a Financial Health tab with the Accounting Anomalies card) are shown; unscored names are left out. Select again to switch it off. Anomalies are questions, not verdicts.')}
      >
        <span className="inline-flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5" /> {anomalyFilter === 'exclude3' ? '0–2 anomaly items only ✓ (tap to turn off)' : 'Only 0–2 anomaly items'}</span>
      </SelectItem>
    </div>
  );
  const megaTierItem = (
    <div className="my-1 border-y border-amber-400 dark:border-amber-500/80 py-1" data-testid="preset-mega-tier-section">
      <SelectItem
        value="mega_tier"
        className="font-semibold text-purple-700 dark:text-violet-300 focus:text-purple-800 dark:focus:text-violet-200"
        data-testid="preset-option-mega-tier"
        {...itemTipHandlers('Mega Tier Undervalued Stocks — opens the 3-stage deep-value funnel page (Quality + Value rank → Graham checks → Piotroski F-Score) on the same Dividend / All / Non-Dividend side. Use Back or the Stock Screener link to return.')}
      >
        <span className="inline-flex items-center gap-1.5"><Gem className="w-3.5 h-3.5" /> Mega Tier Undervalued</span>
      </SelectItem>
    </div>
  );

  // Round 98i (Jul 2026) — Read the `?preset=` URL param on mount and
  // auto-apply it. Powers deep-links like /founder-led → "Open Screener
  // with preset" that lands the user with the founder_led_full
  // Strategy already active. Only fires ONCE (empty deps), and only
  // when the preset value matches a known SCREENER_PRESETS entry —
  // arbitrary strings are ignored so bookmarked bad URLs won't wedge
  // the state. Runs AFTER applyPreset is defined so the auto-apply
  // path exercises the same setter registry as manual clicks.
  useEffect(() => {
    try {
      const presetFromUrl = new URLSearchParams(window.location.search).get('preset');
      if (!presetFromUrl) return;
      const valid = SCREENER_PRESETS.some(p => p.value === presetFromUrl && p.value !== 'none');
      if (valid) applyPreset(presetFromUrl);
    } catch { /* silent — bad URL, skip */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const applyFundType = (presetValue) => {
    // Round P140·4 (Feb 2026) — "Warrants" is a nav shortcut, not a
    // filter. Warrants live at `/warrants` with their own columns
    // (exercise price, underlying, redemption date) so we bounce
    // there instead of trying to shoehorn the value into the equity
    // filter state.
    if (presetValue === 'warrants') {
      navigate('/warrants');
      return;
    }
    applyPresetGeneric(presetValue, activeFundType, setActiveFundType);
    // Round 96ac.1 (Jul 2026) — when the user switches Fund Type away
    // from CEFs, wipe any stale Morningstar CEF Strategy selection so
    // the backend query can't end up with an impossible combo like
    // `is_adr=only AND cef_strategy=Real Estate` (returns zero rows).
    if (presetValue !== 'cef_stocks') {
      setCefStrategyFilter('all');
    }
  };

  const clearFilters = () => {
    // Snapshot every restorable piece of filter state BEFORE we reset,
    // so the undo toast can put everything back exactly as it was.
    // Includes Min Quality + search query + sort key (none of which
    // appear in activeFilters[] but the Clear button nukes them too).
    const snapshot = {
      tier: tierFilter,
      frequency: frequencyFilter,
      yield: yieldFilter,
      payoutRatio: payoutRatioFilter,
      sector: sectorFilter,
      marketCap: marketCapFilter,
      peRatio: peRatioFilter,
      beta: betaFilter,
      debtEquity: debtEquityFilter,
      roic: roicFilter,
      fcfMargin: fcfMarginFilter,
      leveraged: leveragedFilter,
      highExpenseRatio: highExpenseRatioFilter,
      lowExpenseRatio: lowExpenseRatioFilter,
      usesFutures: usesFuturesFilter,
      isAdr: isAdrFilter,
      isBdc: isBdcFilter,
      isClo: isCloFilter,
      isMoneyMarketFund: isMoneyMarketFundFilter,
      isPreferred: isPreferredFilter,
      isCef: isCefFilter,
      leveragedCef: leveragedCefFilter,
      isReit: isReitFilter,
      isSpac: isSpacFilter,
      cefStrategy: cefStrategyFilter,
      dividendChange: dividendChangeFilter,
      minStreakYears,
      minRecentRaisePct,
      nextExWithinDays,
      paysSpecialsFilter,
      anomalyFilter,
      recentlyPaidWithinDays,
      searchQuery,
      sortKey,
      sortDir,
      activePreset,
      activeFundType,
      minRuleOf40Fcf,
      minRuleOf40Op,
      minRuleOf40Sbc,
      marketCapMin,
      marketCapMax,
      maxBeneishM,
      minAltmanZ,
      minRevCagr3y,
      forensicGreen,
      minerviniSepa,
      altmanZoneFilter,
      founderLedFilter,
      minFounderTopPct,
      minPerf1y,
      maxCoverageRatio,
      maxCoverageTrend,
      minCoverageRatio,
      minCoverageTrend,
    };
    const activeCount = activeFilters.length + (searchQuery.trim() ? 1 : 0);

    setTierFilter('all');
    setFrequencyFilter('all');
    setYieldFilter('all');
    setPayoutRatioFilter('all');
    setSectorFilter('all');
    setIndustryFilter('all');
    setMarketCapFilter('all');
    setPeRatioFilter('all');
    setBetaFilter('all');
    setDebtEquityFilter('all');
    setRoicFilter('all');
    setFcfMarginFilter('all');
    setLeveragedFilter('all');
    setHighExpenseRatioFilter('all');
    setLowExpenseRatioFilter('all');
    setUsesFuturesFilter('all');
    setIsAdrFilter('all');
    setIsBdcFilter('all');
    setIsCloFilter('all');
    setIsMoneyMarketFundFilter('all');
    setIsPreferredFilter('all');
    setIsCefFilter('all');
    setLeveragedCefFilter('all');
    setIsReitFilter('all');
    setIsSpacFilter('all');
    setSpacStageFilter('all');
    setMmfTypeFilter('all');
    setCefStrategyFilter('all');
    setDividendChangeFilter('all');
    setNextExWithinDays(0);
    setPaysSpecialsFilter('all');
    setAnomalyFilter('all');
    setRecentlyPaidWithinDays(0);
    setMinStreakYears('0');
    setMinRecentRaisePct('0');
    setSearchQuery('');
    setSortKey('name');
    setSortDir('asc');
    setActivePreset('none');
    setActiveFundType('none');
    setMinRuleOf40Fcf(0);
    setMinRuleOf40Op(0);
    setMinRuleOf40Sbc(0);
    setMarketCapMin(0);
    setMarketCapMax(0);
    setMaxBeneishM(0);
    setMinAltmanZ(0);
    setMinRevCagr3y(0);
    setForensicGreen(false);
    setMinerviniSepa(false);
    setMinerviniSepa(false);
    setAltmanZoneFilter('all');
    setFounderLedFilter(false);
    setMinFounderTopPct(0);
    setMinPerf1y(-9999);
    setMaxCoverageRatio(0);
    setMaxCoverageTrend(-9999);
    setMinCoverageRatio(0);
    setMinCoverageTrend(-9999);
    setPage(1);

    if (activeCount === 0) return; // Nothing was on — no toast needed.

    toast(`Cleared ${activeCount} filter${activeCount === 1 ? '' : 's'}`, {
      description: 'Search and sort reset too.',
      duration: 7000,
      action: {
        label: 'Undo',
        onClick: () => {
          setTierFilter(snapshot.tier);
          setFrequencyFilter(snapshot.frequency);
          setYieldFilter(snapshot.yield);
          setPayoutRatioFilter(snapshot.payoutRatio);
          setSectorFilter(snapshot.sector);
          setMarketCapFilter(snapshot.marketCap);
          setPeRatioFilter(snapshot.peRatio);
          setBetaFilter(snapshot.beta);
          setDebtEquityFilter(snapshot.debtEquity);
          setRoicFilter(snapshot.roic);
          setFcfMarginFilter(snapshot.fcfMargin);
          setLeveragedFilter(snapshot.leveraged);
          setHighExpenseRatioFilter(snapshot.highExpenseRatio);
          setLowExpenseRatioFilter(snapshot.lowExpenseRatio);
          setUsesFuturesFilter(snapshot.usesFutures);
          setIsAdrFilter(snapshot.isAdr);
          setIsBdcFilter(snapshot.isBdc);
          setIsCloFilter(snapshot.isClo || 'all');
          setIsMoneyMarketFundFilter(snapshot.isMoneyMarketFund || 'all');
          setIsPreferredFilter(snapshot.isPreferred || 'all');
          setIsCefFilter(snapshot.isCef);
          setLeveragedCefFilter(snapshot.leveragedCef);
          setIsReitFilter(snapshot.isReit);
          setIsSpacFilter(snapshot.isSpac ?? 'all');
          setCefStrategyFilter(snapshot.cefStrategy);
          setDividendChangeFilter(snapshot.dividendChange);
          setMinStreakYears(snapshot.minStreakYears ?? '0');
          setMinRecentRaisePct(snapshot.minRecentRaisePct ?? '0');
          setNextExWithinDays(snapshot.nextExWithinDays);
          setPaysSpecialsFilter(snapshot.paysSpecialsFilter || 'all');
          setAnomalyFilter(snapshot.anomalyFilter || 'all');
          setRecentlyPaidWithinDays(snapshot.recentlyPaidWithinDays);
          setSearchQuery(snapshot.searchQuery);
          setSortKey(snapshot.sortKey);
          setSortDir(snapshot.sortDir);
          setActivePreset(snapshot.activePreset);
          setActiveFundType(snapshot.activeFundType);
          setMinRuleOf40Fcf(snapshot.minRuleOf40Fcf ?? 0);
          setMinRuleOf40Op(snapshot.minRuleOf40Op ?? 0);
          setMinRuleOf40Sbc(snapshot.minRuleOf40Sbc ?? 0);
          setMarketCapMin(snapshot.marketCapMin ?? 0);
          setMarketCapMax(snapshot.marketCapMax ?? 0);
          setMaxBeneishM(snapshot.maxBeneishM ?? 0);
          setMinAltmanZ(snapshot.minAltmanZ ?? 0);
          setMinRevCagr3y(snapshot.minRevCagr3y ?? 0);
          setForensicGreen(snapshot.forensicGreen ?? false);
          setMinerviniSepa(snapshot.minerviniSepa ?? false);
          setAltmanZoneFilter(snapshot.altmanZoneFilter ?? 'all');
          setFounderLedFilter(snapshot.founderLedFilter ?? false);
          setMinFounderTopPct(snapshot.minFounderTopPct ?? 0);
          setMinPerf1y(snapshot.minPerf1y ?? -9999);
          setMaxCoverageRatio(snapshot.maxCoverageRatio ?? 0);
          setMaxCoverageTrend(snapshot.maxCoverageTrend ?? -9999);
          setMinCoverageRatio(snapshot.minCoverageRatio ?? 0);
          setMinCoverageTrend(snapshot.minCoverageTrend ?? -9999);
        },
      },
    });
  };

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [tierFilter, frequencyFilter, yieldFilter, payoutRatioFilter, sectorFilter, marketCapFilter, peRatioFilter, betaFilter, debtEquityFilter, roicFilter, fcfMarginFilter, leveragedFilter, highExpenseRatioFilter, lowExpenseRatioFilter, usesFuturesFilter, isAdrFilter, isBdcFilter, isCloFilter, isCefFilter, isReitFilter, spacStageFilter, cefStrategyFilter, dividendChangeFilter, minStreakYears, minRecentRaisePct, nextExWithinDays, paysSpecialsFilter, anomalyFilter, recentlyPaidWithinDays, sortKey, sortDir, searchQuery, minQuality]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Coverage defaults ASC — worst (most cut-risk) coverage first.
      setSortDir(key === 'name' || key === 'coverage' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="min-h-screen min-h-[100svh] bg-background transition-colors duration-500" data-testid="screener-page">

      {/* Screener Sub-header */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 border-b border-border/30">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <BackOrHomeButton testid="screener-back-or-home-btn" showEscHint={false} />
          <h2 className="text-base font-bold tracking-tight flex items-center gap-2" data-testid="screener-title">
            <Filter className="w-4 h-4 text-primary" />
            {dividendFilter === 'non_dividend' ? 'Non-Dividend Stock Screener'
              : dividendFilter === 'all' ? 'All Stocks Screener'
              : 'Dividend Stock Screener'}
          </h2>
          {/* Desktop-only: keep the counter + live pill in the sub-header
              on the same row as the title. Mobile moves both under the
              3-way filter switch below (Feb 2026). */}
          <span className="text-xs text-muted-foreground ml-2 hidden sm:inline" data-testid="screener-total-count-desktop">{totalCount} stocks</span>
          <span className="hidden sm:inline">
            <LivePriceIndicator status={screenerStreamStatus} />
          </span>
        </div>

        {/* Round 97a (Jul 4 2026) — 3-way segmented control.
            Non-admin users see All / Non-Dividend as disabled tabs with
            a "Pro" lock icon until Stripe paywall goes live. Admin bypass
            (checked via user.role) lets the site owner preview both
            modes today. Clicking a disabled tab shows an upgrade toast. */}
        {/* Round 104g (Jun 2026) — Screener now uses the SAME 3-way
            segmented control as the Dashboard (gold locks + Plus
            upsell modal on All / Non-Dividend for free users), fixing
            the mismatched inline tablist that only showed a toast. */}
        <div className="mt-2 flex items-center justify-center" data-testid="dividend-filter-switch">
          <DividendFilterSegmentedControl
            value={dividendFilter}
            isAdmin={isAdmin}
            instanceId="screener"
            onChange={(v) => {
              setDividendFilter(v);
              setPage(1);
            }}
          />
          {/* Feb 2026 — Mobile-only stock counter, right of the 3-way
              switch per user request. Desktop keeps its counter in the
              title row above. */}
          <span className="text-xs text-muted-foreground ml-2 sm:hidden" data-testid="screener-total-count-mobile">
            {totalCount} stocks
          </span>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        {/* Filter dropdowns — Mobile/tablet (<lg): horizontal-scroll
            rail with 8 paired columns × 2 rows. Each pair shares one
            width (the larger of the two boxes' desktop widths) so the
            mobile layout reads as tidy equal-pair columns. Desktop (lg+):
            falls back to the original flex-wrap layout where each
            dropdown keeps its individual width — `lg:contents` makes
            the pair-wrapper invisible to the desktop flex layout, so
            its children join the parent flex flow directly. */}
        {/* Mobile-only sticky summary of active filters — appears just
            above the filter rail so users see what's currently narrowed
            without having to scroll the rail or down to the active-pills
            row below the slider. Reuses the same activeFilters[] data
            and per-pill remove handlers as the desktop pills, but
            rendered compact (just colour-coded icon + X) so 5+ active
            filters fit on a single mobile line. The full text label
            stays in the desktop pills row below the slider. The
            "Icon Key" button always renders (even when no filters are
            active) so the legend is permanently discoverable on phone
            and tablet — without it, the icons would be a guessing game
            for first-time users. */}
        {/* Feb 2026 — Removed the mobile "Active (N): [icon][icon]"
            summary strip per user request. The full-worded filter
            chips further below the Min Growth slider already show the
            same active filters with X buttons, so the icon strip was
            visual duplication. Desktop screener didn't have this
            summary block to begin with. */}

        {/* Filter rail wrapper — relative so the directional arrow
            chevrons can be absolutely positioned over the edges. The
            chevrons are hidden on lg+ where the rail wraps and there's
            nothing to scroll. */}
        <div className="relative">
        <div
          ref={filterRailRef}
          className="flex items-stretch gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin lg:flex-wrap lg:overflow-visible lg:p-0 lg:m-0 lg:items-center"
        >
          {/* Round 96ac (Jul 2026) — Consolidated 5-category filter rail.
              Was: 17 individual dropdowns in an 8-pair 2-tall mobile
              scroll strip. Now: Strategies · Fund Types · Dividend
              Health · Valuation & Financials · Sector. Fits one row on
              desktop, single-tall swipe on mobile. Every original
              filter still exists — they now live inside category
              popovers so the top-level rail stays scannable.

              Per-category active-filter counts drive a small badge on
              each category trigger so users can see at a glance where
              their active filters live even when the popover is closed. */}
          {(() => {
            const dividendHealthActiveCount = [
              yieldFilter !== 'all',
              payoutRatioFilter !== 'all',
              frequencyFilter !== 'all',
              dividendChangeFilter !== 'all',
              Number(minStreakYears) > 0,
              Number(minRecentRaisePct) > 0,
              nextExWithinDays > 0,
              paysSpecialsFilter !== 'all',
              tierFilter !== 'all',
            ].filter(Boolean).length;
            const valuationActiveCount = [
              peRatioFilter !== 'all',
              marketCapFilter !== 'all',
              debtEquityFilter !== 'all',
              roicFilter !== 'all',
              fcfMarginFilter !== 'all',
              betaFilter !== 'all',
            ].filter(Boolean).length;
            const fundTypeActiveCount = [
              activeFundType !== 'none',
              cefStrategyFilter !== 'all',
            ].filter(Boolean).length;
            // Round 98f (Feb 2026) — Growth + Forensics & Ownership
            // categories only show in All / Non-Dividend modes since
            // both engines are non-div specific.
            const growthActiveCount = [
              minRuleOf40Fcf > 0,
              minRuleOf40Op > 0,
              minRuleOf40Sbc > 0,
              minRevCagr3y > 0,
              minPerf1y > -9000,
            ].filter(Boolean).length;
            const forensicsActiveCount = [
              altmanZoneFilter !== 'all',
              forensicGreen,
              founderLedFilter,
              minFounderTopPct > 0,
            ].filter(Boolean).length;
            const catBadge = (n) => n > 0 ? (
              <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold bg-blue-500/20 text-blue-700 dark:text-cyan-300 border border-blue-500/40">
                {n}
              </span>
            ) : null;
            return (
              <>
                {/* 1. Strategies — renamed from "Quick Screens". Only shows
                    the true multi-filter combo presets (Blue Chip, Value
                    Picks, Safe & Steady, etc). Fund-type toggles were
                    moved out to their own category. */}
                <Select value={activePreset} onValueChange={applyPreset}>
                  <SelectTrigger className={`w-[195px] lg:w-[180px] h-9 text-sm border-primary/30 bg-primary/5 shrink-0 ${activePreset !== 'none' || anomalyFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="preset-dropdown-trigger"
                    data-anomalies-active={anomalyFilter === 'exclude3' ? 'true' : 'false'}
                    {...tipHandlers(SCREENER_PRESETS, activePreset)}>
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <Zap className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span className="truncate">
                        <SelectValue />
                      </span>
                    </div>
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    <SelectItem value="none">Strategies</SelectItem>
                    {/* Round 98c — Dividend Strategy presets: shown in
                        Dividend + All modes. Hidden in Non-Dividend. */}
                    {dividendFilter !== 'non_dividend' && (
                      <>
                        <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300" data-testid="preset-group-dividend">Dividend Strategy Presets</div>
                        {SCREENER_PRESETS.filter(p => p.group === 'Strategy').map(p => (
                          <SelectItem key={p.value} value={p.value} {...itemTipHandlers(p.tip)}>{p.label}</SelectItem>
                        ))}
                      </>
                    )}
                    {/* Sep 2026 — Mega Tier nav item: bottom of Dividend,
                        bottom of Non-Dividend, between the two in All. */}
                    {megaTierItem}
                    {/* Non-Div Strategy presets: shown in Non-Div + All
                        modes. Hidden in pure Dividend mode. */}
                    {dividendFilter !== 'dividend' && (
                      <>
                        <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300" data-testid="preset-group-non-dividend">Non-Dividend Strategy Presets</div>
                        {SCREENER_PRESETS.filter(p => p.group === 'Non-Div Strategy').map(p => (
                          <SelectItem key={p.value} value={p.value} {...itemTipHandlers(p.tip)}>{p.label}</SelectItem>
                        ))}
                      </>
                    )}
                    {anomaliesItem}
                  </SelectContent>
                </Select>

                {/* 2. Fund Types — REITs, BDCs, CEFs, ADRs, Leveraged,
                    Futures, Cheap ETFs, High-Exp ETFs. When a CEF preset
                    is active AND the CEF Strategy list is available, a
                    nested CEF Strategy sub-select appears inside the
                    popover so users can drill down to a Morningstar
                    bucket without adding another top-level dropdown. */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-[195px] lg:w-[165px] h-9 text-sm justify-between shrink-0 ${fundTypeActiveCount > 0 ? 'text-blue-600 dark:text-cyan-300 border-blue-500/40' : ''}`}
                      data-testid="fund-types-category-trigger"
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <Building2 className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Fund Types</span>
                        {catBadge(fundTypeActiveCount)}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-3" align="start" data-testid="fund-types-category-popover">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
                      Fund Type
                    </div>
                    <Select value={activeFundType} onValueChange={applyFundType}>
                      <SelectTrigger className={`w-full h-9 text-sm ${activeFundType !== 'none' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="fund-type-dropdown-trigger">
                        <SelectValue placeholder="All Fund Types" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[400px]">
                        <SelectItem value="none">All Fund Types</SelectItem>
                        {SCREENER_PRESETS.filter(p => p.group === 'Fund Type').filter(p => {
                          // Round 97a — In Non-Dividend mode, hide the
                          // inherently-dividend fund types (REITs, BDCs,
                          // CEFs, Leveraged CEFs, Leveraged CEFs Monthly)
                          // since they by definition pay income.
                          // P158·14 (Feb 2026) — MMFs also pay monthly
                          // dividend distributions, so hide them too
                          // when the user has narrowed to non-dividend.
                          if (dividendFilter === 'non_dividend') {
                            return !['reit_stocks', 'bdc_stocks', 'cef_stocks', 'leveraged_cefs', 'leveraged_cefs_monthly', 'money_market_funds', 'preferred_shares'].includes(p.value);
                          }
                          // Round P140·5 (Feb 2026) — In Dividend-only
                          // mode, hide Warrants since warrants never pay
                          // dividends (they're option-like contracts on
                          // the underlying, not equity income vehicles).
                          // Round P144 (Feb 2026) — Also hide the SPAC
                          // (blank-check) fund type in Dividend-only
                          // mode. SPACs hold IPO proceeds in trust
                          // and never distribute income, so the option
                          // is nonsensical when the user has already
                          // narrowed the screener to dividend payers.
                          if (dividendFilter === 'dividend') {
                            return p.value !== 'warrants' && p.value !== 'spac_stocks';
                          }
                          // 'all' mode — show everything.
                          return true;
                        }).map(p => (
                          <SelectItem key={p.value} value={p.value} data-testid={`fund-type-option-${p.value}`} {...itemTipHandlers(p.tip)}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* CEF Strategy sub-select — ONLY visible when the
                        CEFs fund type is picked. This is the whole point
                        of nesting it here: it's meaningless outside that
                        context. When the user switches Fund Type away
                        from CEFs, applyFundType() below auto-resets the
                        cefStrategyFilter so a stale strategy value can't
                        poison the backend query. */}
                    {activeFundType === 'cef_stocks' && cefStrategies.length > 0 && (
                      <div className="mt-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
                          CEF Strategy
                        </div>
                        <Select value={cefStrategyFilter} onValueChange={setCefStrategyFilter}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${cefStrategyFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="cef-strategy-filter-trigger"
                          >
                            <SelectValue placeholder="All CEF Strategies" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[400px]">
                            <SelectItem value="all" data-testid="cef-strategy-filter-all">All CEF Strategies</SelectItem>
                            <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                              Morningstar Buckets ({cefStrategies.length})
                            </div>
                            {cefStrategies.map((s) => {
                              const shortName = s.name.replace(/^Morningstar US CEF /, '');
                              return (
                                <SelectItem
                                  key={s.name}
                                  value={s.name}
                                  data-testid={`cef-strategy-filter-${shortName.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  <span className="flex items-center justify-between gap-3 w-full">
                                    <span>{shortName}</span>
                                    <span className="text-[10px] font-mono text-muted-foreground">{s.count}</span>
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>

                {/* 3. Dividend Health — 8 dividend-specific sub-filters.
                    (Yield, Payout Ratio, Frequency, Div Change, Streak,
                    Recent Raise %, Upcoming Ex-Div, Tier). Hidden when
                    the 3-way switch is set to Non-Dividend (these
                    filters are meaningless without a dividend). */}
                {dividendFilter !== 'non_dividend' && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-[195px] lg:w-[175px] h-9 text-sm justify-between shrink-0 ${dividendHealthActiveCount > 0 ? 'text-blue-600 dark:text-cyan-300 border-blue-500/40' : ''}`}
                      data-testid="dividend-health-category-trigger"
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <Percent className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Dividend Health</span>
                        {catBadge(dividendHealthActiveCount)}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[520px] p-3 max-h-[75vh] overflow-y-auto" align="start" data-testid="dividend-health-category-popover">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Yield</div>
                        <Select value={yieldFilter} onValueChange={setYieldFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${yieldFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="yield-filter-trigger" {...tipHandlers(YIELD_OPTIONS, yieldFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {YIELD_OPTIONS.map(o => (
                              <SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>
                                <span className="flex items-center gap-2">
                                  {o.dot && <span className={`w-2.5 h-2.5 rounded-full ${o.dot} shrink-0`} />}
                                  {o.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Payout Ratio</div>
                        <Select value={payoutRatioFilter} onValueChange={setPayoutRatioFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${payoutRatioFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="payout-ratio-filter-trigger" {...tipHandlers(PAYOUT_RATIO_OPTIONS, payoutRatioFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYOUT_RATIO_OPTIONS.map(o => (
                              <SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>
                                <span className="flex items-center gap-2">
                                  {o.dot && <span className={`w-2.5 h-2.5 rounded-full ${o.dot} shrink-0`} />}
                                  {o.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Frequency</div>
                        <Select value={frequencyFilter} onValueChange={setFrequencyFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${frequencyFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="frequency-filter-trigger" {...tipHandlers(FREQUENCY_OPTIONS, frequencyFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{FREQUENCY_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Div Change</div>
                        <Select value={dividendChangeFilter} onValueChange={setDividendChangeFilter}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${dividendChangeFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="dividend-change-filter-trigger"
                            onMouseEnter={(e) => showTip('Recent Dividend Change — surface stocks whose latest payment is at least 10% higher (Raised) or 10% lower (Cut) than the prior payment, when that payment landed in the last 90 days.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue placeholder="Any Div Change" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all" data-testid="dividend-change-filter-all">Any Div Change</SelectItem>
                            <SelectItem value="raised" data-testid="dividend-change-filter-raised">⬆ Recently Raised (≥10%)</SelectItem>
                            <SelectItem value="cut" data-testid="dividend-change-filter-cut">⬇ Recently Cut (≥10%)</SelectItem>
                            <SelectItem value="any" data-testid="dividend-change-filter-any">Either Raise or Cut</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Raise Streak (yrs)</div>
                        <Select value={minStreakYears} onValueChange={setMinStreakYears}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${Number(minStreakYears) > 0 ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="min-streak-years-filter-trigger"
                            onMouseEnter={(e) => showTip('Dividend Raise Streak — minimum consecutive years of raises. 10y+ is a Dividend Achiever, 25y+ is an Aristocrat, 50y+ is a King. Universal coverage; works for every cached stock.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue placeholder="Any Streak" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0" data-testid="min-streak-years-filter-any">Any Streak</SelectItem>
                            <SelectItem value="3" data-testid="min-streak-years-filter-3">≥ 3 years of raises</SelectItem>
                            <SelectItem value="5" data-testid="min-streak-years-filter-5">≥ 5 years of raises</SelectItem>
                            <SelectItem value="10" data-testid="min-streak-years-filter-10">≥ 10 years (Achiever)</SelectItem>
                            <SelectItem value="25" data-testid="min-streak-years-filter-25">≥ 25 years (Aristocrat)</SelectItem>
                            <SelectItem value="50" data-testid="min-streak-years-filter-50">≥ 50 years (King)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Recent Raise %</div>
                        <Select value={minRecentRaisePct} onValueChange={setMinRecentRaisePct}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${Number(minRecentRaisePct) > 0 ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="min-recent-raise-pct-filter-trigger"
                            onMouseEnter={(e) => showTip('Most Recent Raise % — filters by the size of the most recent dividend increase. Combines with the streak filter — set both to find stocks that have raised reliably AND meaningfully.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue placeholder="Any Raise %" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0" data-testid="min-recent-raise-pct-filter-any">Any Raise %</SelectItem>
                            <SelectItem value="1" data-testid="min-recent-raise-pct-filter-1">≥ 1% on last raise</SelectItem>
                            <SelectItem value="3" data-testid="min-recent-raise-pct-filter-3">≥ 3% on last raise</SelectItem>
                            <SelectItem value="5" data-testid="min-recent-raise-pct-filter-5">≥ 5% on last raise</SelectItem>
                            <SelectItem value="10" data-testid="min-recent-raise-pct-filter-10">≥ 10% on last raise</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Upcoming Ex-Div</div>
                        <Select value={String(nextExWithinDays)} onValueChange={(v) => setNextExWithinDays(Number(v))}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${nextExWithinDays > 0 ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="upcoming-ex-div-filter-trigger"
                            onMouseEnter={(e) => showTip('Upcoming Ex-Div — surface stocks whose next ex-dividend date falls within the chosen window. Buy BEFORE the ex-div date to qualify for the next payment. "Within 95 days" covers a full quarterly cycle so every dividend payer shows up at least once.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue placeholder="Any Upcoming Ex-Div" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0" data-testid="upcoming-ex-div-filter-all">Any Upcoming Ex-Div</SelectItem>
                            <SelectItem value="7"  data-testid="upcoming-ex-div-filter-7">Within 7 days</SelectItem>
                            <SelectItem value="15" data-testid="upcoming-ex-div-filter-15">Within 15 days</SelectItem>
                            <SelectItem value="30" data-testid="upcoming-ex-div-filter-30">Within 30 days</SelectItem>
                            <SelectItem value="60" data-testid="upcoming-ex-div-filter-60">Within 60 days</SelectItem>
                            <SelectItem value="95" data-testid="upcoming-ex-div-filter-95">Within 95 days (full quarter)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Tier</div>
                        <Select value={tierFilter} onValueChange={setTierFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${tierFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="tier-filter-trigger" {...tipHandlers(TIER_OPTIONS, tierFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{TIER_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Special Dividends</div>
                        <Select value={paysSpecialsFilter} onValueChange={setPaysSpecialsFilter}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${paysSpecialsFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="pays-specials-filter-trigger"
                            onMouseEnter={(e) => showTip('Pays Specials — regular dividend payers that have ALSO paid 3 or more one-off special dividends in the history we hold (COST-style bonus payers). Specials are not part of yield or payout ratio; they are extra cash returns the board votes on case by case.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all" data-testid="pays-specials-filter-all">Any</SelectItem>
                            <SelectItem value="yes" data-testid="pays-specials-filter-yes" {...itemTipHandlers('Regular payers with 3 or more special dividends on record (COST, JBSS, …).')}>Pays Specials (3+ on record)</SelectItem>
                            <SelectItem value="habitual" data-testid="pays-specials-filter-habitual" {...itemTipHandlers('Habitual bonus payers: 10 or more specials on record — extras are policy, not a one-off (AFG, CME, WRB, PCAR). Note: for BDCs (MAIN, TSLX) and Brazilian ADRs (ITUB, PBR) the "specials" are supplemental / interest-on-capital payments — part of the regular payout structure, so a high count there means stability, not windfall.')}>Habitual Specials (10+ on record)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                )}

                {/* Round 98f (Feb 2026) — Growth category. Only visible
                    in All + Non-Dividend modes (dividend mode users
                    have Dividend Health instead). Three sub-filters:
                    Rule of 40 FCF, Rule of 40 Op, and 3y Revenue CAGR.
                    Values map directly to the same numeric state slots
                    the Non-Div presets set, so a preset + a manual
                    tweak compose seamlessly. */}
                {dividendFilter !== 'dividend' && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-[195px] lg:w-[130px] h-9 text-sm justify-between shrink-0 ${growthActiveCount > 0 ? 'text-blue-600 dark:text-cyan-300 border-blue-500/40' : ''}`}
                      data-testid="growth-category-trigger"
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Growth</span>
                        {catBadge(growthActiveCount)}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[440px] p-3 max-h-[75vh] overflow-y-auto" align="start" data-testid="growth-category-popover">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Rule of 40 (FCF)</div>
                        <Select value={String(minRuleOf40Fcf || 0)} onValueChange={(v) => setMinRuleOf40Fcf(Number(v))}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${minRuleOf40Fcf > 0 ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="growth-rule-of-40-fcf-trigger"
                            onMouseEnter={(e) => showTip('Rule of 40 (FCF variant) = Revenue Growth % + Free Cash Flow Margin %. 40+% is the SaaS-era standard for "growing profitably". Raise the bar to 50 or 60 to find true efficiency compounders.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Any</SelectItem>
                            <SelectItem value="30">≥ 30%</SelectItem>
                            <SelectItem value="40">≥ 40%</SelectItem>
                            <SelectItem value="50">≥ 50%</SelectItem>
                            <SelectItem value="60">≥ 60%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Rule of 40 (Op)</div>
                        <Select value={String(minRuleOf40Op || 0)} onValueChange={(v) => setMinRuleOf40Op(Number(v))}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${minRuleOf40Op > 0 ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="growth-rule-of-40-op-trigger"
                            onMouseEnter={(e) => showTip('Rule of 40 (Operating variant) = Revenue Growth % + Operating Margin %. Tighter test than FCF (op margin is harder to inflate). 40+% is elite operational quality.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Any</SelectItem>
                            <SelectItem value="20">≥ 20%</SelectItem>
                            <SelectItem value="30">≥ 30%</SelectItem>
                            <SelectItem value="40">≥ 40%</SelectItem>
                            <SelectItem value="50">≥ 50%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Rule of 40 (SBC-adjusted FCF)</div>
                        <Select value={String(minRuleOf40Sbc || 0)} onValueChange={(v) => setMinRuleOf40Sbc(Number(v))}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${minRuleOf40Sbc > 0 ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="growth-rule-of-40-sbc-trigger"
                            onMouseEnter={(e) => showTip('Rule of 40 (SBC-adjusted) = Revenue Growth % + (Free Cash Flow − Stock-Based Compensation) ÷ Revenue %. GAAP adds stock comp back into operating cash flow, so the plain FCF variant flatters heavy-SBC companies; this one subtracts the SEC-filed figure again. Passing FCF but failing here = growth funded by diluting you. Only tickers whose 10-K carries the SBC line qualify.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Any</SelectItem>
                            <SelectItem value="20">≥ 20%</SelectItem>
                            <SelectItem value="30">≥ 30%</SelectItem>
                            <SelectItem value="40">≥ 40%</SelectItem>
                            <SelectItem value="50">≥ 50%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Revenue CAGR (3y)</div>
                        <Select value={String(minRevCagr3y || 0)} onValueChange={(v) => setMinRevCagr3y(Number(v))}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${minRevCagr3y > 0 ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="growth-rev-cagr-3y-trigger"
                            onMouseEnter={(e) => showTip('3-year revenue CAGR (compound annual growth rate). Raise to 20%+ to focus on hyper-growth names; 10%+ is a healthy compounder threshold.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Any</SelectItem>
                            <SelectItem value="0.05">≥ 5%</SelectItem>
                            <SelectItem value="0.1">≥ 10%</SelectItem>
                            <SelectItem value="0.15">≥ 15%</SelectItem>
                            <SelectItem value="0.2">≥ 20%</SelectItem>
                            <SelectItem value="0.3">≥ 30%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Round 98h — 12-month trailing return filter.
                          Populated by the existing daily-weekly-tickers
                          cron, so no backfill needed. Also matches the
                          "1Y" column already visible in the Stock Table. */}
                      <div className="col-span-2">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">1-Year Return (perf_1y)</div>
                        <Select value={String(minPerf1y)} onValueChange={(v) => setMinPerf1y(Number(v))}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${minPerf1y > -9000 ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="growth-perf-1y-trigger"
                            onMouseEnter={(e) => showTip('Trailing 12-month price return floor. Positive numbers = outperformers; 20%+ is a healthy momentum threshold. Filters stocks with missing / null 1Y history out entirely.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="-9999">Any</SelectItem>
                            <SelectItem value="0">≥ 0% (positive)</SelectItem>
                            <SelectItem value="10">≥ 10%</SelectItem>
                            <SelectItem value="20">≥ 20%</SelectItem>
                            <SelectItem value="50">≥ 50%</SelectItem>
                            <SelectItem value="100">≥ 100% (doubled)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                )}

                {/* Round 98f (Feb 2026) — Forensics & Ownership category.
                    Only visible in All + Non-Dividend modes. Bundles the
                    3 lenses that answer "can I trust these books + who
                    owns this thing": Altman Z zone, Forensic penalty
                    model, and Founder-Led (Form 3 top-holder detection). */}
                {dividendFilter !== 'dividend' && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-[195px] lg:w-[195px] h-9 text-sm justify-between shrink-0 ${forensicsActiveCount > 0 ? 'text-blue-600 dark:text-cyan-300 border-blue-500/40' : ''}`}
                      data-testid="forensics-category-trigger"
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Forensics &amp; Ownership</span>
                        {catBadge(forensicsActiveCount)}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[440px] p-3 max-h-[75vh] overflow-y-auto" align="start" data-testid="forensics-category-popover">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Altman Z Zone</div>
                        <Select value={altmanZoneFilter} onValueChange={setAltmanZoneFilter}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${altmanZoneFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="forensics-altman-zone-trigger"
                            onMouseEnter={(e) => showTip('Altman Z-Score zones — bankruptcy risk model. SAFE (Z>2.99), GRAY (1.81<Z≤2.99), DISTRESS (Z≤1.81). Model is famously noisy on tech / biotech but a strong "verify the thesis" signal.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Any Zone</SelectItem>
                            <SelectItem value="safe">SAFE (Z &gt; 2.99)</SelectItem>
                            <SelectItem value="gray">GRAY (1.81 &lt; Z ≤ 2.99)</SelectItem>
                            <SelectItem value="distress">DISTRESS (Z ≤ 1.81)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Forensic Model</div>
                        <Select value={forensicGreen ? 'clean' : 'all'} onValueChange={(v) => setForensicGreen(v === 'clean')}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${forensicGreen ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="forensics-forensic-green-trigger"
                            onMouseEnter={(e) => showTip('Forensic Model — our quality engine flags stocks with accounting red flags, dilution traps, or working-capital anomalies. "Clean" restricts to rows where no forensic penalty fired.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Any</SelectItem>
                            <SelectItem value="clean">Clean (no penalty)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Founder-Led</div>
                        <Select value={founderLedFilter ? 'only' : 'all'} onValueChange={(v) => setFounderLedFilter(v === 'only')}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${founderLedFilter ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="forensics-founder-led-trigger"
                            onMouseEnter={(e) => showTip('Founder-Led — restrict to companies where a Form 3 initial insider filing shows a top holder parked at founder-scale size. Detected on ~1,800 tickers across the universe.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Any</SelectItem>
                            <SelectItem value="only">Only Founder-Detected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Round 98h — Insider % of float floor. Tightens
                          plain founder-led into "material stake". Only
                          takes effect when Founder-Led is also set
                          (backend query works either way but the UX
                          promise is clearer this way). */}
                      <div className="col-span-2">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Founder Min % of Float</div>
                        <Select value={String(minFounderTopPct || 0)} onValueChange={(v) => setMinFounderTopPct(Number(v))}>
                          <SelectTrigger
                            className={`w-full h-9 text-sm ${minFounderTopPct > 0 ? 'text-blue-600 dark:text-cyan-300' : ''}`}
                            data-testid="forensics-founder-min-pct-trigger"
                            onMouseEnter={(e) => showTip('Insider-% floor. 10%+ of float = material "skin in the game" — screens out symbolic ownership. Pair with Founder-Led=Only for the tightest founder-scale filter.', e)}
                            onMouseLeave={hideTip}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Any</SelectItem>
                            <SelectItem value="0.05">≥ 5% of float</SelectItem>
                            <SelectItem value="0.1">≥ 10% of float</SelectItem>
                            <SelectItem value="0.2">≥ 20% of float</SelectItem>
                            <SelectItem value="0.3">≥ 30% of float</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                )}

                {/* 4. Valuation & Financials — 6 valuation/fundamentals
                    sub-filters (P/E, Market Cap, D/E, ROIC, FCF Margin,
                    Beta). Visible in every 3-way switch mode. */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-[195px] lg:w-[195px] h-9 text-sm justify-between shrink-0 ${valuationActiveCount > 0 ? 'text-blue-600 dark:text-cyan-300 border-blue-500/40' : ''}`}
                      data-testid="valuation-financials-category-trigger"
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <BarChart3 className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Valuation &amp; Financials</span>
                        {catBadge(valuationActiveCount)}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[520px] p-3 max-h-[75vh] overflow-y-auto" align="start" data-testid="valuation-financials-category-popover">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">P/E Ratio</div>
                        <Select value={peRatioFilter} onValueChange={setPeRatioFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${peRatioFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="pe-ratio-filter-trigger" {...tipHandlers(PE_RATIO_OPTIONS, peRatioFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{PE_RATIO_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Market Cap</div>
                        <Select value={marketCapFilter} onValueChange={setMarketCapFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${marketCapFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="market-cap-filter-trigger" {...tipHandlers(MARKET_CAP_OPTIONS, marketCapFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{MARKET_CAP_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Debt / Equity</div>
                        <Select value={debtEquityFilter} onValueChange={setDebtEquityFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${debtEquityFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="debt-equity-filter-trigger" {...tipHandlers(DEBT_EQUITY_OPTIONS, debtEquityFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{DEBT_EQUITY_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">ROIC</div>
                        <Select value={roicFilter} onValueChange={setRoicFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${roicFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="roic-filter-trigger" {...tipHandlers(ROIC_OPTIONS, roicFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{ROIC_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">FCF Margin</div>
                        <Select value={fcfMarginFilter} onValueChange={setFcfMarginFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${fcfMarginFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="fcf-margin-filter-trigger" {...tipHandlers(FCF_MARGIN_OPTIONS, fcfMarginFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{FCF_MARGIN_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Beta</div>
                        <Select value={betaFilter} onValueChange={setBetaFilter}>
                          <SelectTrigger className={`w-full h-9 text-sm ${betaFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="beta-filter-trigger" {...tipHandlers(BETA_OPTIONS, betaFilter)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{BETA_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>{o.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* 5. Sector — stays as a top-level single Select
                    (11 sectors; natural single-select, no need to nest). */}
                <Select value={sectorFilter} onValueChange={setSectorFilter}>
                  <SelectTrigger className={`w-[195px] lg:w-[155px] h-9 text-sm shrink-0 ${sectorFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300' : ''}`} data-testid="sector-filter-trigger"
                    {...tipHandlers(SECTOR_OPTIONS, sectorFilter)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>{SECTOR_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value} {...itemTipHandlers(o.tip)}>{o.label}</SelectItem>))}</SelectContent>
                </Select>

                {/* 6. Industry — searchable typeahead. Fed by the
                    /api/screener/industries endpoint, filtered per
                    mode (Dividend / All / Non-Dividend). Placeholder
                    hints at the largest bucket so users know where
                    to start typing (e.g. "Pharmaceutical..." for
                    non-div, "Utilities..." for dividend). */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-[195px] lg:w-[165px] h-9 text-sm justify-between shrink-0 ${industryFilter !== 'all' ? 'text-blue-600 dark:text-cyan-300 border-blue-500/40' : ''}`}
                      data-testid="industry-filter-trigger"
                      title={industryFilter !== 'all' ? `Industry: ${industryFilter}` : 'Filter by SIC industry (300+ options — type to search).'}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <Layers className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">
                          {industryFilter === 'all' ? 'All Industries' : industryFilter.replace(/^SERVICES-/, '').split(',')[0]}
                        </span>
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[380px] p-0"
                    align="start"
                    data-testid="industry-filter-popover"
                  >
                    <Command shouldFilter>
                      <CommandInput
                        placeholder={
                          industryTopExample
                            ? `Search — e.g. ${industryTopExample.name.slice(0, 32)}${industryTopExample.name.length > 32 ? '…' : ''}`
                            : 'Search industries…'
                        }
                        data-testid="industry-filter-search"
                      />
                      <CommandList className="max-h-[340px]">
                        <CommandEmpty>No industries match that search.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => setIndustryFilter('all')}
                            className="cursor-pointer"
                            data-testid="industry-filter-item-all"
                          >
                            <span className="flex items-center justify-between gap-3 w-full">
                              <span className="font-medium">All Industries</span>
                              <span className="text-[10px] font-mono text-muted-foreground">
                                {industryList.length}
                              </span>
                            </span>
                          </CommandItem>
                          {industryList.map((ind) => (
                            <CommandItem
                              key={ind.name}
                              value={ind.name}
                              onSelect={() => setIndustryFilter(ind.name)}
                              className={`cursor-pointer ${industryFilter === ind.name ? 'bg-primary/10 font-semibold' : ''}`}
                              data-testid={`industry-filter-item-${ind.name.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              <span className="flex items-center justify-between gap-3 w-full">
                                <span className="truncate">{ind.name}</span>
                                <span className="text-[10px] font-mono text-muted-foreground shrink-0">{ind.count}</span>
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </>
            );
          })()}

          {/* Clear button moved into the active-filters row below */}
        </div>

        {/* Left/Right scroll affordances — fade in based on rail position.
            Mobile/tablet only (lg:hidden). Each chevron is a clickable
            button that scrolls ~80% of the visible rail width in that
            direction, plus an additional gradient fade effect that hints
            "more content this way" without being visually loud. */}
        {railScroll.canLeft && (
          <button
            type="button"
            onClick={() => scrollRail('left')}
            className="lg:hidden absolute left-0 top-0 bottom-2 z-10 flex items-center pl-0.5 pr-2 bg-gradient-to-r from-background/95 via-background/70 to-transparent animate-pulse"
            aria-label="Scroll filter row left"
            data-testid="filter-rail-scroll-left"
          >
            <span className="rounded-full bg-primary/20 ring-1 ring-primary/40 p-0.5 backdrop-blur-sm">
              <ChevronLeft className="w-4 h-4 text-primary" strokeWidth={3} />
            </span>
          </button>
        )}
        {railScroll.canRight && (
          <button
            type="button"
            onClick={() => scrollRail('right')}
            className="lg:hidden absolute right-0 top-0 bottom-2 z-10 flex items-center pl-2 pr-0.5 bg-gradient-to-l from-background/95 via-background/70 to-transparent animate-pulse"
            aria-label="Scroll filter row right"
            data-testid="filter-rail-scroll-right"
          >
            <span className="rounded-full bg-primary/20 ring-1 ring-primary/40 p-0.5 backdrop-blur-sm">
              <ChevronRight className="w-4 h-4 text-primary" strokeWidth={3} />
            </span>
          </button>
        )}
        </div>{/* /Filter rail wrapper */}

        {/* Round 98b (Jul 2026) — Min Quality slider is now shown in
            ALL 3 modes. Label + hint swap based on which quality score
            drives the sort/filter for the current mode:
              • Dividend    → Dividend Quality Score
              • Non-Div     → Growth Quality Score
              • All         → Unified (matches whichever badge each row shows)
            The backend `min_quality` filter is mode-aware (dividend
            → quality_score; non-div → non_dividend_quality_score;
            all → either matches the badge on the row). Bands + max
            re-fetch on mode switch so the histogram + slider ceiling
            stay honest per mode. */}
        <div
          className="flex items-center gap-3 mt-3 px-3 py-2 rounded-lg bg-muted/30 border border-border/40"
          data-testid="min-quality-filter"
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
            Min {dividendFilter === 'non_dividend' ? 'Growth' : 'Quality'}
          </span>
          <input
            type="range"
            min={0}
            max={qualityMax}
            step={5}
            value={Math.min(minQuality, qualityMax)}
            onChange={(e) => setMinQuality(Number(e.target.value))}
            className="flex-1 max-w-xs accent-emerald-500"
            data-testid="min-quality-slider"
          />
          <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 w-10 text-right">
            {minQuality === 0 ? 'Off' : `${minQuality}+`}
          </span>
          {minQuality > 0 && (
            <button
              type="button"
              onClick={() => setMinQuality(0)}
              className="text-[11px] text-rose-500 hover:text-rose-400 hover:underline"
              data-testid="min-quality-reset"
            >
              reset
            </button>
          )}
          <span className="text-[11px] text-muted-foreground hidden sm:inline">
            {dividendFilter === 'non_dividend'
              ? 'Growth Quality Score (0–100). Set 70+ to keep just G-A / G-S names.'
              : dividendFilter === 'all'
                ? 'Unified Quality Score (0–100). Uses each row\'s badge score. Set 70+ to keep A / G-A tier names.'
                : 'Dividend Quality Score (0–100). Set 70+ to keep just A-grade names.'}
          </span>
        </div>

        {/* Quality histogram chip-row — click-to-narrow. Each chip
            snaps the slider to that band's threshold. The active band
            (i.e. the highest threshold ≤ the current slider value)
            gets a stronger emerald ring so users can see at a glance
            where they currently sit. Round 98b — visible in every
            mode, driven by mode-aware bands from the backend. */}
        {Array.isArray(qualityBands) && qualityBands.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs px-3" data-testid="quality-histogram-row">
            {qualityBands.map((b) => {
              const isActive = minQuality > 0 && minQuality === b.threshold;
              return (
                <button
                  key={b.threshold}
                  type="button"
                  onClick={() => setMinQuality(b.threshold)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                    isActive
                      ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500/60'
                      : 'bg-muted/60 text-foreground hover:bg-emerald-500/15 hover:text-emerald-700 dark:hover:text-emerald-300'
                  }`}
                  data-testid={`quality-histogram-${b.threshold}`}
                  title={`Show only stocks with quality score ${b.threshold}+ (${b.label}-tier and above) — ${b.count} stocks`}
                >
                  <span className="font-bold">{b.threshold}+</span>
                  <span className="opacity-75">{b.label}</span>
                  <span className="font-mono opacity-60">({b.count.toLocaleString()})</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Active Filter Pills */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="active-filters">
            <span className="text-xs text-muted-foreground">Filters:</span>
            {activeFilters.map(f => (
              <span
                key={`${f.cat}-${f.label}`}
                className="inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                data-testid={`active-filter-${f.cat.toLowerCase().replace(/[\s/]+/g, '-')}`}
              >
                {/* Per-pill remove X — capital, red, click clears just
                    THIS filter without touching the others. The Clear
                    button at the end is still there for nuking everything. */}
                {f.onRemove && (
                  <button
                    type="button"
                    onClick={() => (f.restore ? clearFilterWithUndo(f) : f.onRemove())}
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-950/40 transition-colors"
                    aria-label={`Remove ${f.cat} filter`}
                    data-testid={`active-filter-remove-${f.cat.toLowerCase().replace(/[\s/]+/g, '-')}`}
                  >
                    <X className="w-3 h-3" strokeWidth={3} />
                  </button>
                )}
                <span className="font-bold">{f.cat}:</span>{f.label}
              </span>
            ))}
            <Button variant="outline" size="sm" onClick={clearFilters} className="ml-1 h-6 gap-1 text-xs text-red-600 border-red-400 hover:bg-red-50 dark:text-red-400 dark:border-red-500/50 dark:hover:bg-red-950/30" data-testid="clear-filters-btn">
              <X className="w-3 h-3" /> Clear
            </Button>
          </div>
        )}

        {/* Round P136 (Feb 2026) — Leverage risk nudge. Fires when
            EITHER the Structurally-Leveraged CEF filter or the
            Leveraged & Inverse ETF filter is on. Distinct wording for
            each because the underlying mechanic is different (CEFs
            use fund-level borrowing to boost distributions, leveraged
            ETFs use daily-reset swaps and suffer volatility decay). */}
        {leveragedCefFilter === 'only' && (
          <div
            className="flex items-start gap-2 mt-2 px-3 py-2 rounded-md border border-amber-500/60 bg-amber-50 dark:bg-amber-950/25 text-xs sm:text-sm text-amber-900 dark:text-amber-100"
            data-testid="screener-leverage-nudge"
          >
            <Zap className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <span>
              <span className="font-semibold">Leverage cuts both ways.</span>{' '}
              These CEFs borrow (typically 25-45%) to boost distributions — amplifying the yield AND the drawdown. In a credit or rate shock, prices fall faster and distributions may be trimmed.{' '}
              <Link to="/glossary/cef-strategies" className="underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300">
                See the Leverage lens →
              </Link>
            </span>
          </div>
        )}
        {leveragedFilter === 'only' && (
          <div
            className="flex items-start gap-2 mt-2 px-3 py-2 rounded-md border border-orange-500/60 bg-orange-50 dark:bg-orange-950/25 text-xs sm:text-sm text-orange-900 dark:text-orange-100"
            data-testid="screener-leveraged-etf-nudge"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-orange-600 dark:text-orange-400" />
            <span>
              <span className="font-semibold">Leveraged &amp; inverse ETFs decay over time.</span>{' '}
              These funds (2x/3x long, short/inverse) reset their leverage daily using swaps and futures. In choppy markets they lose value even if the underlying ends flat — the classic "volatility drag." They&apos;re short-term trading tools, not long-term dividend positions, and any &quot;yield&quot; is usually a return-of-capital distribution from the swaps rather than real income.
            </span>
          </div>
        )}

        {/* Frequency breakdown chip row — only renders when one of the
            date-range filters is on (Upcoming Ex-Div / Recently Paid Div)
            AND the user hasn't already narrowed by Frequency. Clicking a
            segment sets the All Frequencies dropdown to that frequency
            (which then fires through the normal filter pipeline and adds
            its own pill above). The chip shows the FULL date-window
            breakdown so the segments don't disappear once a click narrows
            the result. */}
        {(nextExWithinDays > 0 || recentlyPaidWithinDays > 0) && frequencyFilter === 'all' && Array.isArray(freqBreakdown) && freqBreakdown.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs" data-testid="freq-breakdown-row">
            <span className="text-muted-foreground">Cadence mix:</span>
            <span className="font-mono font-bold text-foreground">{totalCount} {nextExWithinDays > 0 ? 'upcoming' : 'paid'}</span>
            {freqBreakdown.map((b) => (
              <button
                key={b.frequency}
                type="button"
                onClick={() => setFrequencyFilter(b.frequency)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-muted/60 text-foreground hover:bg-primary/15 hover:text-primary transition-colors"
                data-testid={`freq-breakdown-${b.frequency.toLowerCase().replace(/[\s-]+/g, '-')}`}
                title={`Filter to just ${b.count} ${b.frequency.toLowerCase()} stocks in this window`}
              >
                <span className="font-bold">{b.count}</span>
                <span className="opacity-80">{b.frequency.toLowerCase()}</span>
              </button>
            ))}
          </div>
        )}

        {/* Search bar + Badge Key — sit on the same line on every
            viewport so the Badge Key is always one tap away from the
            search field without adding vertical clutter. The search
            input flexes to fill remaining width; the Badge Key keeps
            its intrinsic width on the right.

            Feb 2026 phone-view cleanup — on mobile (<sm) we move the
            **Live pill + Badge Key + Copy Filter Link** to their own
            row ABOVE the search bar per user request, so the search
            row is dedicated to search / clear buttons only. Desktop
            layout is untouched (all controls stay on the same line). */}
        <div className="flex flex-wrap items-center gap-2 mt-3 sm:hidden" data-testid="screener-mobile-controls-row">
          <LivePriceIndicator status={screenerStreamStatus} />
          <BadgeKey />
          <CopyFilterLinkButton
            buildParams={() => buildSharableFilterParams({
              tierFilter, frequencyFilter, yieldFilter, payoutRatioFilter,
              sectorFilter, marketCapFilter, peRatioFilter, betaFilter,
              debtEquityFilter, roicFilter, fcfMarginFilter, leveragedFilter,
              highExpenseRatioFilter, lowExpenseRatioFilter,
              usesFuturesFilter, isAdrFilter, isBdcFilter, isCloFilter, isCefFilter,
              isReitFilter, dividendChangeFilter, nextExWithinDays,
              recentlyPaidWithinDays, minQuality, sortKey, sortDir,
              cefStrategyFilter,
            })}
          />
        </div>
        <div className="flex items-center gap-2 mt-3" data-testid="search-bar-row">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="screener-search"
              type="text"
              placeholder="Search ticker or name..."
              value={searchInput}
              onChange={(e) => {
                const v = e.target.value;
                setSearchInput(v);              // instant UI feedback
                debouncedSetSearchQuery(v);     // search fires 1.5s after last keystroke
              }}
              className="pl-9 h-9 text-sm border-2 border-amber-700 dark:border-amber-300/70 focus-visible:ring-amber-500/40 focus-visible:border-amber-500"
            />
          </div>
          {/* Feb 2026 — Badge Key + Copy Filter Link show only from sm+;
              on mobile they sit above (see screener-mobile-controls-row). */}
          <span className="hidden sm:contents">
            <BadgeKey />
            <CopyFilterLinkButton
              buildParams={() => buildSharableFilterParams({
                tierFilter, frequencyFilter, yieldFilter, payoutRatioFilter,
                sectorFilter, marketCapFilter, peRatioFilter, betaFilter,
                debtEquityFilter, roicFilter, fcfMarginFilter, leveragedFilter,
                highExpenseRatioFilter, lowExpenseRatioFilter,
                usesFuturesFilter, isAdrFilter, isBdcFilter, isCloFilter, isCefFilter,
                isReitFilter, dividendChangeFilter, nextExWithinDays,
                recentlyPaidWithinDays, minQuality, sortKey, sortDir,
                cefStrategyFilter,
              })}
            />
          </span>
          {/* Round 97c (Jul 2026) — Clear Search + Clear Sort. These
              ONLY reset the search text / column sort respectively.
              Screener filters have their own row-level clear affordance
              in the active-filters chip strip below, so we don't touch
              them here. Buttons stay visible always but grey out when
              there's nothing to clear so users know the affordance
              exists ahead of time. */}
          <button
            type="button"
            onClick={() => { setSearchInput(''); setSearchQuery(''); setPage(1); }}
            disabled={!searchInput && !searchQuery}
            className="inline-flex items-center gap-1 px-2.5 h-9 rounded-md text-xs font-semibold transition-colors bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-muted/60 disabled:hover:text-muted-foreground shrink-0"
            data-testid="screener-clear-search-btn"
            title="Clear the ticker/name search box only. Leaves Screener filters and column sort intact."
          >
            <X className="w-3 h-3" />
            <span className="hidden sm:inline">Clear Search</span>
            <span className="sm:hidden">Search</span>
          </button>
          <button
            type="button"
            onClick={() => { setSortKey('name'); setSortDir('asc'); setPage(1); }}
            disabled={sortKey === 'name' && sortDir === 'asc'}
            className="inline-flex items-center gap-1 px-2.5 h-9 rounded-md text-xs font-semibold transition-colors bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-muted/60 disabled:hover:text-muted-foreground shrink-0"
            data-testid="screener-clear-sort-btn"
            title="Reset column sort back to default (Name ascending). Leaves Screener filters and search intact."
          >
            <X className="w-3 h-3" />
            <span className="hidden sm:inline">Clear Sort</span>
            <span className="sm:hidden">Sort</span>
          </button>
        </div>
      </div>

      {/* Results Table */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-0">
        {loading && stocks.length === 0 ? (
          <div className="rounded-lg border overflow-x-auto md:overflow-hidden" data-testid="screener-loading">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-8 px-2"></TableHead>
                  <TableHead className="font-bold text-xs">Name<RefreshCw className="w-3.5 h-3.5 animate-spin text-foreground inline-block ml-2 -mt-0.5" data-testid="screener-loading-spinner" aria-label="Loading stocks" /></TableHead>
                  <TableHead className="font-bold text-xs text-right">Price</TableHead>
                  {dividendFilter !== 'dividend' && <TableHead className="font-bold text-xs text-right">Mkt Cap</TableHead>}
                  {dividendFilter !== 'non_dividend' && <TableHead className="font-bold text-xs text-right">Yield</TableHead>}
                  {dividendFilter === 'dividend' && <TableHead className="font-bold text-xs text-right">Div/Yr</TableHead>}
                  {dividendFilter !== 'non_dividend' && <TableHead className="font-bold text-xs text-center">Payout</TableHead>}
                  {dividendFilter === 'non_dividend' && <TableHead className="font-bold text-xs text-center">Altman Z</TableHead>}
                  <TableHead className="font-bold text-xs text-center">Quality</TableHead>
                  {dividendFilter === 'non_dividend' && <TableHead className="font-bold text-xs text-right">Rev Growth</TableHead>}
                  <TableHead className="font-bold text-xs text-right ">1 Mo</TableHead>
                  <TableHead className="font-bold text-xs text-right ">1 Yr</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <TableCell key={j}><div className="h-4 rounded shimmer w-16" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : stocks.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="screener-no-results">
            <Filter className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-lg font-semibold">No stocks match your filters</p>
            <p className="text-sm mt-1 font-semibold">Try adjusting your criteria or clearing filters</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>Clear All Filters</Button>
          </div>
        ) : (
          <>
          {minerviniSepa && (
            <div
              className="mb-3 rounded-lg border border-sky-300/60 dark:border-sky-800/60 bg-sky-50/70 dark:bg-sky-950/30 px-3 py-2 text-xs sm:text-sm leading-relaxed"
              data-testid="minervini-explainer"
            >
              <span className="font-bold text-sky-900 dark:text-sky-200">Minervini SEPA Lite.</span>{' '}
              Non-dividend common stocks passing all 8 rules of Mark Minervini&apos;s <AbbrTip term="Trend Template">Trend Template</AbbrTip> (Stage-2 uptrend) with latest-quarter EPS up ≥20% year over year — the automatable half of his <AbbrTip term="SEPA">SEPA</AbbrTip> method. Sorted by relative-strength rank; a <span className="font-semibold text-amber-700 dark:text-amber-300">Tight base</span> badge flags <AbbrTip term="VCP">VCP</AbbrTip>-style contraction, which still needs a chart check. Recalculated nightly after the close.{' '}
              <Link to="/learn/minervini-sepa" className="font-semibold text-amber-600 dark:text-amber-400 hover:underline" data-testid="minervini-learn-link">Read the guide →</Link>
            </div>
          )}
          <div className="rounded-lg border overflow-x-auto md:overflow-hidden" data-testid="screener-table">
            {/* Round 96n (Jul 3 2026) — Real vs Nominal Yield display
                toggle for the Screener. NOTE: This is DISPLAY-ONLY.
                The Yield Range filter and yield sorting both continue
                to run against nominal yield on the backend, so
                flipping this toggle never breaks or shifts your
                screener's saved settings. Hidden in Non-Dividend
                mode — those stocks have no yield to display, so the
                Nominal/Real toggle is irrelevant + visually confusing.
                Round P144 (Feb 2026) — The bar also now hosts the
                "Allow SPAC & Warrant" checkbox on the far left, so
                it renders even in Non-Dividend mode when the checkbox
                needs a home. */}
            <div className="flex flex-wrap items-center justify-between px-2 sm:px-3 py-1.5 border-b border-border/40 dark:border-white/[0.04] bg-muted/20 gap-1.5" data-testid="screener-yield-mode-toggle">
              {/* Round P144 — "Allow SPAC & Warrant" checkbox lives
                  on the LEFT. Auto-checks when the SPAC/Warrants
                  fund-type filter is active; uncheck to hide both
                  from the results universe. Session-persisted.
                  Hidden in pure Dividend mode — SPACs and warrants
                  never pay dividends so the checkbox has nothing
                  to do there. */}
              {dividendFilter !== 'dividend' && (
              <label
                className={`inline-flex items-center gap-1.5 select-none mr-auto ${anomalyLocksFunds ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid="allow-spacs-warrants-toggle"
                data-locked={anomalyLocksFunds ? 'true' : 'false'}
                title={anomalyLocksFunds ? ANOMALY_LOCK_TIP : "Show SPACs (blank-check companies) and Warrants in this screener. Off by default so a low-to-high price sort surfaces real stocks instead of a wall of $0.00 SPAC units. Auto-checks when you pick the SPAC or Warrants fund type."}
              >
                <input
                  type="checkbox"
                  checked={!!allowSpacsWarrants}
                  disabled={anomalyLocksFunds}
                  onChange={(e) => { setAllowSpacsWarrants(e.target.checked); if (!e.target.checked) setSpacStageFilter('all'); }}
                  className="w-3.5 h-3.5 rounded border-border/60 cursor-pointer accent-primary disabled:cursor-not-allowed"
                  data-testid="allow-spacs-warrants-checkbox"
                />
                {/* Round P144·1 — Compact label on mobile so it fits
                    alongside the yield-mode pill without wrapping. */}
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">
                  Allow: SPAC &amp; Warrant
                </span>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground sm:hidden">
                  SPAC/Warrant
                </span>
              </label>
              )}
              {/* Round P157 — SPAC lifecycle-stage filter. All /
                  Non-Dividend modes only. Disabled until the Allow SPAC & Warrant
                  toggle is ticked; unticking resets it to Any. */}
              {dividendFilter !== 'dividend' && (
                <select
                  value={spacStageFilter}
                  disabled={anomalyLocksFunds || !allowSpacsWarrants || mmfTypeFilter !== 'all' || screenerFilterActive}
                  onChange={(e) => setSpacStageFilter(e.target.value)}
                  className="text-[10px] uppercase tracking-wide bg-background border border-border/60 rounded-md px-1.5 py-1 text-muted-foreground cursor-pointer hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-muted-foreground"
                  title={anomalyLocksFunds ? ANOMALY_LOCK_TIP : !allowSpacsWarrants ? 'Tick "Allow: SPAC & Warrants" first to pick a SPAC stage.' : screenerFilterActive ? 'Greyed out while screener filters are active — clear the filters to pick a SPAC stage.' : mmfTypeFilter !== 'all' ? 'Greyed out while an MMF Type is selected — a stock can\'t be both a SPAC and a money market fund. Set MMF Type back to Any to use this.' : "Filter SPACs by lifecycle stage (from the weekly SEC EDGAR sweep): Searching for a target, Deal Announced (425/S-4 on file), Vote Pending (merger proxy filed — redemption deadline coming), Ticker Changed (renamed pre-close), Completed (de-SPAC closed), or Liquidated."}
                  data-testid="spac-stage-filter"
                >
                  <option value="all">SPAC Stage: Any</option>
                  <option value="searching">Searching</option>
                  <option value="deal_announced">Deal Announced</option>
                  <option value="vote_pending">Vote Pending</option>
                  <option value="ticker_changed">Ticker Changed</option>
                  <option value="completed">Completed</option>
                  <option value="liquidated">Liquidated</option>
                </select>
              )}
              {/* Sep 2026 — 2nd line: "Allow: Money Market Funds" checkbox
                  (left) + MMF Type dropdown (right). Dividend + All modes.
                  Picking a type auto-checks the checkbox so results show. */}
              {dividendFilter !== 'non_dividend' && (
                <div className={`flex items-center gap-1.5 ${dividendFilter === 'all' ? 'order-last basis-full' : 'mr-auto'}`} data-testid="allow-mmf-row">
                  <label
                    className={`inline-flex items-center gap-1.5 select-none mr-auto ${anomalyLocksFunds ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid="allow-mmf-toggle"
                    data-locked={anomalyLocksFunds ? 'true' : 'false'}
                    title={anomalyLocksFunds ? ANOMALY_LOCK_TIP : "Show Money Market Funds (SPAXX, VMFXX…) and open-end mutual funds (VTSAX, FXAIX…) in this screener. Off by default: MMFs have no price change, market cap or payout ratio, so a lowest-first sort on those columns surfaced a wall of “—” rows. The MMF Type dropdown unlocks once this is ticked."}
                  >
                    <input
                      type="checkbox"
                      checked={!!allowMmf}
                      disabled={anomalyLocksFunds}
                      onChange={(e) => { setAllowMmf(e.target.checked); if (!e.target.checked) setMmfTypeFilter('all'); }}
                      className="w-3.5 h-3.5 rounded border-border/60 cursor-pointer accent-primary disabled:cursor-not-allowed"
                      data-testid="allow-mmf-checkbox"
                    />
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">
                      Allow: Money Market &amp; Mutual Funds
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground sm:hidden">
                      MMF/Mutual
                    </span>
                  </label>
                  <select
                    value={mmfTypeFilter}
                    disabled={anomalyLocksFunds || !allowMmf || spacStageFilter !== 'all' || screenerFilterActive}
                    onChange={(e) => setMmfTypeFilter(e.target.value)}
                    className={`text-[10px] uppercase tracking-wide bg-background border border-border/60 rounded-md px-1.5 py-1 text-muted-foreground cursor-pointer hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${dividendFilter === 'all' ? 'ml-auto' : ''}`}
                    title={anomalyLocksFunds ? ANOMALY_LOCK_TIP : !allowMmf ? 'Tick "Allow: Money Market Funds" first to pick an MMF type.' : screenerFilterActive ? 'Greyed out while screener filters are active — clear the filters to pick an MMF type.' : spacStageFilter !== 'all' ? 'Greyed out while a SPAC Stage is selected — a stock can\'t be both a SPAC and a money market fund. Set SPAC Stage back to Any to use this.' : "Filter Money Market Funds by what they hold: Government (Treasuries + agency debt + repo — the default sweep type), Treasury (T-bills only — state-tax-free interest), Prime (adds bank CDs and commercial paper for a bit more yield), Municipal (tax-exempt muni paper)."}
                    data-testid="mmf-type-filter"
                  >
                    <option value="all">MMF Type: Any</option>
                    <option value="Government">Government</option>
                    <option value="Treasury">Treasury</option>
                    <option value="Prime">Prime</option>
                    <option value="Municipal">Municipal</option>
                  </select>
                </div>
              )}
              {dividendFilter !== 'non_dividend' && !mmfView && (
                <>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">Yield:</span>
              <div className="flex items-center bg-background rounded-md p-0.5 border border-border/60" role="tablist" aria-label="Yield display mode">
                <button
                  type="button"
                  onClick={() => setYieldMode('nominal')}
                  className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${effectiveYieldMode === 'nominal' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  title="Show nominal dividend yield — the raw annual dividend ÷ price percentage. This is what the Yield Range filter uses."
                  data-testid="screener-yield-mode-nominal"
                >
                  Nominal
                </button>
                <button
                  type="button"
                  onClick={() => setYieldMode('real')}
                  disabled={typeof cpiYoy !== 'number'}
                  className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${effectiveYieldMode === 'real' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'} disabled:opacity-40 disabled:cursor-not-allowed`}
                  title={
                    typeof cpiYoy === 'number'
                      ? `Real Yield = Nominal − CPI YoY (${cpiYoy.toFixed(2)}%). DISPLAY ONLY — your Yield Range filter and yield sorting still run against nominal yield, so your saved screener settings are unaffected.`
                      : "Real Yield unavailable — CPI YoY still loading."
                  }
                  data-testid="screener-yield-mode-real"
                >
                  Real{typeof cpiYoy === 'number' ? ` (−${cpiYoy.toFixed(1)}%)` : ''}
                </button>
              </div>
                </>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-8 px-2"></TableHead>
                  <TableHead className="font-bold text-xs uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors min-w-[160px]" onClick={() => handleSort('name')} data-testid="sort-name">
                    {mmfView ? 'Fund' : 'Name'}{sortKey === 'name' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    {loading && <RefreshCw className="w-3.5 h-3.5 animate-spin text-foreground inline-block ml-2 -mt-0.5" data-testid="screener-loading-spinner" aria-label="Loading stocks" />}
                  </TableHead>
                  {minerviniSepa && (
                    <TableHead
                      className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
                      onClick={() => handleSort('minervini_stage2')}
                      title="Scan days the stock has held all 8 Trend Template rules (Stage 2). Sort to find fresh breakouts or long-running leaders."
                      data-testid="screener-sort-stage2"
                    >
                      Stage 2{sortKey === 'minervini_stage2' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  <TableHead className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('price')} data-testid="sort-price" {...(mmfView ? { title: 'Net asset value. A money market fund is run to hold exactly $1.00 — a print away from a buck is the thing to look at, not the yield.' } : headerTip(HEADER_TIPS.price))}>
                    {mmfView ? 'NAV' : 'Price'}{sortKey === 'price' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                  </TableHead>
                  {/* Sep 2026 — CLO peer Sharpe column. Only rendered
                      when the CLO ETFs fund-type filter is active. Sort
                      is client-side (Mongo doesn't know clo_sharpe). */}
                  {cloView && (
                    <TableHead
                      className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
                      onClick={() => handleSort('clo_sharpe')}
                      data-testid="sort-clo-sharpe"
                      title="3-year Sharpe ratio — annualized total return (dividends reinvested) minus the current 3-year Treasury yield, divided by annualized volatility. Above 1.0 = each 1% of price volatility bought >1% of excess return. Above 1.5 = excellent risk efficiency. Sourced from Massive daily closes + our dividend cache. Falls back to 2y or 1y for funds younger than 3 years."
                    >
                      3yr Sharpe{sortKey === 'clo_sharpe' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  {/* Sep 2026 — money-market column set: family, category,
                      7-day SEC yield, expense ratio, fund size and weekly
                      liquid assets, matching the hub page exactly. Family
                      and Type are derived server-side from the registrant
                      name so they aren't Mongo-sortable — no click. */}
                  {mmfView && (
                    <>
                      <TableHead className="font-bold text-xs uppercase tracking-wider" data-testid="mmf-head-family">Family</TableHead>
                      <TableHead className="font-bold text-xs uppercase tracking-wider" data-testid="mmf-head-type">Type</TableHead>
                      <TableHead
                        className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
                        onClick={() => handleSort('mmf_yield')}
                        data-testid="sort-mmf-yield"
                        title="7-day SEC yield — the annualised net yield over the last seven days, after fees. The only yield measure that compares money funds honestly."
                      >
                        7-Day Yield{sortKey === 'mmf_yield' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                      </TableHead>
                      <TableHead
                        className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors"
                        onClick={() => handleSort('mmf_expense')}
                        data-testid="sort-mmf-expense"
                        title="Annual expense ratio from the fund's shareholder report. Money funds all hold the same short paper, so the fee is most of what separates their yields."
                      >
                        Expense{sortKey === 'mmf_expense' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                      </TableHead>
                      <TableHead
                        className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
                        onClick={() => handleSort('mmf_size')}
                        data-testid="sort-mmf-size"
                        title="Net assets from the fund's monthly SEC Form N-MFP filing."
                      >
                        Fund Size{sortKey === 'mmf_size' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                      </TableHead>
                      <TableHead
                        className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
                        onClick={() => handleSort('mmf_liquid')}
                        data-testid="sort-mmf-liquid"
                        title="Weekly liquid assets — the share of the portfolio convertible to cash within five business days. Government funds run 70-100%; the regulatory floor is 50% for the rest."
                      >
                        Weekly Liquid{sortKey === 'mmf_liquid' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                      </TableHead>
                    </>
                  )}
                  {/* Oct 2026 — Preferred Shares column set. */}
                  {prefView && (
                    <>
                      <TableHead
                        className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors"
                        onClick={() => handleSort('pref_par')}
                        data-testid="sort-pref-par"
                        title="Par (liquidation) value — the price the issuer must pay to redeem the share. Usually $25; Strategy's treasury-vehicle preferreds use $100."
                      >
                        Par{sortKey === 'pref_par' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                      </TableHead>
                      <TableHead
                        className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors"
                        onClick={() => handleSort('pref_coupon')}
                        data-testid="sort-pref-coupon"
                        title="Stated annual coupon on par. Yield (next column) is the same dollars divided by today's market price."
                      >
                        Coupon{sortKey === 'pref_coupon' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                      </TableHead>
                      <TableHead
                        className="font-bold text-xs uppercase tracking-wider text-right whitespace-nowrap"
                        data-testid="pref-head-vs-par"
                        title="Market price minus par. Above par + callable now = a redemption hands you an instant loss; below par = a call pays you the difference."
                      >
                        vs Par
                      </TableHead>
                      <TableHead
                        className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
                        onClick={() => handleSort('pref_call')}
                        data-testid="sort-pref-call"
                        title="First (or next) date the issuer may redeem at par — from the prospectus, admin-confirmed. 'Callable now' = protection has ended. 'Issuer's choice' = redeemable any time on notice."
                      >
                        Call Date{sortKey === 'pref_call' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                      </TableHead>
                      <TableHead className="font-bold text-xs uppercase tracking-wider" data-testid="pref-head-type" title="Cumulative (missed dividends accrue) vs Non-Cumulative (skipped = lost), Fixed-to-Floating (rate resets to a benchmark + spread), Term (mandatory redemption year), Treasury Vehicle (ROC-funded).">Type</TableHead>
                    </>
                  )}
                  {/* Round 97b (Jul 2026) — Mode-aware column set. Adds MKT
                      CAP to All + Non-Div. Non-Div swaps YIELD/DIV-YR/PAYOUT
                      for ALTMAN Z + REV GROWTH. Dividend mode unchanged. */}
                  {dividendFilter !== 'dividend' && !altView && (
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('market_cap')} data-testid="sort-market-cap" title="Market capitalization (Price × Shares Outstanding). Click to sort.">
                      Mkt Cap{sortKey === 'market_cap' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  {dividendFilter !== 'non_dividend' && !mmfView && (
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('yield')} data-testid="sort-yield" {...headerTip(effectiveYieldMode === 'real' && typeof cpiYoy === 'number' ? `Real Yield = nominal dividend yield minus current CPI YoY (${cpiYoy.toFixed(2)}%). Display-only overlay — sort + Yield Range filter still use nominal yield.` : HEADER_TIPS.yield)}>
                      {effectiveYieldMode === 'real' ? 'Real Yield' : 'Yield'}{sortKey === 'yield' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  {prefView && (
                    <TableHead
                      className="font-bold text-xs uppercase tracking-wider text-right whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                      onClick={() => handleSort('pref_ytc')}
                      data-testid="pref-head-ytc"
                      title="Yield to call — your annualized return if the issuer redeems at par on the call date (coupons + pull-to-par). Below current yield = you paid an above-par premium that a call wipes out. 'Now' = already callable: the figure is the instant gain/loss if redeemed today. Click to sort (computed server-side across the whole list)."
                    >
                      Yield to Call{sortKey === 'pref_ytc' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  {dividendFilter === 'dividend' && !altView && (
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('divyr')} data-testid="sort-divyr" {...headerTip(HEADER_TIPS.divyr)}>
                      Div/Yr{sortKey === 'divyr' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  {dividendFilter !== 'non_dividend' && !altView && (
                    <TableHead className="font-bold text-xs uppercase tracking-wider text-center cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('payout')} data-testid="sort-payout" {...headerTip(HEADER_TIPS.payout)}>
                      Payout{sortKey === 'payout' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  {dividendFilter === 'non_dividend' && (
                    <TableHead
                      className="font-bold text-xs uppercase tracking-wider text-center cursor-pointer select-none hover:text-foreground transition-colors"
                      onClick={() => handleSort('altman_z')}
                      data-testid="sort-altman-z"
                      title="Altman Z-Score — bankruptcy risk model. Z>2.99=SAFE (green), 1.81<Z≤2.99=GRAY (amber), Z≤1.81=DISTRESS (red). Higher = safer. Click to sort SAFE→DISTRESS or DISTRESS→SAFE."
                    >
                      Altman Z{sortKey === 'altman_z' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  {!altView && (
                  <TableHead
                    className="font-bold text-xs uppercase tracking-wider text-center cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => handleSort('quality')}
                    data-testid="sort-quality"
                    title={dividendFilter === 'non_dividend' ? 'Non-Dividend Quality Score (0–100). Growth-tier tier badge (G-S/A/B/C/SPEC). Sort desc = best-first.' : 'Dividend Quality Score (0–100). Server-side sort across all stocks.'}
                  >
                    Quality{sortKey === 'quality' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                  </TableHead>
                  )}
                  {/* Jul 2026 — Coverage column, visible only when a
                      coverage filter (Coverage Watch preset) is active.
                      Header ⓘ + cells open tap popups (phone-safe;
                      native title tooltips are dead on touch). */}
                  {coverageColumnActive && !altView && (
                    <TableHead
                      className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
                      onClick={() => handleSort('coverage')}
                      data-testid="sort-coverage"
                    >
                      Coverage{sortKey === 'coverage' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                      <CoverageHeaderTip />
                    </TableHead>
                  )}
                  {/* Sep 2026 — BDC preset "NAV Growth" column. Only
                      renders when the user has selected "Only BDCs"
                      under Fund Types. Ranks BDCs by 5-quarter change
                      in NAV per share (loan-book health signal). */}
                  {bdcView && !altView && (
                    <TableHead
                      className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
                      onClick={() => handleSort('bdc_nav_growth')}
                      data-testid="sort-bdc-nav-growth"
                      title="Book / NAV growth over the trailing 5 quarters. Positive = loan book expanding (healthy for a BDC). Negative = shrinking book, watch NII coverage. Sort desc = fastest expanders first."
                    >
                      NAV Growth{sortKey === 'bdc_nav_growth' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  {dividendFilter === 'non_dividend' && !altView && (
                    <TableHead
                      className="font-bold text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-foreground transition-colors"
                      onClick={() => handleSort('rev_growth')}
                      data-testid="sort-rev-growth"
                      title="Revenue 3-year CAGR — average annual revenue growth over the last 3 years. Sparkline shows the 7-year revenue history. Sort desc = fastest growers first."
                    >
                      Rev Growth{sortKey === 'rev_growth' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </TableHead>
                  )}
                  {!altView && (
                  <TableHead className="font-bold text-xs uppercase tracking-wider text-right  cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('perf_1m')} data-testid="sort-1mo" {...headerTip(HEADER_TIPS.perf_1m)}>
                    1 Mo{sortKey === 'perf_1m' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                  </TableHead>
                  )}
                  {!mmfView && (
                  <TableHead className="font-bold text-xs uppercase tracking-wider text-right  cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('perf_1y')} data-testid="sort-1yr" {...headerTip(HEADER_TIPS.perf_1y)}>
                    1 Yr{sortKey === 'perf_1y' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                  </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  // Server applies Min Quality filter now (round 29
                  // backfill). Quality sort is handled server-side too
                  // (sort_by=quality). This block is intentionally
                  // a passthrough — leaves room for any future
                  // page-only post-processing without disturbing the
                  // call-site below.
                  //
                  // Sep 2026 — client-side sort for the CLO Sharpe
                  // column. Mongo doesn't know the computed sharpe,
                  // so when that sort is active we reorder the visible
                  // page by the bulk metrics map. Sharpe-less rows
                  // sink to the bottom regardless of direction so the
                  // ranked funds stay together at the top.
                  if (cloView && sortKey === 'clo_sharpe' && cloBulk) {
                    const dir = sortDir === 'asc' ? 1 : -1;
                    return [...stocks].sort((a, b) => {
                      const sa = cloBulk[a.ticker]?.sharpe;
                      const sb = cloBulk[b.ticker]?.sharpe;
                      if (sa == null && sb == null) return 0;
                      if (sa == null) return 1;
                      if (sb == null) return -1;
                      return (sa - sb) * dir;
                    });
                  }
                  return stocks;
                })().map(stock => {
                  const inWatchlist = watchlist.includes(stock.ticker);
                  return (
                    <TableRow
                      key={stock.ticker}
                      className="cursor-pointer group hover:bg-muted/50 transition-all"
                      onClick={() => handleStockClick(stock.ticker)}
                      data-testid={`screener-row-${stock.ticker}`}
                    >
                      <TableCell className="px-2 py-3 w-8">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleWatchlist(stock.ticker); }}
                          className="p-0.5"
                          data-testid={`screener-star-${stock.ticker}`}
                        >
                          <Star className={`w-4 h-4 transition-colors ${inWatchlist ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground/40 hover:text-muted-foreground'}`} />
                        </button>
                      </TableCell>
                      <TableCell className="py-3">
                        {mmfView ? (
                          <div data-testid={`screener-mmf-fund-${stock.ticker}`}>
                            <span className="font-mono text-xs text-yellow-600 dark:text-yellow-400 font-bold block">{stock.ticker}</span>
                            <span className="text-sm text-muted-foreground block group-hover:text-primary transition-colors" title={stock.mmf_name || stock.name}>
                              {(() => {
                                const n = stock.mmf_name || stock.name || '';
                                return n.length > 46 ? n.slice(0, 43) + ' ...' : n;
                              })()}
                            </span>
                          </div>
                        ) : (
                        <div>
                          <span className="font-semibold text-sm block group-hover:text-primary transition-colors" title={stock.name}>
                            {stock.name && stock.name.length > 28 ? stock.name.slice(0, 25) + ' ...' : (stock.name || stock.ticker)}
                          </span>
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                            <span className="font-mono text-xs text-yellow-600 dark:text-yellow-400 font-bold">{stock.ticker}</span>
                            <TierBadge tier={stock.dividend_tier} />
                            {/* Round 97a fix (Jul 2026) — Non-Div rows
                                now surface the Growth Quality Tier
                                badge (`non_dividend_quality_tier`)
                                instead of the dividend QualityBadge,
                                which was leaking "37/100 D-WEAK" style
                                tooltips onto non-div rows in the
                                Non-Dividend Screener view. Mirrors the
                                same isNonDiv branch used by StockTable
                                so both surfaces stay consistent. */}
                            {stock.pays_dividend === false ? (
                              stock?.equity_quality?.rank != null && (
                                <CapitalScoreBadge
                                  rank={stock.equity_quality.rank}
                                  verdict={stock.equity_quality.verdict}
                                  verdictLabel={stock.equity_quality.verdict_label}
                                  size="xs"
                                  ticker={stock.ticker}
                                  testIdSuffix={`screener-${stock.ticker}`}
                                />
                              )
                            ) : (
                              <QualityBadge stock={stock} size="xs" interactive testIdSuffix={`screener-${stock.ticker}`} />
                            )}
                            {/* Round 97l (Feb 2026) — Founder Stake
                                Detector. Form 3 baseline holding
                                exceeds a founder-level threshold.
                                Gated to admin while the Insider tab
                                paywall is still admin-only — swap to
                                `user?.role === 'admin' || user?.is_pro`
                                once the non-div paywall ships. */}
                            {user?.role === 'admin' && stock.form3_founder_stake?.detected && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStockClick(stock.ticker, 'insider-activity');
                                }}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-300/60 dark:border-sky-700/60 hover:brightness-110 cursor-pointer"
                                title={(() => {
                                  const fs = stock.form3_founder_stake;
                                  const owner = fs.top_owner || 'Top insider';
                                  const role = fs.top_role || 'Insider';
                                  const sharesK = fs.top_shares
                                    ? fs.top_shares >= 1_000_000
                                      ? `${(fs.top_shares / 1_000_000).toFixed(1)}M`
                                      : `${(fs.top_shares / 1_000).toFixed(0)}K`
                                    : null;
                                  const pct = fs.top_pct != null
                                    ? ` (${(fs.top_pct * 100).toFixed(1)}% of float)`
                                    : '';
                                  return `Founder Stake Detector — ${owner} (${role}) holds ${sharesK || '—'} baseline shares${pct}. Click to view the full Initial Insider Positions table.`;
                                })()}
                                data-testid={`screener-founder-stake-badge-${stock.ticker}`}
                              >
                                <Flag className="w-2.5 h-2.5" />
                                Founder
                              </button>
                            )}
                            {minerviniSepa && stock.minervini && (
                              <MinerviniBadge ticker={stock.ticker} m={stock.minervini} kind="rs" />
                            )}
                            {minerviniSepa && stock.minervini?.tight_base && (
                              <MinerviniBadge ticker={stock.ticker} m={stock.minervini} kind="tight" />
                            )}
                            {stock.is_leveraged && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 border border-red-300/60 dark:border-red-800/60"
                                title="Leveraged / inverse ETF — amplified risk; not suitable for long-term dividend investing"
                                data-testid={`screener-leveraged-badge-${stock.ticker}`}
                              >
                                <AlertTriangle className="w-2.5 h-2.5" />
                                Leveraged
                              </span>
                            )}
                            {stock.uses_futures && !stock.is_leveraged && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/60 dark:border-amber-800/60"
                                title="Futures-based fund — uses derivatives for exposure. Carries margin/leverage risk and roll costs; returns can diverge from the underlying over time."
                                data-testid={`screener-futures-badge-${stock.ticker}`}
                              >
                                <AlertTriangle className="w-2.5 h-2.5" />
                                Futures
                              </span>
                            )}
                            {stock.is_adr && (
                              <span
                                className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-pink-100 text-red-800 dark:bg-pink-200/90 dark:text-red-900 border border-red-300/60 dark:border-red-400/60"
                                title="American Depositary Receipt — U.S.-listed certificate representing shares of a foreign company. Trades and pays dividends in USD."
                                data-testid={`screener-adr-badge-${stock.ticker}`}
                              >
                                ADR
                              </span>
                            )}
                            {stock.is_bdc && (
                              <span
                                className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-300/60 dark:border-indigo-700/60"
                                title="Business Development Company — closed-end fund that loans to small/mid-sized private businesses. By law, distributes 90%+ of taxable income; yields of 8-12% are normal."
                                data-testid={`screener-bdc-badge-${stock.ticker}`}
                              >
                                BDC
                              </span>
                            )}
                            {/* Round P121 (Feb 2026) — SPAC pill on
                                screener rows so blank-check cash
                                shells are visually distinct from
                                real dividend stocks. */}
                            {stock.is_spac && (
                              <span
                                className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-slate-200 text-slate-800 dark:bg-slate-800/70 dark:text-slate-200 border border-slate-400/60 dark:border-slate-600/60"
                                title="Blank-Check / SPAC — Special Purpose Acquisition Company. A cash shell listed to acquire a private business. Pays NO dividends."
                                data-testid={`screener-spac-badge-${stock.ticker}`}
                              >
                                SPAC
                              </span>
                            )}
                            {stock.is_cef && (
                              <span
                                className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border border-teal-300/60 dark:border-teal-700/60"
                                title="Closed-End Fund — exchange-traded fund with a fixed share count that can trade at a premium or discount to NAV. Often uses leverage; many distribute monthly with high yields."
                                data-testid={`screener-cef-badge-${stock.ticker}`}
                              >
                                CEF
                              </span>
                            )}
                            <LeveragedCefIcon
                              ticker={stock.ticker}
                              isCef={!!stock.is_cef}
                              testIdPrefix="screener-lev-cef-badge"
                            />
                            {stock.is_reit && (
                              <span
                                className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300/60 dark:border-emerald-700/60"
                                title="Real Estate Investment Trust — company that owns, operates, or finances income-producing real estate. Required by law to distribute 90%+ of taxable income, so yields of 4-8% are typical."
                                data-testid={`screener-reit-badge-${stock.ticker}`}
                              >
                                REIT
                              </span>
                            )}
                            <DividendChangeBadge stock={stock} size="sm" />
                            {!prefView && <PreferredCallRiskChip stock={stock} onOpenTicker={(t) => handleStockClick(t, 'insights')} />}
                          </div>
                        </div>
                        )}
                      </TableCell>
                      {minerviniSepa && (
                        <TableCell className="text-right font-mono text-sm whitespace-nowrap" data-testid={`screener-stage2-${stock.ticker}`}>
                          {stock.minervini?.trend_template_pass
                            ? <span title={stock.minervini.stage2_since ? `Held Stage 2 since ${stock.minervini.stage2_since}` : ''}>{stock.minervini.stage2_days || 1}<span className="text-[10px] text-muted-foreground ml-0.5">d</span></span>
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      )}
                      <TableCell className="text-right font-mono text-sm">
                        {stock.price ? (
                          <span>
                            ${stock.price.toFixed(2)}
                          </span>
                        ) : '--'}
                      </TableCell>
                      {/* Sep 2026 — CLO Sharpe cell. Matches the CLO
                          Sharpe column added in the header when
                          isCloFilter === 'only'. Colored by Morningstar-
                          style verdict: ≥1.5 excellent (emerald), ≥1
                          good (emerald), ≥0.5 adequate (amber),
                          negative (rose). Suffix shows the window used
                          when it isn't 3y (e.g., "2y" for younger
                          funds). */}
                      {cloView && (
                        <TableCell className="text-right font-mono text-sm whitespace-nowrap" data-testid={`screener-clo-sharpe-${stock.ticker}`}>
                          {(() => {
                            const m = cloBulk?.[stock.ticker];
                            if (!m || m.sharpe == null) return <span className="text-muted-foreground/50">—</span>;
                            const s = m.sharpe;
                            const tone = s >= 1.5 ? 'text-emerald-600 dark:text-emerald-400 font-bold'
                              : s >= 1 ? 'text-emerald-600 dark:text-emerald-400'
                              : s >= 0.5 ? 'text-amber-600 dark:text-amber-400'
                              : s >= 0 ? 'text-amber-600/70 dark:text-amber-400/70'
                              : 'text-rose-600 dark:text-rose-400';
                            const winTag = m.window_years !== 3 ? <span className="text-[9px] text-muted-foreground ml-1">{m.window_years}y</span> : null;
                            return <><span className={tone}>{s.toFixed(2)}</span>{winTag}</>;
                          })()}
                        </TableCell>
                      )}
                      {/* Sep 2026 — money-market cells, same measures as
                          the hub page. Values arrive pre-cleaned from the
                          screener endpoint via services/mmf_hub.py. */}
                      {mmfView && (
                        <>
                          <TableCell className="text-sm" data-testid={`screener-mmf-family-${stock.ticker}`}>
                            {stock.mmf_family_label || <span className="text-muted-foreground/50">—</span>}
                          </TableCell>
                          <TableCell data-testid={`screener-mmf-type-${stock.ticker}`}>
                            <span className={`inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide border ${
                              stock.mmf_category === 'Treasury'
                                ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60'
                                : stock.mmf_category === 'Government'
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60'
                                : stock.mmf_category === 'Municipal'
                                ? 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300 border-violet-300/60 dark:border-violet-700/60'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300/60 dark:border-amber-700/60'
                            }`}>
                              {stock.mmf_category || '—'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm" data-testid={`screener-mmf-yield-${stock.ticker}`}>
                            {stock.mmf_yield != null ? (
                              <span className="text-green-600 dark:text-green-400 font-medium">{stock.mmf_yield.toFixed(2)}%</span>
                            ) : <span className="text-muted-foreground/50">—</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm" data-testid={`screener-mmf-expense-${stock.ticker}`}>
                            {stock.mmf_expense_ratio != null
                              ? `${stock.mmf_expense_ratio.toFixed(2)}%`
                              : <span className="text-muted-foreground/50" title="No expense ratio in the fund's latest shareholder report on file.">—</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm" data-testid={`screener-mmf-size-${stock.ticker}`}>
                            {(() => {
                              const a = stock.mmf_size;
                              if (a == null) return <span className="text-muted-foreground/50">—</span>;
                              return a >= 1e9 ? `$${(a / 1e9).toFixed(1)}B`
                                : a >= 1e6 ? `$${(a / 1e6).toFixed(0)}M`
                                : `$${(a / 1e3).toFixed(0)}K`;
                            })()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm" data-testid={`screener-mmf-liquid-${stock.ticker}`}>
                            {stock.mmf_weekly_liquid_pct != null ? (
                              <span className={
                                stock.mmf_weekly_liquid_pct >= 90 ? 'text-green-600 dark:text-green-400 font-medium'
                                  : stock.mmf_weekly_liquid_pct >= 60 ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-foreground/70'
                              }>
                                {Math.round(stock.mmf_weekly_liquid_pct)}%
                              </span>
                            ) : <span className="text-muted-foreground/50">—</span>}
                          </TableCell>
                        </>
                      )}
                      {prefView && <PreferredCells stock={stock} />}
                      {/* Round 97b — MKT CAP column, All + Non-Div only. */}
                      {dividendFilter !== 'dividend' && !altView && (
                        <TableCell className="text-right font-mono text-sm" data-testid={`screener-mktcap-${stock.ticker}`}>
                          {(() => {
                            const mc = stock.market_cap;
                            if (mc == null || mc <= 0) return <span className="text-muted-foreground/50">--</span>;
                            const fmt = mc >= 1e12 ? `$${(mc / 1e12).toFixed(2)}T`
                              : mc >= 1e9 ? `$${(mc / 1e9).toFixed(2)}B`
                              : mc >= 1e6 ? `$${(mc / 1e6).toFixed(1)}M`
                              : `$${(mc / 1e3).toFixed(0)}K`;
                            return <span>{fmt}</span>;
                          })()}
                        </TableCell>
                      )}
                      {dividendFilter !== 'non_dividend' && !mmfView && (
                      <TableCell className="text-right">
                        {/* Round 102 (Feb 2026) — Treat a 0 / near-zero
                            yield as "no dividend" and render "—". Many
                            non-div ADRs (ARM, YALA, TRVG, EVAX etc.)
                            carry a numeric 0 yield instead of null,
                            which was rendering "0.00%" in All-mode
                            mixed rows. Applies to `dividend_yield` and
                            `dividend_rate` uniformly across Screener +
                            StockTable. */}
                        {stock.dividend_yield != null && stock.dividend_yield > 0 ? (() => {
                          const nominal = stock.dividend_yield;
                          const useReal = effectiveYieldMode === 'real' && typeof cpiYoy === 'number';
                          const displayed = useReal ? computeRealYield(nominal, cpiYoy) : nominal;
                          const cls = useReal && displayed < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-green-600 dark:text-green-400';
                          const tip = useReal
                            ? `Real Yield = Nominal ${nominal.toFixed(2)}% − CPI ${cpiYoy.toFixed(2)}% = ${displayed.toFixed(2)}%. Screener filters + sorting still use nominal yield.`
                            : `Nominal dividend yield. Toggle "Real" above the table to overlay yield minus current CPI YoY.`;
                          return (
                            <span
                              className={`font-mono text-sm font-medium ${cls}`}
                              title={tip}
                              data-testid={`screener-yield-${stock.ticker}`}
                            >
                              {displayed.toFixed(2)}%
                            </span>
                          );
                        })() : (
                          <span className="font-mono text-sm text-muted-foreground">--</span>
                        )}
                      </TableCell>
                      )}
                      {prefView && <PreferredYtcCell stock={stock} />}
                      {dividendFilter === 'dividend' && !altView && (
                      <TableCell className="text-right font-mono text-sm">
                        {stock.dividend_rate != null && stock.dividend_rate > 0 ? `$${stock.dividend_rate.toFixed(2)}` : '--'}
                      </TableCell>
                      )}
                      {dividendFilter !== 'non_dividend' && !altView && (
                      <TableCell className="text-center">
                        {stock.frequency && ((stock.dividend_yield != null && stock.dividend_yield > 0) || (stock.dividend_rate != null && stock.dividend_rate > 0)) ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${PAYOUT_COLORS[stock.frequency] || 'bg-muted text-muted-foreground'}`}>
                            {stock.frequency}
                          </span>
                        ) : '--'}
                      </TableCell>
                      )}
                      {/* Round 97b — ALTMAN Z column, Non-Div only.
                          Bankruptcy risk model. SAFE (green) / GRAY
                          (amber) / DISTRESS (red). Higher = safer. */}
                      {dividendFilter === 'non_dividend' && !altView && (
                        <TableCell className="text-center" data-testid={`screener-altman-${stock.ticker}`}>
                          {(() => {
                            const eq = stock.equity_quality || {};
                            const z = eq.altman_z_raw;
                            const zone = eq.altman_zone;
                            if (z == null && !zone) return <span className="text-muted-foreground/50 text-sm">—</span>;
                            const label = zone === 'Safe' ? 'SAFE' : zone === 'Distress' ? 'DISTRESS' : zone === 'Grey' || zone === 'Gray' ? 'GRAY' : (zone ? zone.toUpperCase() : '—');
                            const cls = label === 'SAFE'
                              ? { bg: 'bg-emerald-500/15 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-500/40', dot: 'bg-emerald-500' }
                              : label === 'DISTRESS'
                              ? { bg: 'bg-rose-500/15 dark:bg-rose-500/20', text: 'text-rose-700 dark:text-rose-300', ring: 'ring-rose-500/40', dot: 'bg-rose-500' }
                              : { bg: 'bg-amber-500/15 dark:bg-amber-500/20', text: 'text-amber-700 dark:text-amber-300', ring: 'ring-amber-500/40', dot: 'bg-amber-500' };
                            return (
                              <span
                                className={`inline-flex items-center font-bold uppercase tracking-wider ring-1 rounded-full px-1 py-px text-[9px] gap-0.5 ${cls.bg} ${cls.text} ${cls.ring}`}
                                title={`Altman Z-Score = ${z != null ? z.toFixed(2) : '—'} · ${label} zone. Bankruptcy model: Z>2.99 safe, 1.81<Z≤2.99 gray, Z≤1.81 distress.`}
                              >
                                <span className={`rounded-full w-1.5 h-1.5 ${cls.dot}`} />
                                {z != null ? z.toFixed(2) : '—'}
                                <span className="opacity-70 font-mono">{label}</span>
                              </span>
                            );
                          })()}
                        </TableCell>
                      )}
                      {!altView && (
                      <TableCell className="text-center">
                        {/* Round 97a fix (Jul 4 2026) — Non-div rows show
                            the Growth Quality Tier badge as a rounded-full
                            pill matching QualityBadge exactly (dot + score
                            + short code like "G-A", "SPEC") so the Quality
                            column looks visually consistent when All mode
                            mixes div + non-div rows. Also shows the
                            Capital Analytics rank pill alongside when
                            present, matching the UNOV two-badge pattern. */}
                        {stock.pays_dividend === false ? (
                          <div className="flex items-center gap-1 justify-center flex-wrap">
                            {stock.non_dividend_quality_tier && (() => {
                              const t = stock.non_dividend_quality_tier;
                              const short = t === 'SPECULATIVE' ? 'SPEC' : t.replace('GROWTH-', 'G-');
                              const cls = {
                                'GROWTH-S': { bg: 'bg-emerald-500/15 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-500/40', dot: 'bg-emerald-500' },
                                'GROWTH-A': { bg: 'bg-sky-500/15 dark:bg-sky-500/20', text: 'text-sky-700 dark:text-sky-300', ring: 'ring-sky-500/40', dot: 'bg-sky-500' },
                                'GROWTH-B': { bg: 'bg-blue-500/15 dark:bg-blue-500/20', text: 'text-blue-700 dark:text-blue-300', ring: 'ring-blue-500/40', dot: 'bg-blue-500' },
                                'GROWTH-C': { bg: 'bg-amber-500/15 dark:bg-amber-500/20', text: 'text-amber-700 dark:text-amber-300', ring: 'ring-amber-500/40', dot: 'bg-amber-500' },
                                'SPECULATIVE': { bg: 'bg-rose-500/15 dark:bg-rose-500/20', text: 'text-rose-700 dark:text-rose-300', ring: 'ring-rose-500/40', dot: 'bg-rose-500' },
                              }[t] || { bg: 'bg-muted', text: 'text-muted-foreground', ring: 'ring-border', dot: 'bg-muted-foreground' };
                              return (
                                <span
                                  className={`inline-flex items-center font-bold uppercase tracking-wider ring-1 rounded-full px-1 py-px text-[9px] gap-0.5 ${cls.bg} ${cls.text} ${cls.ring}`}
                                  title={`Non-Div Quality · ${stock.non_dividend_quality_score ?? '—'}/100 (${t})`}
                                  data-testid={`non-div-tier-screener-q-${stock.ticker}`}
                                >
                                  <span className={`rounded-full w-1.5 h-1.5 ${cls.dot}`} />
                                  {stock.non_dividend_quality_score ?? '—'}
                                  <span className="opacity-70 font-mono">{short}</span>
                                </span>
                              );
                            })()}
                            {stock?.equity_quality?.rank != null && (
                              <CapitalScoreBadge
                                rank={stock.equity_quality.rank}
                                verdict={stock.equity_quality.verdict}
                                verdictLabel={stock.equity_quality.verdict_label}
                                size="xs"
                                ticker={stock.ticker}
                                testIdSuffix={`screener-q-${stock.ticker}`}
                              />
                            )}
                            {!stock.non_dividend_quality_tier && stock?.equity_quality?.rank == null && (
                              <span className="text-muted-foreground/50 text-sm">—</span>
                            )}
                          </div>
                        ) : (
                          <QualityBadge stock={stock} size="xs" interactive testIdSuffix={`screener-q-${stock.ticker}`} />
                        )}
                      </TableCell>
                      )}
                      {/* Jul 2026 — Coverage cell (Coverage Watch preset).
                          Tap-to-open popup via CoverageCellTip. */}
                      {coverageColumnActive && !altView && (
                        <TableCell className="text-right font-mono text-xs whitespace-nowrap" data-testid={`screener-coverage-${stock.ticker}`}>
                          <CoverageCellTip stock={stock} />
                        </TableCell>
                      )}
                      {/* Sep 2026 — BDC NAV Growth cell (mirrors the
                          header on bdcView). Shows 5Q change with the
                          same emerald/amber/rose thresholds as the
                          BdcNavGauge chip in the stock modal. */}
                      {bdcView && !altView && (
                        <TableCell className="text-right font-mono text-xs whitespace-nowrap" data-testid={`screener-nav-growth-${stock.ticker}`}>
                          {(() => {
                            const g = stock.bdc_nav_growth_5q_pct;
                            if (g == null || !Number.isFinite(Number(g))) {
                              return <span className="text-muted-foreground/50" title="Weekly cron hasn't populated this yet — will fill on the next Sunday run.">—</span>;
                            }
                            const n = Number(g);
                            const color = n >= 3 ? 'text-emerald-600 dark:text-emerald-400'
                              : n >= 0 ? 'text-emerald-500/80 dark:text-emerald-400/70'
                              : n >= -5 ? 'text-amber-600 dark:text-amber-400'
                              : 'text-rose-600 dark:text-rose-400';
                            return (
                              <span
                                className={`font-semibold ${color}`}
                                title={
                                  n >= 3 ? 'Loan book expanding (+≥3%). Positive BDC signal.'
                                  : n >= 0 ? 'Loan book essentially flat (0 to +3%). Neutral.'
                                  : n >= -5 ? 'Loan book slightly shrinking (0 to −5%). Monitor NII coverage.'
                                  : 'Loan book shrinking fast (< −5%). Classic yield-trap setup — verify coverage.'
                                }
                              >
                                {n >= 0 ? '▲ ' : '▼ '}{n >= 0 ? '+' : ''}{n.toFixed(1)}%
                              </span>
                            );
                          })()}
                        </TableCell>
                      )}
                      {/* Round 97b — REV GROWTH column, Non-Div only.
                          Sparkline of 7-yr revenue history + 3-yr CAGR
                          %. Empty cell if neither field is available. */}
                      {dividendFilter === 'non_dividend' && !altView && (
                        <TableCell className="text-right pr-3" data-testid={`screener-rev-growth-${stock.ticker}`}>
                          {(() => {
                            const cagr = stock.non_dividend_quality_signals?.revenue_3yr_cagr;
                            const history = stock.non_div_revenue_history_7y;
                            const hasHistory = Array.isArray(history) && history.length >= 3;
                            const hasCagr = cagr != null;
                            if (!hasHistory && !hasCagr) {
                              return <span className="text-muted-foreground/50 text-xs">—</span>;
                            }
                            const pctTxt = hasCagr
                              ? `${cagr >= 0 ? '+' : ''}${(cagr * 100).toFixed(1)}%`
                              : '';
                            const color = hasCagr
                              ? cagr >= 0.10 ? 'text-emerald-600 dark:text-emerald-400'
                              : cagr <= -0.05 ? 'text-rose-600 dark:text-rose-400'
                              : 'text-foreground/70'
                              : 'text-foreground/70';
                            return (
                              <div className="inline-flex items-center gap-1.5 justify-end">
                                {hasHistory && (
                                  <RevenueSparkline
                                    history={history}
                                    ticker={stock.ticker}
                                    onClick={() => handleStockClick(stock.ticker, 'non-div-growth')}
                                  />
                                )}
                                {hasCagr && (
                                  <span className={`font-mono text-xs font-semibold ${color}`}>
                                    {pctTxt}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                      )}
                      {!altView && (
                      <TableCell className="text-right ">
                        <span onClick={(e) => { e.stopPropagation(); handleStockClick(stock.ticker, 'stock-metrics', '1mo'); }} className="cursor-pointer hover:underline" data-testid={`screener-perf-1mo-${stock.ticker}`}>
                          <PerfCell value={stock.perf_1m} />
                        </span>
                      </TableCell>
                      )}
                      {!mmfView && (
                      <TableCell className="text-right ">
                        <span onClick={(e) => { e.stopPropagation(); handleStockClick(stock.ticker, 'stock-metrics', '1y'); }} className="cursor-pointer hover:underline" data-testid={`screener-perf-1y-${stock.ticker}`}>
                          <PerfCell value={stock.perf_1y} />
                        </span>
                      </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6" data-testid="screener-pagination">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(p => p - 1); scrollToTableTop('[data-testid="screener-search"]'); }}>Previous</Button>
            <span className="text-sm text-muted-foreground px-3">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(p => p + 1); scrollToTableTop('[data-testid="screener-search"]'); }}>Next</Button>
          </div>
        )}
      </div>

      {/* Stock Modal */}
      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedStock(null); setPrefetchedNews(null); setPendingInitialTab(null); setPendingInitialChartPeriod(null); fetchScreener(); }}
        loading={loadingDetail}
        isInWatchlist={selectedStock ? watchlist.includes(selectedStock.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        prefetchedNews={prefetchedNews}
        user={user}
        onLoginRequired={() => setLoginModalOpen(true)}
        onPositionsChanged={() => {
          if (user) {
            axios.get(`${API}/portfolio/owned-tickers`, { withCredentials: true })
              .then(res => setOwnedTickers(res.data || [])).catch(() => {});
          }
          window.dispatchEvent(new Event('portfolio-changed'));
        }}
        onViewPortfolio={() => { setModalOpen(false); navigate('/?view=portfolio'); }}
        initialTab={pendingInitialTab || "dividend-info"}
        initialChartPeriod={pendingInitialChartPeriod}
        onCompareClick={(t) => {
          if (t) { setCompareTicker(t); setCompareOpen(true); }
        }}
        onSelectStock={handleStockClick}
        watchlist={watchlist}
      />

      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialTickerA={compareTicker}
        watchlist={watchlist}
        onTickerClick={() => {}}
      />

      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />

      {/* Hover tooltip for filter/preset definitions */}
      <ScreenerTooltip tip={tooltip.tip} anchorRect={tooltip.anchorRect} />

      <SiteDisclaimer />
    </div>
  );
}
